<template>
  <div>
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">Detalle del Albarán</h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </div>
        <div class="col-lg-6 col-5 text-right">
          <base-button
            size="sm"
            type="neutral"
            @click="goBack"
          >
            <i class="fas fa-arrow-left mr-2"></i>
            Volver
          </base-button>
          <base-button
            v-if="canEdit"
            size="sm"
            type="neutral"
            @click="openEditAlbaranModal"
          >
            <i class="fas fa-edit mr-2"></i>
            Editar
          </base-button>
          <base-button
            v-if="canEdit && albaran?.tarea_keikaku_id && !isAlbaranBloqueadoParaEdicion()"
            size="sm"
            type="neutral"
            @click="actualizarParteTrabajo"
            :loading="actualizandoParte"
            class="ml-2"
          >
            <i class="fas fa-sync-alt mr-2" :class="{ 'fa-spin': actualizandoParte }"></i>
            Actualizar Parte
          </base-button>
          <base-button
            v-if="!albaran?.albaran_original_id && !isAlbaranBloqueadoParaEdicion()"
            size="sm"
            type="neutral"
            @click="showClones"
            class="ml-2"
          >
            <i class="fas fa-copy mr-2"></i>
            Ver Clones
          </base-button>
          <base-button
            v-if="!getIdTareaFromData"
            size="sm"
            type="success"
            @click="openLinkKeikakuModal"
            class="ml-2"
          >
            <i class="fas fa-link mr-2"></i>
            Enlazar Tarea
          </base-button>
          <base-button
            v-if="albaran && albaran.estado !== 'cerrado' && !isAlbaranBloqueadoParaEdicion()"
            size="sm"
            type="neutral"
            @click="openCloneModal"
            class="ml-2"
          >
            <i class="fas fa-clone mr-2"></i>
            Clonar Albarán
          </base-button>
          <base-button
            v-if="puedeRevertirClon && !isAlbaranBloqueadoParaEdicion()"
            size="sm"
            type="warning"
            @click="revertirClon"
            class="ml-2"
          >
            <i class="fas fa-undo mr-2"></i>
            Revertir Clon
          </base-button>
          <base-button
            v-if="puedeCerrarAlbaran && !isAlbaranBloqueadoParaEdicion()"
            size="sm"
            type="success"
            @click="cerrarAlbaran"
            class="ml-2"
          >
            <i class="fas fa-check-circle mr-2"></i>
            Cerrar Albarán
          </base-button>
          <base-button
            v-if="consumiblesParaIncidencia.length > 0 && !isAlbaranBloqueadoParaEdicion()"
            size="sm"
            type="neutral"
            @click="openIncidenciaConsumibleModal"
            class="ml-2"
            title="Registrar incidencia sobre un consumible (pérdida, rotura, no retorna a taller)"
          >
            <i class="fas fa-exclamation-triangle mr-2"></i>
            Incidencia consumible
          </base-button>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--6">
      <!-- Loading Overlay -->
      <div v-if="loading" class="text-center py-5">
        <i class="fas fa-spinner fa-spin fa-3x"></i>
      </div>

      <!-- Error State -->
      <div v-if="!loading && !albaran" class="alert alert-danger">
        No se ha podido cargar la información del albarán
      </div>

      <div v-else-if="albaran" class="row">
        <!-- Información Principal -->
        <div class="col-12">
          <card>
            <template v-slot:header>
              <div class="d-flex justify-content-between align-items-center">
                <div class="d-flex align-items-center flex-wrap">
                  <h3 class="mb-0 mr-3">
                    {{ getAlbaranAttributes.numero || 'N/A' }} - {{ getAlbaranAttributes.titulo || 'Sin título' }}
                  </h3>
                  <span
                    class="badge badge-dot mr-2 px-3"
                    :class="getEstadoBadgeClass(getEstadoAlbaran()) + ' text-white'"
                    :style="{
                      backgroundColor: getEstadoColor(getEstadoAlbaran()),
                      color: 'white'
                    }"
                  >
                    <span class="status">{{ formatEstado(getEstadoAlbaran()) }}</span>
                  </span>
                  <span
                    v-if="getAlbaranAttributes.fecha_entrega"
                    class="badge badge-secondary px-3"
                    style="font-size: 1rem;"
                  >
                    <i class="fas fa-calendar-alt mr-1"></i>
                    {{ formatDate(getAlbaranAttributes.fecha_entrega) }}
                  </span>
                  <span
                    v-if="getAlbaranAttributes.es_facturable"
                    class="badge px-3 text-white"
                    style="font-size: 1rem; background-color: #1e3a5f !important; color: white !important;"
                  >
                    <i class="fas fa-euro-sign"></i>
                  </span>
                </div>
                <button 
                  class="btn btn-sm btn-corporate"
                  @click="infoAlbaranCollapsed = !infoAlbaranCollapsed"
                  type="button"
                >
                  <i :class="infoAlbaranCollapsed ? 'fas fa-chevron-down' : 'fas fa-chevron-up'"></i>
                </button>
              </div>
            </template>

            <div v-show="!infoAlbaranCollapsed" class="pl-lg-4">
              <!-- Tabla con 2 filas y 3 columnas - Responsive -->
              <div class="table-responsive">
                <table class="table table-bordered mb-0">
                  <tbody>
                    <!-- Primera fila: Jefe de Equipo, Tarea Keikaku, Parte Asociado -->
                    <tr>
                      <td style="min-width: 200px; vertical-align: middle;">
                        <label class="form-control-label font-weight-bold">Jefe de Equipo</label>
                        <div class="d-flex align-items-center mt-2 flex-wrap">
                      <img
                        v-if="getUser && getUser.imagen_perfil"
                        :src="getUser.imagen_perfil.url"
                        class="avatar rounded-circle mr-2"
                            style="flex-shrink: 0;"
                      />
                      <img
                        v-else
                        src="/img/placeholder.png"
                        class="avatar rounded-circle mr-2"
                            style="flex-shrink: 0;"
                          />
                          <div class="flex-grow-1" style="min-width: 0;">
                            <h5 class="mb-0 text-truncate">{{ getUserName }}</h5>
                            <small v-if="getUser" class="text-muted d-block text-truncate">{{ getUser.email }}</small>
                      </div>
                          <div class="ml-2" style="flex-shrink: 0;">
                        <base-button
                          v-if="!getUser && !isAlbaranBloqueadoParaEdicion()"
                          size="sm"
                          type="primary"
                          @click="openUserAssignModal"
                        >
                          <i class="fas fa-user-plus"></i>
                        </base-button>
                        <base-button
                          v-if="getUser && !isAlbaranBloqueadoParaEdicion()"
                          size="sm"
                          type="danger"
                          @click="desasignarEmpleado"
                          title="Desasignar jefe de equipo"
                        >
                          <i class="fas fa-user-times"></i>
                        </base-button>
                      </div>
                    </div>
                      </td>
                      <td style="min-width: 200px; vertical-align: middle;">
                        <label class="form-control-label font-weight-bold">Tarea Keikaku</label>
                        <div class="mt-2">
                          <div v-if="getIdTareaFromData" class="mb-2">
                           
                  </div>
                          <div class="d-flex align-items-center justify-content-between flex-wrap">
                            <strong>Id Tarea: {{ getIdTareaFromData }}</strong>
                            <base-button
                              v-if="getAlbaranAttributes.tarea_keikaku_data && !isAlbaranBloqueadoParaEdicion()"
                              size="sm"
                              type="neutral"
                              @click="openTareaKeikakuModal"
                              style="flex-shrink: 0;"
                            >
                              <i class="fas fa-tasks mr-2"></i>
                              Ver Tarea
                            </base-button>
                </div>
                  </div>
                      </td>
                      <td style="min-width: 200px; vertical-align: middle;">
                        <label class="form-control-label font-weight-bold">Parte Asociado</label>
                        <div class="mt-2">
                          <div v-if="getIdParteFromData || getProyectoFromData" class="mb-2">
                            <div v-if="getIdParteFromData">
                              <strong>Id Parte: {{ getIdParteFromData }}</strong>
                </div>
                            <div v-if="getProyectoFromData" class="mt-1">
                              
              </div>
                  </div>
                          <div class="d-flex align-items-center justify-content-between flex-wrap">
                            <strong>Proyecto: {{ getProyectoFromData }}</strong>
                            <base-button
                              v-if="getAlbaranAttributes.parte_trabajo && !isAlbaranBloqueadoParaEdicion()"
                              size="sm"
                              type="neutral"
                              @click="openParteTrabajoModal"
                              style="flex-shrink: 0;"
                            >
                              <i class="fas fa-file-alt mr-2"></i>
                              Ver Parte
                            </base-button>
                </div>
                  </div>
                      </td>
                    </tr>
                    <!-- Segunda fila: Descripción, Cliente, Observaciones -->
                    <tr>
                      <td style="min-width: 200px; vertical-align: top;">
                        <label class="form-control-label font-weight-bold">Descripción</label>
                        <p class="mb-0 mt-2" style="word-wrap: break-word;">{{ getAlbaranAttributes.descripcion || 'Sin descripción' }}</p>
                      </td>
                      <td style="min-width: 200px; vertical-align: top;">
                        <label class="form-control-label font-weight-bold">Cliente</label>
                        <p class="mb-0 mt-2" style="word-wrap: break-word;">{{ getAlbaranAttributes.cliente || 'Sin especificar' }}</p>
                      </td>
                      <td style="min-width: 200px; vertical-align: top;">
                        <label class="form-control-label font-weight-bold">Observaciones</label>
                        <div class="mt-2" style="max-height: 200px; overflow-y: auto; word-wrap: break-word; white-space: pre-line;">{{ observacionesCompletas }}</div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <!-- Información adicional del ciclo de vida -->
              <div class="row" v-if="getAlbaranAttributes.es_clon || getAlbaranAttributes.albaran_original_id || getAlbaranAttributes.fecha_cierre || cerradoConIncidencias">
                <div class="col-12">
                  <hr>
                  <h6 class="text-muted">Información del Ciclo de Vida</h6>
                </div>
                <div class="col-lg-3" v-if="getAlbaranAttributes.es_clon">
                  <div class="form-group">
                    <label class="form-control-label">Es Clon</label>
                    <p class="mb-0 text-info"><i class="fas fa-copy mr-1"></i>Sí</p>
                  </div>
                </div>
                <div class="col-lg-3" v-if="getAlbaranAttributes.albaran_original_id">
                  <div class="form-group">
                    <label class="form-control-label">Albarán Original</label>
                    <p class="mb-0">{{ getAlbaranAttributes.albaran_original_id }}</p>
                  </div>
                </div>
                <div class="col-lg-3" v-if="getAlbaranAttributes.fecha_cierre">
                  <div class="form-group">
                    <label class="form-control-label">Fecha Cierre</label>
                    <p class="mb-0 text-success">{{ formatDate(getAlbaranAttributes.fecha_cierre) }}</p>
                  </div>
                </div>
                <div class="col-lg-3" v-if="cerradoConIncidencias">
                  <div class="form-group">
                    <label class="form-control-label">Incidencia</label>
                    <p class="mb-0">
                      <span class="badge badge-warning"><i class="fas fa-exclamation-triangle mr-1"></i>Cerrado con incidencias</span>
                    </p>
                  </div>
                </div>
              </div>
                </div>
          </card>
              </div>

        <!-- Incidencias de consumibles (visibles en el detalle del albarán) -->
        <div class="col-12" v-if="incidenciasAlbaran.length > 0">
          <card>
            <template slot="header">
              <div class="row align-items-center">
                <div class="col">
                  <h3 class="mb-0">
                    <i class="fas fa-exclamation-triangle text-warning mr-2"></i>
                    Incidencias de consumibles
                  </h3>
                </div>
                <div class="col-auto" v-if="!isAlbaranBloqueadoParaEdicion()">
                  <base-button size="sm" type="neutral" @click="openIncidenciaConsumibleModal">
                    <i class="fas fa-plus mr-1"></i> Nueva incidencia
                  </base-button>
                </div>
              </div>
            </template>
            <div class="table-responsive">
              <table class="table table-sm table-hover align-items-center">
                <thead class="thead-light">
                  <tr>
                    <th>Material</th>
                    <th>Cantidad</th>
                    <th>Motivo</th>
                    <th>Estado</th>
                    <th>Fecha</th>
                    <th>Descripción</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="ia in incidenciasAlbaran" :key="ia.id">
                    <td>{{ getMaterialNombreIncidenciaAlbaran(ia) }}</td>
                    <td>{{ ia.cantidad_afectada }} ud.</td>
                    <td>{{ getMotivoConsumibleLabel(ia.motivo_consumible) }}</td>
                    <td>
                      <span class="badge" :class="estadoIncidenciaAlbaranBadge(ia)">{{ getEstadoIncidenciaAlbaranNombre(ia) }}</span>
                    </td>
                    <td>{{ formatDate(ia.fecha) }}</td>
                    <td class="text-muted small">
                      <div>{{ getMaterialDescripcionIncidenciaAlbaran(ia) }}</div>
                      <div v-if="ia.descripcion" class="text-muted" style="opacity: 0.9;">
                        <em>Incidencia:</em> {{ ia.descripcion }}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </card>
        </div>
              
        <!-- Materiales -->
        <div class="col-12">
          <card>
            <template v-slot:header>
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <h3 class="mb-0">Lista Materiales Keikaku</h3>
                  <small class="text-muted">
                    Activos: <strong>{{ getTotalActivos }}</strong> | 
                    Consumibles: <strong>{{ getTotalConsumibles }}</strong>
                  </small>
                </div>
                <div>
                  <button
                    v-if="albaran?.tarea_keikaku_id"
                    class="btn btn-sm btn-primary mr-2"
                    @click="actualizarMaterialesDesdeKeikaku"
                    :disabled="actualizandoMateriales || isAlbaranBloqueadoParaEdicion()"
                    type="button"
                  >
                    <i class="fas fa-sync-alt mr-2" :class="{ 'fa-spin': actualizandoMateriales }"></i>
                    {{ actualizandoMateriales ? 'Actualizando...' : 'Actualizar' }}
                  </button>
                  <button
                    v-if="puedeMostrarImportacionMateriales && !isAlbaranBloqueadoParaEdicion()"
                    class="btn btn-sm btn-success mr-2"
                    @click="openImportMaterialesModal"
                    type="button"
                    title="Importar materiales desde otro albarán con misma tarea y fecha"
                  >
                    <i class="fas fa-file-import mr-2"></i>
                    Importar
                  </button>
                <button 
                  class="btn btn-sm btn-corporate"
                  @click="materialesCollapsed = !materialesCollapsed"
                  type="button"
                >
                  <i :class="materialesCollapsed ? 'fas fa-chevron-down' : 'fas fa-chevron-up'"></i>
                </button>
                </div>
              </div>
            </template>

            <div v-show="!materialesCollapsed">
              <el-table
                :data="getMaterialesTaller"
                style="width: 100%"
                class="table-responsive align-items-center table-flush"
                header-row-class-name="thead-light"
                empty-text="No hay materiales del taller asignados"
              >
              <el-table-column
                label="Código"
                prop="idArticulo"
                sortable="custom"
                :min-width="120"
              >
                <template v-slot="{ row }">
                  <b>{{ row.idArticulo }}</b>
                </template>
              </el-table-column>

              <el-table-column
                label="Descripción"
                prop="idArticulo_flxtext"
                sortable="custom"
                :min-width="200"
              >
                <template v-slot="{ row }">
                  {{ row.idArticulo_flxtext }}
                </template>
              </el-table-column>

              <el-table-column
                label="Cantidad"
                prop="cantidad"
                sortable="custom"
                align="center"
                :min-width="100"
              >
                <template v-slot="{ row }">
                  <span class="badge badge-default text-white text-center">
                    {{ row.cantidad }} {{ row.unidad || 'un' }}
                  </span>
                </template>
              </el-table-column>

              <el-table-column
                label="Asignados"
                align="center"
                :min-width="100"
              >
                <template v-slot="{ row }">
                  <template v-if="isConsumible(row)">
                    <!-- Para consumibles, mostrar cantidad asignada vs requerida -->
                    <span v-if="getConsumibleAsignadoCantidad(row) > 0">
                      <b>{{ getConsumibleAsignadoCantidad(row) }}</b>
                      <i class="fas fa-check-circle ml-1" style="color: #495057;"></i>
                    </span>
                    <span v-else>
                      0
                      <i class="fas fa-times-circle text-danger ml-1"></i>
                    </span>
                  </template>
                  <template v-else>
                    <!-- Para activos, mostrar progreso de asignación -->
                    <span v-if="getActivosAsignadosCount(row) > 0">
                      <span :class="getProgresoBadgeClass(row)" :style="getActivosAsignadosCount(row) >= getMaterialesRequeridos(row) ? 'background-color: #495057 !important; border-color: #495057 !important;' : ''">
                        {{ getActivosAsignadosCount(row) }}/{{ getMaterialesRequeridos(row) }}
                      </span>
                      <i :class="getProgresoIconClass(row)" class="ml-1" :style="getActivosAsignadosCount(row) >= getMaterialesRequeridos(row) ? 'color: #343a40;' : ''"></i>
                    </span>
                    <span v-else>
                      <span class="badge badge-danger text-white">
                        0/{{ getMaterialesRequeridos(row) }}
                      </span>
                      <i class="fas fa-times-circle text-danger ml-1"></i>
                    </span>
                  </template>
                </template>
              </el-table-column>

              <el-table-column
                label="Acciones"
                align="center"
                :min-width="200"
                v-if="!isAlbaranBloqueadoParaEdicion()"
              >
                <template v-slot="{ row }">
                  <div class="d-flex justify-content-center">
                    <!-- Botón para consumibles (CONS) -->
                    <base-button
                      v-if="isConsumible(row)"
                      size="sm"
                      type="primary"
                      @click="editCantidadConsumible(row)"
                      class="mx-1"
                      style="min-height: 32px; display: flex; align-items: center; justify-content: center;"
                    >
                      <i class="fas fa-plus mr-1"></i>
                      Consumibles
                    </base-button>
                    
                    <!-- Botón para activos (no CONS) -->
                    <base-button
                      v-else
                      size="sm"
                      type="primary"
                      @click="asignarActivo(row)"
                      class="mx-1"
                      style="min-height: 32px; display: flex; align-items: center; justify-content: center;"
                    >
                      <i class="fas fa-plus mr-1"></i>
                      Activo
                    </base-button>
                    
                    <!-- Botón para marcar/desmarcar manualmente -->
                    <base-button
                      size="sm"
                      type="secondary"
                      @click="marcarMaterialManual(row)"
                      class="mx-1"
                      :class="{ 'material-marcado-manual': estaMarcadoManualmente(row) }"
                      style="line-height: 1.5;"
                      :title="estaMarcadoManualmente(row) ? 'Desmarcar como asignado manualmente' : 'Marcar como asignado manualmente (no se añade a la lista de asignados)'"
                    >
                      <i :class="estaMarcadoManualmente(row) ? 'fas fa-check-circle' : 'fas fa-check'" style="margin: 0;"></i>
                    </base-button>
            </div>
                </template>
              </el-table-column>
            </el-table>
            </div>
          </card>
        </div>

        <!-- Lista de Activos -->
        <div class="col-12">
          <card>
            <template v-slot:header>
              <div>
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <div style="width: 50%;">
                    <base-input
                      v-model="qrInput"
                      type="text"
                      placeholder="Escanear QR de activo o ID de consumible..."
                      @keydown.native="prepararAudioAsignacion"
                      @keyup.enter.native="handleQRScan"
                      :class="{'is-invalid': error}"
                      autocomplete="off"
                        class="qr-scanner-input"
                    >
                      <template v-slot:error>
                        {{ error }}
                      </template>
                    </base-input>
                  </div>
                  <div class="d-flex align-items-center" v-if="!isAlbaranBloqueadoParaEdicion()">
                    <base-button
                      type="primary"
                      size="sm"
                      class="mr-2"
                      @click="abrirModalAnadirActivo"
                    >
                      <i class="fas fa-plus mr-1"></i>
                      Añadir activo
                    </base-button>
                    <base-button
                      type="primary"
                      size="sm"
                      class="mr-3"
                      @click="abrirModalAddMaterial"
                    >
                      <i class="fas fa-plus mr-1"></i>
                      Añadir consumibles
                    </base-button>
                    
                    <!-- Selector de Vista -->
                    <div class="btn-group" role="group">
                      <button
                        type="button"
                        class="btn btn-corporate"
                        :class="{ 'active': viewMode === 'table' }"
                        @click="viewMode = 'table'"
                        title="Vista de Tabla"
                      >
                        <i class="fas fa-table"></i>
                      </button>
                      <button
                        type="button"
                        class="btn btn-corporate"
                        :class="{ 'active': viewMode === 'kanban' }"
                        @click="viewMode = 'kanban'"
                        title="Vista de Kanban"
                      >
                        <i class="fas fa-columns"></i>
                      </button>
                    </div>
                    
                    <!-- Botón de Actualización -->
                    <base-button
                      type="primary"
                      size="sm"
                      @click="refrescarAlbaran"
                      :loading="loading"
                      class="ml-2"
                      title="Actualizar materiales del albarán"
                    >
                      <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
                      Actualizar
                    </base-button>
                    <!-- Transferir materiales desde otro albarán del mismo día -->
                    <base-button
                      v-if="puedeMostrarTransferenciaMateriales"
                      type="success"
                      size="sm"
                      @click="openTransferMaterialesModal"
                      class="ml-2"
                      title="Transferir materiales desde otro albarán del mismo día (los materiales pasan a este albarán)"
                    >
                      <i class="fas fa-exchange-alt mr-1"></i>
                      Transferir
                    </base-button>
                  </div>
                </div>
              </div>
            </template>

            <!-- Vista Tabla -->
            <el-table
              v-if="viewMode === 'table'"
              :data="getMateriales"
              style="width: 100%"
              class="table-responsive align-items-center table-flush"
              header-row-class-name="thead-light"
              empty-text="No hay materiales o equipos asignados a este albarán"
            >
              <!-- COLUMNA TIPO -->
              <el-table-column
                label="TIPO"
                sortable="custom"
                :min-width="100"
                :max-width="100"
              >
                <template v-slot="{ row }">
                  <span v-if="row.es_saca || row.tipo_material === 'saca'" class="badge badge-primary text-white">
                    <i class="fas fa-box mr-1"></i>
                    Saca
                  </span>
                  <span v-else-if="row.activo" class="badge badge-default text-white">
                    Activo
                  </span>
                  <span v-else class="badge badge-warning text-white">
                    Consumible
                  </span>
                </template>
              </el-table-column>

              <!-- COLUMNA NOMBRE DEL ACTIVO -->
              <el-table-column
                label="Nombre"
                sortable="custom"
                :min-width="250"
              >
                <template v-slot="{ row }">
                  <!-- Si es una Saca -->
                  <div v-if="row.es_saca || row.tipo_material === 'saca'">
                    <b><i class="fas fa-box text-primary mr-1"></i>{{ row.nombre || 'Saca sin nombre' }}</b>
                    <br>
                    <small class="text-muted">Saca de activos</small>
                  </div>
                  <!-- Si es un activo normal -->
                  <div v-else-if="row.activo">
                    <b>{{ row.activo.nombre || 'Sin nombre' }}</b>
                    <br v-if="row.activo.descripcion">
                    <small v-if="row.activo.descripcion" class="text-muted">{{ row.activo.descripcion }}</small>
                  </div>
                  <!-- Si es un consumible -->
                  <div v-else>
                    <b>{{ row.descripcion || row.idArticulo_flxtext || 'Sin descripción' }}</b>
                    <br>
                    <small class="text-muted">Consumible</small>
                  </div>
                </template>
              </el-table-column>

              <!-- COLUMNA MODELO - OCULTA -->
              <!-- <el-table-column
                label="Modelo"
                sortable="custom"
                :min-width="200"
              >
                <template v-slot="{ row }">
                  <span v-if="row.activo">
                    {{ row.activo.modelo?.nombre }} 
                    <small class="text-muted">({{ row.activo.fabricante?.nombre }})</small>
                  </span>
                  <span v-else>
                    {{ row.idArticulo_flxtext }}
                  </span>
                </template>
              </el-table-column> -->

              <!-- COLUMNA NUMERO SERIE -->
              <el-table-column
                label="Núm. Serie"
                prop="numero_serie"
                sortable="custom"
                :min-width="120"
              >
                <template v-slot="{ row }">
                  <!-- Si es una Saca, no tiene número de serie -->
                  <span v-if="row.es_saca || row.tipo_material === 'saca'" class="text-muted">
                    -
                  </span>
                  <!-- Si es un activo normal -->
                  <span v-else-if="row.activo">
                    <b>{{ row.activo.numero_serie }}</b>
                  </span>
                  <!-- Si es un consumible -->
                  <span v-else>
                    <b>{{ row.idArticulo || '-' }}</b>
                  </span>
                </template>
              </el-table-column>

              <!-- COLUMNA QR -->
              <el-table-column
                label="QR"
                sortable="custom"
                :min-width="100"
              >
                <template v-slot="{ row }">
                  <!-- Si es una Saca, mostrar el QR de la Saca -->
                  <span v-if="row.es_saca || row.tipo_material === 'saca'">
                    <b>{{ row.qr || '-' }}</b>
                  </span>
                  <!-- Si es un activo normal -->
                  <span v-else-if="row.activo">
                    {{ row.activo.numero_serie_qr }}
                  </span>
                  <!-- Si es un consumible -->
                  <span v-else class="text-muted">-</span>
                </template>
              </el-table-column>

              <!-- COLUMNA ESTADO -->
              <el-table-column
                label="Estado"
                prop="estado"
                sortable="custom"
                :min-width="120"
              >
                <template v-slot="{ row }">
                  <div class="d-flex flex-column align-items-start">
                    <span
                      class="badge badge-default mb-1"
                      :class="getEstadoBadgeClass(getEstadoVisualizacion(row))"
                      :style="{ backgroundColor: getEstadoColorMaterial(getEstadoVisualizacion(row)), color: '#ffffff' }"
                    >
                      {{ formatEstadoMaterial(getEstadoVisualizacion(row)) }}
                    </span>
                    <span v-if="getEstadoVisualizacion(row) === 'incidencia' || (row.incidencias && row.incidencias.some(inc => {
                      const estadoNombre = inc.attributes?.estado_incidencia?.data?.attributes?.nombre;
                      return estadoNombre && estadoNombre !== 'Resuelta';
                    }))" 
                          class="badge badge-danger badge-sm text-white">
                      <i class="fas fa-exclamation-triangle"></i> INCIDENCIA
                    </span>
                    <!-- Mostrar motivo del rechazo si el material está rechazado -->
                    <div v-if="getEstadoVisualizacion(row) === 'rechazado' && row.observaciones" 
                         class="mt-1">
                      <small class="text-muted d-block" style="font-size: 0.75rem; max-width: 200px;">
                        <i class="fas fa-info-circle mr-1"></i>
                        <strong>Motivo:</strong> {{ extraerMotivoRechazo(row.observaciones) }}
                      </small>
                    </div>
                  </div>
                </template>
              </el-table-column>

              <!-- COLUMNA RESPONSABLE -->
              <el-table-column
                label="Responsable"
                sortable="custom"
                :min-width="150"
              >
                <template v-slot="{ row }">
                  <div class="d-flex align-items-center">
                    <span v-if="row.activo">{{ formatResponsable(row.responsable_actual) }}</span>
                    <span v-else>{{ formatResponsable(row.responsable_actual) }}</span>
                  </div>
                </template>
              </el-table-column>

              <!-- COLUMNA CANTIDAD -->
              <el-table-column
                label="CANT"
                sortable="custom"
                align="center"
                :min-width="80"
                :max-width="80"
              >
                <template v-slot="{ row }">
                  <span v-if="!row.activo" class="badge badge-default text-white">
                    {{ row.cantidad || getMaterialCantidad(row) }}
                  </span>
                  <span v-else>1</span>
                </template>
              </el-table-column>

              <!-- COLUMNA ACCIONES -->
              <el-table-column 
                label="Acciones" 
                align="center" 
                :min-width="140"
                fixed="right"
                v-if="!isAlbaranBloqueadoParaEdicion()"
              >
                <template v-slot="{ row }">
                  <div class="table-actions d-flex justify-content-center">
                                        <!-- Botones para Revisar Devolución (jefe_taller puede aceptar devoluciones) -->
                    <template v-if="row.estado === 'pendiente_aceptar_devolucion'">
                      <base-button
                        size="sm"
                        type="success"
                        @click="aceptarDevolucion(row)"
                        class="mx-1"
                        title="Aceptar devolución"
                      >
                        <i class="fas fa-check"></i>
                      </base-button>
                      <base-button
                        size="sm"
                        type="danger"
                        @click="rechazarDevolucion(row)"
                        class="mx-1"
                        title="Rechazar devolución"
                      >
                        <i class="fas fa-times"></i>
                      </base-button>
                    </template>
                    <!-- Botones normales para todos los demás estados -->
                    <template v-else>
                      <!-- Botón desvincular (si aplica) -->
                      <template v-if="canDesasignar(row)">
                        <base-button
                          v-if="row.activo"
                          outline
                          size="sm"
                          type="danger"
                          @click="desasignarActivo(row)"
                          class="mx-1"
                          title="Desvincular activo"
                        >
                          <i class="fas fa-unlink"></i>
                        </base-button>
                        <base-button
                          v-else
                          outline
                          size="sm"
                          type="danger"
                          @click="desasignarConsumible(row)"
                          class="mx-1"
                          title="Desvincular consumible"
                        >
                          <i class="fas fa-unlink"></i>
                        </base-button>
                      </template>
                                            <!-- Botón resolver incidencia (si tiene incidencia activa) -->
                      <base-button
                        v-if="row.activo && hasIncidenciaActiva(row)"
                        size="sm"
                        type="warning"
                        @click="abrirModalResolverIncidencia(row)"
                        class="mx-1"
                        title="Resolver incidencia"
                      >
                        <i class="fas fa-check-circle"></i>
                      </base-button>
                      <!-- Botón ver activo (si es activo) -->
                      <base-button
                        v-if="row.activo"
                        outline
                        size="sm"
                        type="primary"
                        @click="viewTipo(row.activo)"
                        class="mx-1"
                        title="Ver detalles del activo"
                      >
                        <i class="fas fa-eye"></i>
                      </base-button>
                      <!-- Botón editar (siempre al final) -->
                      <base-button
                        size="sm"
                        type="primary"
                        @click="editarMaterial(row)"
                        class="mx-1"
                        title="Editar material"
                      >
                        <i class="fas fa-edit text-white"></i>
                      </base-button>
                    </template>
                  </div>
                </template>
              </el-table-column>
            </el-table>

            <!-- Vista Kanban -->
            <MaterialKanban
              v-if="viewMode === 'kanban'"
              :materiales="getMateriales"
              :albaran="albaran"
              :loading="loading"
              @asignar-jefe-equipo="asignarJefeEquipoMaterial"
              @aceptar-material="aceptarMaterial"
              @rechazar-material="rechazarMaterial"
              @solicitar-devolucion="solicitarDevolucion"
              @aceptar-devolucion="aceptarDevolucion"
              @rechazar-devolucion="rechazarDevolucion"
              @marcar-no-devuelto="marcarComoNoDevuelto"
              @marcar-consumido="marcarComoConsumido"
              @change-material-state="changeMaterialState"
              @editar-material="editarMaterial"
              @aceptar-devoluciones-masivas="aceptarDevolucionesMasivas"
              @aceptar-devolucion-consumible="aceptarDevolucionConsumible"
              @incidencia-resuelta="manejarIncidenciaResuelta"
            />
          </card>
        </div>
      </div>
    </div>

    <!-- Modal de Asignación de Usuario -->
    <modal
      :show.sync="showUserAssignModal"
      modal-classes="modal-dialog-centered modal-xl"
      @close="showUserAssignModal = false"
    >
      <template v-slot:header>
        <h3 class="modal-title">Asignar Jefe de Equipo</h3>
      </template>
      <div class="modal-body">
        <base-input label="Buscar jefe de equipo">
          <el-select
            v-model="selectedUserId"
            filterable
            placeholder="Escriba para buscar..."
            :loading="loadingUsers"
            class="w-100"
            popper-class="user-select-dropdown"
            @input="searchUsers"
          >
            <el-option
              v-for="user in availableUsers"
              :key="user.id"
              :label="user.nombre + ' ' + user.apellidos"
              :value="user.id"
            >
              <div class="d-flex align-items-center">
                <img
                  v-if="user.imagen_perfil"
                  :src="user.imagen_perfil.url"
                  class="avatar avatar-xs rounded-circle mr-2"
                />
                <img
                  v-else
                  src="/img/placeholder.png"
                  class="avatar avatar-xs rounded-circle mr-2"
                />
                <span>{{ user.nombre }} {{ user.apellidos }}</span>
                <small class="text-muted ml-2">({{ user.email }})</small>
              </div>
            </el-option>
          </el-select>
        </base-input>
      </div>
      <template v-slot:footer>
        <base-button type="secondary" @click="showUserAssignModal = false">
          Cancelar
        </base-button>
        <base-button type="primary" @click="assignUser" :disabled="!selectedUserId || assigningUser">
          {{ assigningUser ? 'Asignando...' : 'Asignar' }}
        </base-button>
      </template>
    </modal>

    <!-- Modal de Edición de Cantidad de Consumible -->
    <modal
      :show.sync="showEditCantidadModal"
      modal-classes="modal-dialog-centered modal-secondary"
      @close="showEditCantidadModal = false"
    >
      <template v-slot:header>
        <h3 class="modal-title">Añadir Consumibles</h3>
      </template>
      <div class="modal-body">
        <div class="form-group">
          <label class="form-control-label">Material</label>
          <p class="mb-0">{{ selectedConsumible?.idArticulo_flxtext }}</p>
  </div>
        <base-input 
          label="Cantidad"
          v-model="nuevaCantidad"
          type="number"
          min="0"
          step="1"
        />
      </div>
      <template v-slot:footer>
        <base-button type="secondary" @click="showEditCantidadModal = false">
          Cancelar
        </base-button>
        <base-button type="primary" @click="guardarCantidadConsumible" :disabled="!nuevaCantidad || guardandoCantidad || isAlbaranBloqueadoParaEdicion()">
          {{ guardandoCantidad ? 'Guardando...' : 'Añadir' }}
        </base-button>
      </template>
    </modal>

    <!-- Modal de Importación de Materiales -->
    <modal
      :show.sync="showImportMaterialesModal"
      modal-classes="modal-dialog-centered modal-xl"
      @close="showImportMaterialesModal = false"
    >
      <template v-slot:header>
        <h3 class="modal-title">Importar materiales desde otro albarán</h3>
      </template>
      <div class="modal-body">
        <div class="mb-3">
          <small class="text-muted">
            Esta opción solo aplica cuando el albarán tiene <b>tarea Keikaku</b> y <b>fecha de entrega</b>.
          </small>
        </div>

        <p class="mb-2">
          Importa materiales desde <b>otro albarán</b> que tenga la <b>misma tarea Keikaku</b> y la <b>misma fecha de entrega</b>.
        </p>

        <div v-if="loadingImportCandidates" class="text-muted mb-3">
          <i class="fas fa-spinner fa-spin mr-2"></i>
          Cargando albaranes candidatos...
        </div>

        <div v-else>
          <div v-if="importCandidates.length === 0" class="alert alert-secondary">
            No hay otros albaranes con la misma tarea Keikaku y fecha de entrega.
          </div>

          <div v-else class="form-group">
            <label class="form-control-label font-weight-bold">Albarán origen</label>
            <el-select
              v-model="sourceAlbaranId"
              filterable
              placeholder="Selecciona un albarán..."
              class="w-100"
            >
              <el-option
                v-for="a in importCandidates"
                :key="a.id"
                :label="formatAlbaranCandidate(a)"
                :value="a.id"
              />
            </el-select>

            <div class="mt-3">
              <base-button
                type="primary"
                @click="importarMaterialesDesdeOtroAlbaran"
                :disabled="!sourceAlbaranId || importandoMateriales"
              >
                {{ importandoMateriales ? 'Importando...' : 'Importar materiales de este albarán' }}
              </base-button>
            </div>
            <small class="text-muted d-block mt-2">
              Consumibles: se importará solo la diferencia de cantidades. Activos: se omiten los ya presentes en este albarán.
            </small>
          </div>
        </div>
      </div>
      <template v-slot:footer>
        <base-button type="secondary" @click="showImportMaterialesModal = false">
          Cerrar
        </base-button>
      </template>
    </modal>

    <!-- Modal de Transferencia de Materiales (mismo día) -->
    <modal
      :show.sync="showTransferMaterialesModal"
      modal-classes="modal-dialog-centered modal-xl"
      @close="showTransferMaterialesModal = false"
    >
      <template v-slot:header>
        <h3 class="modal-title">Transferir materiales desde otro albarán del día</h3>
      </template>
      <div class="modal-body">
        
        <p class="mb-2">
          Elige un albarán con la <b>misma fecha de entrega</b> que este albarán para transferir todos sus materiales aquí.
        </p>
        <div v-if="loadingTransferCandidates" class="text-muted mb-3">
          <i class="fas fa-spinner fa-spin mr-2"></i>
          Cargando albaranes del día...
        </div>
        <div v-else>
          <div v-if="transferCandidates.length === 0" class="alert alert-secondary">
            No hay otros albaranes con la misma fecha de entrega.
          </div>
          <div v-else class="form-group">
            <label class="form-control-label font-weight-bold">Albarán del que transferir</label>
            <el-select
              v-model="sourceAlbaranIdTransfer"
              filterable
              placeholder="Selecciona un albarán..."
              class="w-100"
            >
              <el-option
                v-for="a in transferCandidates"
                :key="a.id"
                :label="formatAlbaranCandidate(a)"
                :value="a.id"
              />
            </el-select>
            <div class="mt-3">
              <base-button
                type="primary"
                @click="transferirMaterialesDesdeOtroAlbaran"
                :disabled="!sourceAlbaranIdTransfer || transferiendoMateriales"
              >
                {{ transferiendoMateriales ? 'Transferiendo...' : 'Transferir materiales al este albarán' }}
              </base-button>
            </div>
          </div>
        </div>
      </div>
      <template v-slot:footer>
        <base-button type="secondary" @click="showTransferMaterialesModal = false">
          Cerrar
        </base-button>
      </template>
    </modal>

    <!-- Modal de Asignación/Adición de Activo -->
    <modal
      :show.sync="showAsignarActivoModal"
      modal-classes="modal-dialog-centered modal-xl"
      @close="cerrarModalActivo"
    >
      <template v-slot:header>
        <h3 class="modal-title">{{ modoAsignacion ? 'Asignar Activo' : 'Añadir Activo al Albarán' }}</h3>
      </template>
      <div class="modal-body">
        <div class="form-group" v-if="modoAsignacion">
          <label class="form-control-label">Material a asignar</label>
          <p class="mb-0">{{ selectedActivo?.idArticulo_flxtext }}</p>
        </div>
        <div class="form-group">
          <label class="form-control-label">{{ modoAsignacion ? 'Buscar activo para asignar' : 'Buscar activo para añadir' }}</label>
          <div class="input-group">
            <base-input 
              ref="qrActivoInput"
              v-model="qrActivo"
              :placeholder="modoAsignacion ? 'Escanear código QR o introducir número de serie...' : 'Buscar activo para añadir al albarán...'"
              @keydown.native="prepararAudioAsignacion"
              @keyup.enter.native="buscarActivo"
              class="flex-grow-1"
            />
            <div class="input-group-append">
                <base-button
                type="primary" 
                @click="buscarActivo"
                :loading="buscandoActivo"
                  :disabled="!qrActivo.trim() || isAlbaranBloqueadoParaEdicion()"
              >
                <i class="fas fa-search"></i>
                Buscar
              </base-button>
            </div>
          </div>
        </div>
        
        <!-- Resultados de búsqueda -->
        <div v-if="activosEncontrados && activosEncontrados.length > 0" class="mt-3">
          <h6>✅ Activos encontrados: {{ activosEncontrados.length }}</h6>
          <div class="list-group activos-scroll" style="max-height: 300px; overflow-y: auto;">
            <div 
              v-for="(activo, index) in activosEncontrados" 
              :key="activo.id"
              class="list-group-item list-group-item-action p-2"
              :class="{ 'active': activoSeleccionado && activoSeleccionado.id === activo.id }"
              @click="seleccionarActivo(activo)"
              style="cursor: pointer; font-size: 14px;"
            >
              <div class="d-flex align-items-center justify-content-between">
                <div class="d-flex flex-column">
                  <span class="font-weight-bold" style="font-size: 15px;">{{ activo.nombre }}</span>
                  <div class="d-flex align-items-center mt-1" style="font-size: 13px;">
                    <span class="text-muted mr-3">Modelo: {{ activo.modelo?.nombre || '-' }}</span>
                    <span class="text-muted mr-3">Nº Serie: {{ activo.numero_serie || '-' }}</span>
                    <span class="text-muted mr-3">QR: <strong>{{ activo.numero_serie_qr || '-' }}</strong></span>
                <span class="text-muted">Estado: 
                  <span v-if="getUltimaRevisionActivo(activo)"
                        :class="{
                          'text-success': getUltimaRevisionActivo(activo).estado === 'apto',
                          'text-warning': getUltimaRevisionActivo(activo).estado === 'apto_a_vigilar',
                          'text-danger': getUltimaRevisionActivo(activo).estado === 'no_apto'
                        }">
                    {{ getUltimaRevisionActivo(activo).estado }}
                  </span>
                  <span v-else class="text-muted">-</span>
                </span>
                  </div>
                </div>
                <i v-if="activoSeleccionado && activoSeleccionado.id === activo.id" class="fas fa-check-circle text-success ml-2" style="font-size: 18px;"></i>
              </div>
            </div>
          </div>
          <!-- Mensaje informativo sobre los resultados -->
          <div v-if="activosEncontrados.length > 0" class="mt-2 text-center">
            <small class="text-muted">
              Se encontraron {{ activosEncontrados.length }} activo{{ activosEncontrados.length > 1 ? 's' : '' }}.
              <br>Selecciona uno de los mostrados para añadirlo al albarán.
              <br v-if="activosEncontrados.length > 10"><i class="fas fa-info-circle"></i> Usa el scroll para ver todos los resultados.
            </small>
          </div>
        </div>
        
        <!-- Mensaje cuando no se encuentra nada -->
        <div v-else-if="qrActivo && !buscandoActivo && activosEncontrados !== null" class="mt-3">
          <div class="alert alert-warning">
            <i class="fas fa-exclamation-triangle"></i>
            No se encontró ningún activo con ese código en Taller Verticales.
          </div>
        </div>
      </div>
      <template v-slot:footer>
        <base-button type="secondary" @click="cerrarModalActivo">
          Cancelar
        </base-button>
        <base-button type="primary" @click="asignarActivoAlAlbaran" :disabled="!activoSeleccionado || asignandoActivo || isAlbaranBloqueadoParaEdicion()">
          {{ asignandoActivo ? 'Procesando...' : (modoAsignacion ? 'Asignar Activo' : 'Añadir al Albarán') }}
        </base-button>
      </template>
    </modal>

    <!-- Modal Unificado de Añadir Material -->
    <modal
      :show.sync="showAddMaterialModal"
      modal-classes="modal-dialog-centered modal-xl"
      @close="cerrarModalAddMaterial"
    >
      <template v-slot:header>
        <h3 class="modal-title">Añadir Consumible al Albarán</h3>
      </template>
      <div class="modal-body">
        <!-- Mensaje informativo -->
        <div class="alert alert-primary mb-3">
          <i class="fas fa-info-circle mr-2"></i>
          Busca y selecciona un consumible del ERP para añadirlo al albarán
        </div>

        <!-- Buscador de Consumibles del ERP -->
        <div class="form-group">
          <label class="form-control-label">Buscar Consumible del ERP *</label>
          <div class="input-group">
            <base-input 
              ref="searchConsumibleInput"
              v-model="searchConsumible"
              placeholder="Buscar por descripción o ID..."
              @keyup.enter.native="buscarConsumiblesERP"
              @input="buscarConsumiblesERP"
              @focus="showConsumiblesList = true"
              @blur="ocultarListaConsumibles"
              class="flex-grow-1"
            />
            <div class="input-group-append">
              <base-button 
                type="primary" 
                @click="buscarConsumiblesERP"
                :loading="loadingConsumibles"
                :disabled="!searchConsumible || searchConsumible.length < 2"
              >
                <i class="fas fa-search"></i>
                Buscar
              </base-button>
            </div>
          </div>
          
          <!-- Lista de consumibles filtrados -->
          <div 
            v-if="showConsumiblesList && filteredConsumibles.length > 0" 
            class="list-group mt-2"
            style="max-height: 200px; overflow-y: auto; position: relative; z-index: 1000;"
          >
            <button
              v-for="consumible in filteredConsumibles"
              :key="consumible.idArticulo"
              type="button"
              class="list-group-item list-group-item-action"
              @click="seleccionarConsumibleERP(consumible)"
              style="text-align: left; cursor: pointer; font-size: 14px;"
            >
              <div class="d-flex w-100 justify-content-between">
                <h6 class="mb-1" style="font-size: 15px;">{{ consumible.descripcion }}</h6>
                <span style="font-size: 13px;">{{ consumible.idArticulo }}</span>
              </div>
              <span class="text-muted" style="font-size: 13px;">Familia: {{ consumible.idFamilia }}</span>
            </button>
          </div>
          
          <!-- Mensaje cuando no hay resultados -->
          <div v-if="showConsumiblesList && searchConsumible && filteredConsumibles.length === 0 && !loadingConsumibles" class="alert alert-info mt-2">
            No se encontraron consumibles que coincidan con la búsqueda
          </div>
        </div>

        <!-- Consumible seleccionado -->
        <div v-if="selectedConsumibleERP" class="alert alert-success">
          <h6><i class="fas fa-check-circle mr-2"></i>Consumible Seleccionado:</h6>
          <strong>{{ selectedConsumibleERP.descripcion }}</strong><br>
          <small>ID: {{ selectedConsumibleERP.idArticulo }}</small>
        </div>

        <!-- Campos de cantidad -->
        <base-input 
          label="Cantidad *"
          v-model="nuevoMaterial.cantidad"
          type="number"
          min="1"
          step="1"
          required
          :disabled="!selectedConsumibleERP"
        />
      </div>
      <template v-slot:footer>
        <base-button type="secondary" @click="cerrarModalAddMaterial">
          Cancelar
        </base-button>
        <base-button type="primary" @click="guardarNuevoMaterial" :disabled="!isMaterialValidoERP || guardandoMaterial || isAlbaranBloqueadoParaEdicion()">
          {{ guardandoMaterial ? 'Guardando...' : 'Añadir Consumible' }}
        </base-button>
      </template>
    </modal>

    <!-- Modal de Edición de Material -->
    <modal
      :show.sync="showEditMaterialModal"
      modal-classes="modal-dialog-centered modal-secondary"
      @close="cerrarModalEditMaterial"
    >
      <template v-slot:header>
        <h3 class="modal-title">
          <span v-if="materialSeleccionado?.es_saca || materialSeleccionado?.tipo_material === 'saca'">
            <i class="fas fa-box text-primary mr-2"></i>Editar Saca
          </span>
          <span v-else-if="materialSeleccionado?.activo">
            Editar Estado
          </span>
          <span v-else>
            Editar Consumible
          </span>
        </h3>
      </template>
      <div class="modal-body">
        <!-- Campo de estado para materiales -->
        <div class="form-group">
          <label class="form-control-label">Estado</label>
          <select 
            v-model="editMaterial.estado" 
            class="form-control"
          >
            <option value="pendiente_aceptar">Pendiente aceptar</option>
            <option value="entregado">Entregado</option>
            <option value="rechazado">Rechazado</option>
            <option value="pendiente_aceptar_devolucion">Revisar Devolución</option>
            <option value="devuelto">Devuelto</option>
            <option value="no_devuelto">No Devuelto</option>
            <option value="consumido">Consumido</option>
            <option value="incidencia">Incidencia</option>
          </select>
        </div>
        
        <!-- Campo de cantidad para consumibles (solo si estado es pendiente_aceptar o rechazado) -->
        <div 
          v-if="!materialSeleccionado?.activo && !materialSeleccionado?.es_saca && materialSeleccionado?.tipo_material !== 'saca' && (editMaterial.estado === 'pendiente_aceptar' || editMaterial.estado === 'rechazado')"
          class="form-group"
        >
          <label class="form-control-label">Cantidad</label>
          <base-input 
            v-model="editMaterial.cantidad"
            type="number"
            min="0"
            step="1"
            placeholder="Cantidad de consumibles"
          />
          <small class="form-text text-muted" v-if="getMaterialUnidad(materialSeleccionado)">
            Unidad: {{ getMaterialUnidad(materialSeleccionado) }}
          </small>
        </div>
        
        <!-- Campo de observaciones -->
        <base-input 
          label="Observaciones"
          v-model="editMaterial.observaciones"
          type="text"
        />
        
        <!-- Campo de responsable actual -->
        <div class="form-group">
          <label class="form-control-label">Responsable Actual</label>
          <select 
            v-model="editMaterial.responsable_actual" 
            class="form-control"
          >
            <option value="">Seleccionar responsable</option>
            <option value="jefe_taller">Jefe de Taller</option>
            <option value="jefe_equipo">Jefe de Equipo</option>
          </select>
        </div>
      </div>
      <template v-slot:footer>
        <base-button type="secondary" @click="cerrarModalEditMaterial">
          Cancelar
        </base-button>
        <base-button type="primary" @click="guardarEdicionMaterial" :disabled="!isValidEdit">
          Guardar cambios
        </base-button>
      </template>
    </modal>

    <!-- Modal de Edición de Albarán -->
    <modal
      :show.sync="showEditAlbaranModal"
      modal-classes="modal-dialog-centered modal-lg"
      @close="cerrarModalEditAlbaran"
    >
      <template v-slot:header>
        <h3 class="modal-title">Editar Albarán</h3>
      </template>
      <div class="modal-body">
        <div class="row">
          <!-- Estado -->
          <div class="col-md-12">
            <div class="form-group">
              <label class="form-control-label">Estado</label>
              <select 
                v-model="editAlbaran.estado" 
                class="form-control"
              >
                <option value="pendiente_asignar">Pendiente asignar</option>
                <option value="asignado">Asignado</option>
                <option value="entregado">Entregado</option>
                <option value="devuelto">Devuelto</option>
                <option value="cerrado">Cerrado</option>
                <option value="cerrado_sin_tarea">Cerrado sin tarea</option>
                <option value="pendiente_aceptar_devolucion">Revisar Devolución</option>
                <option value="incidencia">Incidencia</option>
                <option value="material_abandonado">Material Abandonado</option>
              </select>
            </div>
          </div>

          <!-- Título -->
          <div class="col-md-12">
            <base-input
              label="Título"
              v-model="editAlbaran.titulo"
              type="text"
              placeholder="Título del albarán"
            />
          </div>
          
          <!-- Descripción -->
          <div class="col-md-12">
            <div class="form-group">
              <label class="form-control-label">Descripción</label>
              <textarea
                v-model="editAlbaran.descripcion"
                class="form-control"
                rows="3"
                placeholder="Descripción del albarán"
              ></textarea>
            </div>
          </div>

          <!-- Fechas en la misma línea -->
          <div class="col-md-6">
            <base-input
              label="Fecha Entrega"
              v-model="editAlbaran.fecha_entrega"
              type="date"
            />
          </div>
          <div class="col-md-6">
            <base-input
              label="Fecha Devolución"
              v-model="editAlbaran.fecha_devolucion"
              type="date"
            />
          </div>

          <!-- Observaciones -->
          <div class="col-md-12">
            <div class="form-group mb-0">
              <label class="form-control-label">Observaciones</label>
              <textarea
                v-model="editAlbaran.observaciones"
                class="form-control"
                rows="3"
                placeholder="Observaciones adicionales"
              ></textarea>
            </div>
          </div>
        </div>
      </div>
      <template v-slot:footer>
        <div class="d-flex justify-content-between w-100">
          <div>
            <base-button 
              v-if="puedeEliminarAlbaran"
              type="danger" 
              @click="eliminarAlbaranDesdeModal"
              class="mr-2"
            >
              <i class="fas fa-trash mr-2"></i>
              Eliminar Albarán
            </base-button>
          </div>
          <div>
            <base-button type="secondary" @click="cerrarModalEditAlbaran" class="mr-2">
          Cancelar
        </base-button>
        <base-button type="primary" @click="guardarEdicionAlbaran" :disabled="!isValidAlbaranEdit">
          Guardar cambios
        </base-button>
          </div>
        </div>
      </template>
    </modal>

    <!-- Modal de Confirmación -->
    <modal
      :show.sync="showConfirmModal"
      modal-classes="modal-dialog-centered modal-secondary"
      @close="cancelConfirmAction"
    >
      <template v-slot:header>
        <h3 class="modal-title">{{ confirmTitle }}</h3>
      </template>
      <div class="modal-body">
        <p class="mb-0">{{ confirmMessage }}</p>
      </div>
      <template v-slot:footer>
        <base-button type="secondary" @click="cancelConfirmAction">
          Cancelar
        </base-button>
        <base-button :type="confirmButtonType" @click="executeConfirmAction">
          {{ confirmButtonText }}
        </base-button>
      </template>
    </modal>

    <!-- Modal para enlazar con tarea de Keikaku -->
    <modal
      :show.sync="showLinkKeikakuModal"
        modal-classes="modal-dialog-centered modal-md"
      @close="cerrarModalLinkKeikaku"
    >
      <template v-slot:header>
        <h3 class="modal-title">Enlazar con Tarea de Keikaku</h3>
      </template>
      <div class="modal-body">
        <div class="form-group">
          <label class="form-control-label">ID de Tarea Keikaku</label>
          <base-input
            v-model="tareaKeikakuId"
            placeholder="Ej: TAREA-123"
            type="text"
          />
        </div>
      </div>
      <template v-slot:footer>
        <base-button
          type="secondary"
          @click="cerrarModalLinkKeikaku"
        >
          Cancelar
        </base-button>
        <base-button
          type="primary"
          @click="enlazarTareaKeikaku"
          :loading="linkingKeikaku"
        >
          Enlazar
        </base-button>
      </template>
    </modal>

    <!-- Modal para seleccionar fecha de entrega al clonar -->
    <modal
      :show.sync="showCloneModal"
      modal-classes="modal-dialog-centered modal-md"
      @close="cerrarModalClone"
    >
      <template v-slot:header>
        <h3 class="modal-title">Clonar Albarán</h3>
      </template>
      <div class="modal-body">
        <div class="form-group">
          <label class="form-control-label">Fecha de Entrega</label>
          <base-input
            v-model="fechaEntregaClon"
            type="date"
            placeholder="Selecciona la fecha de entrega"
          />
          <small class="form-text text-muted">
            Por defecto se usa la fecha de hoy
          </small>
        </div>
      </div>
      <template v-slot:footer>
        <base-button
          type="secondary"
          @click="cerrarModalClone"
        >
          Cancelar
        </base-button>
        <base-button
          type="primary"
          @click="confirmarClonarAlbaran"
          :loading="loading"
        >
          Clonar
        </base-button>
      </template>
    </modal>

    <!-- Modal para mostrar clones -->
    <modal
      :show.sync="showClonesModal"
      modal-classes="modal-dialog-centered modal-lg"
      @close="cerrarModalClones"
    >
      <template v-slot:header>
        <h3 class="modal-title">Clones del Albarán</h3>
      </template>
      <div class="modal-body">
        <div v-if="loadingClones" class="text-center">
          <i class="fas fa-spinner fa-spin fa-2x"></i>
          <p>Cargando clones...</p>
        </div>
        <div v-else-if="clones.length === 0" class="text-center">
          <p>No se encontraron clones para este albarán.</p>
        </div>
        <div v-else>
          <div v-for="clon in clones" :key="clon.id" class="card mb-3">
            <div class="card-body">
              <div class="row">
                <div class="col-md-6">
                  <h6>{{ clon.attributes?.numero || clon.numero }}</h6>
                  <p class="mb-1"><strong>Estado:</strong> {{ formatEstado(clon.attributes?.estado || clon.estado) }}</p>
                  <p class="mb-1"><strong>Fecha creación:</strong> {{ formatDate(clon.attributes?.createdAt || clon.createdAt) }}</p>
                </div>
                <div class="col-md-6 text-right">
                  <base-button
                    size="sm"
                    type="primary"
                    @click="verClon(clon.id)"
                  >
                    Ver Detalle
                  </base-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <template v-slot:footer>
        <base-button
          type="secondary"
          @click="cerrarModalClones"
        >
          Cerrar
        </base-button>
      </template>
    </modal>

    <!-- Modal para visualizar Tarea Keikaku -->
    <modal
      :show.sync="showTareaKeikakuModal"
      modal-classes="modal-dialog-centered modal-xl"
      @close="cerrarModalTareaKeikaku"
    >
      <template v-slot:header>
        <h3 class="modal-title">Tarea Keikaku</h3>
      </template>
      <div class="modal-body">
        <div v-if="!getAlbaranAttributes.tarea_keikaku_data" class="text-center text-muted">
          <p>No hay datos de tarea de Keikaku disponibles para este albarán.</p>
        </div>
        <div v-else class="table-responsive">
          <table class="table table-bordered table-striped">
            <tbody>
              <tr v-for="(value, key) in getTareaKeikakuDataFlattened" :key="key">
                <th style="width: 30%; background-color: #f8f9fa; font-weight: 600;">{{ formatTareaKeikakuKey(key) }}</th>
                <td>{{ formatTareaKeikakuValue(value) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <template v-slot:footer>
            <base-button
          type="secondary"
          @click="cerrarModalTareaKeikaku"
            >
          Cerrar
            </base-button>
      </template>
    </modal>

    <!-- Modal para visualizar Parte de Trabajo -->
    <modal
      :show.sync="showParteTrabajoModal"
      modal-classes="modal-dialog-centered modal-xl"
      @close="cerrarModalParteTrabajo"
    >
      <template v-slot:header>
        <h3 class="modal-title">Parte de Trabajo</h3>
      </template>
      <div class="modal-body">
        <div v-if="!getAlbaranAttributes.parte_trabajo" class="text-center text-muted">
          <p>No hay datos de parte de trabajo disponibles para este albarán.</p>
          </div>
        <div v-else>
          <!-- Información destacada: Id Parte y Proyecto -->
          <div class="alert alert-info mb-3">
            <div v-if="getIdParteFromData">
              <strong>Id Parte: {{ getIdParteFromData }}</strong>
            </div>
            <div v-if="getProyectoFromData" class="mt-2">
              <strong>Proyecto: {{ getProyectoFromData }}</strong>
            </div>
          </div>
          <div class="table-responsive">
            <table class="table table-bordered table-striped">
              <tbody>
                <tr v-for="(value, key) in getAlbaranAttributes.parte_trabajo" :key="key">
                  <th style="width: 30%; background-color: #f8f9fa; font-weight: 600;">{{ formatParteTrabajoKey(key) }}</th>
                  <td>{{ formatParteTrabajoValue(value) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <template v-slot:footer>
        <base-button
          type="secondary"
          @click="cerrarModalParteTrabajo"
        >
          Cerrar
        </base-button>
      </template>
    </modal>

    <!-- Modal para resolver incidencia desde el listado -->
    <modal
      :show.sync="showResolverIncidenciaModal"
      modal-classes="modal-dialog-centered modal-md"
      @close="cerrarModalResolverIncidencia"
    >
      <template v-slot:header>
        <h3 class="modal-title">
          <i class="fas fa-check-circle text-warning mr-2"></i>
          Resolver Incidencia
        </h3>
      </template>
      <div class="modal-body">
        <div v-if="incidenciaSeleccionada" class="alert alert-info">
          <i class="fas fa-info-circle mr-2"></i>
          <strong>Material:</strong> {{ incidenciaSeleccionada.activo?.nombre || incidenciaSeleccionada.activo?.numero_serie || 'N/A' }}
        </div>
        <div class="form-group">
          <label class="form-control-label">
            <strong>Comentario de Resolución:</strong>
          </label>
          <textarea
            class="form-control"
            v-model="comentarioResolucion"
            rows="5"
            placeholder="Describe detalladamente cómo se resolvió la incidencia, qué acciones se tomaron, etc."
            required
          ></textarea>
          <small class="form-text text-muted">
            Este comentario quedará registrado en el historial de la incidencia.
          </small>
        </div>
      </div>
      <template v-slot:footer>
        <base-button
          outline
          type="secondary"
          @click="cerrarModalResolverIncidencia"
          :disabled="resolviendoIncidencia"
        >
          Cancelar
        </base-button>
        <base-button
          type="warning"
          @click="resolverIncidenciaDesdeListado"
          :disabled="!comentarioResolucion || !comentarioResolucion.trim() || resolviendoIncidencia"
          :loading="resolviendoIncidencia"
        >
          <i class="fas fa-check-circle mr-1"></i>
          Resolver Incidencia
        </base-button>
      </template>
    </modal>

    <!-- Modal Incidencia sobre consumible -->
    <modal
      :show.sync="showIncidenciaConsumibleModal"
      modal-classes="modal-dialog-centered modal-lg"
      @close="cerrarIncidenciaConsumibleModal"
    >
      <template v-slot:header>
        <h3 class="modal-title">
          <i class="fas fa-exclamation-triangle text-warning mr-2"></i>
          Incidencia sobre consumible
        </h3>
      </template>
      <div class="modal-body">
        <p class="text-muted mb-3">
          Registre una incidencia sobre material consumible (sin nº de serie): pérdida, rotura, no retorna a taller, etc.
        </p>
        <div class="form-group">
          <label class="form-control-label">Consumible afectado <span class="text-danger">*</span></label>
          <el-select
            v-model="incidenciaConsumibleForm.albaran_activo_material"
            placeholder="Seleccione el consumible"
            class="w-100"
            filterable
          >
            <el-option
              v-for="m in consumiblesParaIncidencia"
              :key="m.id"
              :label="(m.descripcion || m.nombre || m.idArticulo || 'Consumible') + ' (cant: ' + (m.cantidad || getMaterialCantidad(m) || 0) + ')'"
              :value="m.id"
            />
          </el-select>
        </div>
        <div class="form-group">
          <label class="form-control-label">Cantidad afectada <span class="text-danger">*</span></label>
          <base-input
            v-model.number="incidenciaConsumibleForm.cantidad_afectada"
            type="number"
            min="1"
            :max="cantidadMaxIncidenciaConsumible"
            placeholder="Cantidad"
          />
          <small v-if="cantidadMaxIncidenciaConsumible" class="form-text text-muted">
            Máximo: {{ cantidadMaxIncidenciaConsumible }} (cantidad del material en el albarán)
          </small>
        </div>
        <div class="form-group">
          <label class="form-control-label">Motivo <span class="text-danger">*</span></label>
          <el-select v-model="incidenciaConsumibleForm.motivo_consumible" class="w-100" placeholder="Motivo">
            <el-option label="Pérdida" value="perdida" />
            <el-option label="Rotura / Estropeado" value="rotura" />
            <el-option label="No retorna a taller" value="no_retorna_taller" />
            <el-option label="Otro" value="otro" />
          </el-select>
        </div>
        <div class="form-group">
          <label class="form-control-label">Descripción (opcional)</label>
          <textarea
            v-model="incidenciaConsumibleForm.descripcion"
            class="form-control"
            rows="3"
            placeholder="Detalle de la incidencia..."
          />
        </div>
        <div v-if="incidenciasAlbaran.length > 0" class="mt-3">
          <h6 class="text-muted">Incidencias de consumibles en este albarán</h6>
          <ul class="list-unstyled small">
            <li v-for="ia in incidenciasAlbaran" :key="ia.id" class="mb-2">
              <span class="badge" :class="estadoIncidenciaAlbaranBadge(ia)">{{ getEstadoIncidenciaAlbaranNombre(ia) }}</span>
              {{ getMaterialNombreIncidenciaAlbaran(ia) }} — {{ ia.cantidad_afectada }} ud., {{ getMotivoConsumibleLabel(ia.motivo_consumible) }}
              <span v-if="ia.descripcion" class="text-muted"> — {{ ia.descripcion }}</span>
            </li>
          </ul>
        </div>
      </div>
      <template v-slot:footer>
        <base-button outline type="secondary" @click="cerrarIncidenciaConsumibleModal" :disabled="guardandoIncidenciaConsumible">
          Cancelar
        </base-button>
        <base-button
          type="warning"
          @click="crearIncidenciaConsumible"
          :disabled="!isIncidenciaConsumibleFormValida || guardandoIncidenciaConsumible"
          :loading="guardandoIncidenciaConsumible"
        >
          <i class="fas fa-exclamation-triangle mr-1"></i>
          Registrar incidencia
        </base-button>
      </template>
    </modal>

    <!-- Modal para motivo de rechazo -->
    <modal
      :show.sync="showMotivoRechazoModal"
      modal-classes="modal-dialog-centered modal-md"
      @close="cerrarModalMotivoRechazo"
    >
      <template v-slot:header>
        <h3 class="modal-title">
          <i class="fas fa-times-circle text-danger mr-2"></i>
          {{ esRechazoDevolucion ? 'Motivo del Rechazo de Devolución' : 'Motivo del Rechazo' }}
        </h3>
      </template>
      <div class="modal-body">
        <div v-if="materialParaRechazar" class="alert alert-info">
          <i class="fas fa-info-circle mr-2"></i>
          <strong>Material:</strong> {{ materialParaRechazar.activo?.nombre || materialParaRechazar.descripcion || materialParaRechazar.idArticulo_flxtext || 'N/A' }}
        </div>
        <div class="form-group">
          <label class="form-control-label">
            <strong>Motivo del rechazo:</strong> <span class="text-danger">*</span>
          </label>
          <textarea
            class="form-control"
            v-model="motivoRechazo"
            rows="4"
            placeholder="Describe el motivo por el cual se rechaza este material..."
            required
          ></textarea>
          <small class="form-text text-muted">
            Este motivo quedará registrado en las observaciones del material.
          </small>
        </div>
      </div>
      <template v-slot:footer>
        <base-button
          outline
          type="secondary"
          @click="cerrarModalMotivoRechazo"
          :disabled="rechazandoMaterial"
        >
          Cancelar
        </base-button>
        <base-button
          type="danger"
          @click="confirmarRechazoMaterial"
          :disabled="!motivoRechazo || !motivoRechazo.trim() || rechazandoMaterial"
          :loading="rechazandoMaterial"
        >
          <i class="fas fa-times-circle mr-1"></i>
          Rechazar Material
        </base-button>
      </template>
    </modal>

    <!-- Modal para cantidad de devolución de consumible -->
    <modal
      :show.sync="showCantidadDevolucionModal"
      modal-classes="modal-dialog-centered modal-md"
      @close="cerrarModalCantidadDevolucion"
    >
      <template v-slot:header>
        <h3 class="modal-title">
          <i class="fas fa-undo text-success mr-2"></i>
          Aceptar Devolución de Consumible
        </h3>
      </template>
      <div class="modal-body">
        <div v-if="materialDevolucion" class="alert alert-info mb-3">
          <i class="fas fa-info-circle mr-2"></i>
          <strong>Consumible:</strong> {{ materialDevolucion.idArticulo_flxtext || materialDevolucion.descripcion || 'N/A' }}
          <br>
          <strong>Cantidad asignada:</strong> {{ materialDevolucion.cantidad || getMaterialCantidad(materialDevolucion) }} {{ (materialDevolucion && materialDevolucion.unidad) ? materialDevolucion.unidad : 'un' }}
        </div>
        <div class="form-group">
          <label class="form-control-label">
            <strong>Cantidad a devolver:</strong>
          </label>
          <base-input
            v-model="cantidadDevolucion"
            type="number"
            min="0"
            :max="materialDevolucion ? (materialDevolucion.cantidad || getMaterialCantidad(materialDevolucion)) : 0"
            placeholder="Ingrese la cantidad a devolver"
            required
          >
          </base-input>
          <small class="form-text text-muted">
            La cantidad debe ser mayor a 0 y no puede exceder la cantidad asignada.
          </small>
        </div>
      </div>
      <template v-slot:footer>
        <base-button
          outline
          type="secondary"
          @click="cerrarModalCantidadDevolucion"
          :disabled="guardandoDevolucion"
        >
          Cancelar
        </base-button>
        <base-button
          type="success"
          @click="confirmarDevolucionConCantidad"
          :disabled="!cantidadDevolucion || parseFloat(cantidadDevolucion) <= 0 || parseFloat(cantidadDevolucion) > (materialDevolucion ? (materialDevolucion.cantidad || getMaterialCantidad(materialDevolucion)) : 0) || guardandoDevolucion"
          :loading="guardandoDevolucion"
        >
          <i class="fas fa-check mr-1"></i>
          Aceptar Devolución
        </base-button>
      </template>
    </modal>
  </div>
</template>

<script>
import { Table, TableColumn, Select, Option } from "element-ui";
import moment from "moment";
import "moment/locale/es";
import axios from 'axios';
import swal from "sweetalert2";
import UsersService from "@/store/services/users-service";
import Modal from "@/components/Modal";
import MaterialKanban from "@/components/MaterialKanban";
import { getEstadoColor as getEstadoColorUtil, getEstadoBadgeClass as getEstadoBadgeClassUtil, getEstadoTexto as getEstadoTextoUtil } from "@/util/estadoColors";
import { KEIKAKU_ACCESS_TOKEN, SAT_ACCESS_TOKEN, KEIKAKU_API_BASE_URL, SAT_API_BASE_URL } from "@/config/externalApiTokens";
import erpService from '@/services/erpService';
moment.locale("es");

export default {
  name: "view-albaran",
  components: {
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Select.name]: Select,
    [Option.name]: Option,
    Modal,
    MaterialKanban
  },
  data() {
    return {
      loading: true,
      qrInput: "",
      error: "",
      viewMode: 'table', // 'table' o 'kanban'
      infoAlbaranCollapsed: true, // Controla si la sección de información del albarán está plegada (por defecto colapsado)
      materialesCollapsed: true, // Controla si la sección de materiales está plegada (por defecto colapsado)
      albaran: null,
      showUserAssignModal: false,
      selectedUserId: null,
      availableUsers: [],
      loadingUsers: false,
      assigningUser: false,
      allUsers: [],
      // Variables para modal de edición de cantidad
      showEditCantidadModal: false,
      selectedConsumible: null,
      nuevaCantidad: "",
      nuevaUnidad: "",
      guardandoCantidad: false,
      // Variables para modal de asignación de activo
      showAsignarActivoModal: false,
      selectedActivo: null,
      qrActivo: "",
      // Variables para modal de resolución de incidencia
      showResolverIncidenciaModal: false,
      incidenciaSeleccionada: null,
      comentarioResolucion: "",
      resolviendoIncidencia: false,
      // Variables para modal de cantidad de devolución
      showCantidadDevolucionModal: false,
      materialDevolucion: null,
      cantidadDevolucion: "",
      guardandoDevolucion: false,
      activosEncontrados: null, // Nuevo para almacenar los activos encontrados
      activoSeleccionado: null, // Nuevo para almacenar el activo seleccionado
      asignandoActivo: false,
      buscandoActivo: false,
      actualizandoMateriales: false, // Control para el botón de actualización de materiales
      actualizandoParte: false, // Control para el botón de actualización de parte
      showParteTrabajoModal: false, // Control para el modal de parte de trabajo
      showTareaKeikakuModal: false, // Control para el modal de tarea Keikaku
      materialesMarcadosManual: [], // Array de IDs de materiales marcados manualmente (solo visual, no se guarda en BD)
      incidenciasCache: {}, // Cache de incidencias por activo para evitar llamadas duplicadas
      colorMap: {
        AZUL: "#3498DB",
        ROJO: "#E74C3C",
        AMARILLO: "#F1C40F",
        MORADO: "#8E44AD",
        VERDE: "#2ECC71",
      },
      apiUrl: process.env.VUE_APP_API_URL || 'https://misepis-api.delfromsoftware.com/api',
      modoAsignacion: false, // Nuevo: para controlar si estamos asignando o añadiendo
      showAddConsumibleModal: false,
      nuevoConsumible: {
        descripcion: '',
        cantidad: ''
      },
      guardandoConsumible: false,
      // Variables para modal de añadir consumible
      showAddMaterialModal: false,
      nuevoMaterial: {
        idArticulo: '',
        descripcion: '',
        cantidad: '',
        material_keikaku_id: ''
      },
      guardandoMaterial: false,
      // Variables para el buscador del ERP
      searchConsumible: '',
      consumiblesERP: [],
      filteredConsumibles: [],
      loadingConsumibles: false,
      selectedConsumibleERP: null,
      showConsumiblesList: false,
      showEditMaterialModal: false,
      materialSeleccionado: null,
      editMaterial: {
        estado: '',
        observaciones: '',
        responsable_actual: '',
        cantidad: null
      },
      showEditAlbaranModal: false,
      editAlbaran: {
        titulo: '',
        estado: '',
        fecha_entrega: '',
        fecha_devolucion: '',
        descripcion: '',
        observaciones: ''
      },
      showConfirmModal: false,
      confirmTitle: '',
      confirmMessage: '',
      confirmButtonText: '',
      confirmButtonType: 'primary',
      confirmAction: null,
      // Variables para modal de motivo de rechazo
      showMotivoRechazoModal: false,
      materialParaRechazar: null,
      motivoRechazo: '',
      rechazandoMaterial: false,
      esRechazoDevolucion: false, // Para distinguir entre rechazo de material y rechazo de devolución
      // Variables para modal de importación de materiales
      showImportMaterialesModal: false,
      importCandidates: [],
      loadingImportCandidates: false,
      sourceAlbaranId: null,
      importandoMateriales: false,
      // Variables para modal de transferencia de materiales (mismo día)
      showTransferMaterialesModal: false,
      transferCandidates: [],
      loadingTransferCandidates: false,
      sourceAlbaranIdTransfer: null,
      transferiendoMateriales: false,
      // Nuevas variables para gestión de ciclo de vida
      showLinkKeikakuModal: false,
      tareaKeikakuId: '',
      linkingKeikaku: false,
      showClonesModal: false,
      clones: [],
      loadingClones: false,
      showCloneModal: false,
      fechaEntregaClon: '',
      // Incidencia sobre consumible (albarán)
      showIncidenciaConsumibleModal: false,
      incidenciaConsumibleForm: {
        albaran_activo_material: null,
        cantidad_afectada: 1,
        motivo_consumible: 'perdida',
        descripcion: ''
      },
      guardandoIncidenciaConsumible: false,
      incidenciasAlbaran: []
    };
  },
  watch: {
    showUserAssignModal(val) {
      if (val) {
        this.$nextTick(() => {
          this.fetchEmpleados();
        });
      }
    },
    showImportMaterialesModal(val) {
      if (val) {
        this.$nextTick(() => {
          this.initImportMateriales();
        });
      }
    },
    showTransferMaterialesModal(val) {
      if (val) {
        this.$nextTick(() => {
          this.initTransferMateriales();
        });
      }
    }
  },
  computed: {
    getUser() {
      return this.albaran?.users_permissions_user;
    },
    getUserName() {
      if (this.getUser) {
        return `${this.getUser.nombre} ${this.getUser.apellidos}`;
      }
      return 'Sin asignar';
    },
    getMateriales() {
      if (!this.albaran) return [];

      // Obtener solo los materiales de albaran_activo_materials
      // Ya no necesitamos consumibles de materiales_taller porque se manejan igual que los activos
      const materialesActivos = this.albaran?.albaran_activo_materials || [];

      return materialesActivos;
    },
    getMaterialesTaller() {
      const materialesTaller = this.albaran?.materiales_taller;
      return materialesTaller && Array.isArray(materialesTaller) ? materialesTaller : [];
    },
    getTotalActivos() {
      return this.getMaterialesTaller
        .filter(material => !this.isConsumible(material))
        .reduce((total, material) => {
          const cantidad = parseInt(material.cantidad) || 1;
          return total + cantidad;
        }, 0);
    },
    getTotalConsumibles() {
      return this.getMaterialesTaller
        .filter(material => this.isConsumible(material))
        .reduce((total, material) => {
          const cantidad = parseInt(material.cantidad) || 1;
          return total + cantidad;
        }, 0);
    },
    getAlbaranAttributes() {
      // Return the direct albaran object since we don't need .attributes anymore
      return this.albaran || {};
    },
    fechaEntregaYMD() {
      const fecha = this.getAlbaranAttributes.fecha_entrega;
      if (!fecha) return null;
      return moment(fecha).format('YYYY-MM-DD');
    },
    puedeMostrarImportacionMateriales() {
      // Mostrar la opción solo si el albarán tiene tarea Keikaku y fecha de entrega
      const tareaId = this.albaran?.tarea_keikaku_id || this.albaran?.attributes?.tarea_keikaku_id;
      return Boolean(tareaId) && Boolean(this.getAlbaranAttributes.fecha_entrega);
    },
    puedeMostrarTransferenciaMateriales() {
      // Mostrar transferir si el albarán tiene fecha de entrega (albaranes del mismo día)
      return Boolean(this.getAlbaranAttributes.fecha_entrega);
    },
    puedeRevertirClon() {
      // El botón de revertir clon solo debe aparecer cuando:
      // 1. Es un clon
      // 2. No tiene tarea de Keikaku
      // 3. No está asignado a nadie
      if (!this.getAlbaranAttributes.es_clon) {
        return false;
      }
      
      const tieneTareaKeikaku = this.getAlbaranAttributes.tarea_keikaku_id !== null && 
                                 this.getAlbaranAttributes.tarea_keikaku_id !== undefined;
      
      const tieneUsuarioAsignado = this.getAlbaranAttributes.users_permissions_user !== null && 
                                   this.getAlbaranAttributes.users_permissions_user !== undefined;
      
      return !tieneTareaKeikaku && !tieneUsuarioAsignado;
    },
    getCreadoPor() {
      // Obtener "Creado por" desde tarea_keikaku_data.IdResponsable.Text
      if (!this.albaran) return null;
      
      const tareaKeikakuData = this.albaran.tarea_keikaku_data || this.albaran.attributes?.tarea_keikaku_data;
      
      if (tareaKeikakuData && tareaKeikakuData.IdResponsable) {
        // IdResponsable puede tener Text o TextVa
        const responsable = tareaKeikakuData.IdResponsable.Text || tareaKeikakuData.IdResponsable.TextVa;
        if (responsable) {
          return responsable;
        }
      }
      
      return null;
    },
    getPreasignadoPor() {
      // Obtener "Preasignado a" desde tarea_keikaku_data.PersonasAsignadas.Text
      if (!this.albaran) return null;
      
      const tareaKeikakuData = this.albaran.tarea_keikaku_data || this.albaran.attributes?.tarea_keikaku_data;
      
      if (tareaKeikakuData && tareaKeikakuData.PersonasAsignadas) {
        // PersonasAsignadas puede tener Text o TextVa
        const personasAsignadas = tareaKeikakuData.PersonasAsignadas.Text || tareaKeikakuData.PersonasAsignadas.TextVa;
        if (personasAsignadas) {
          return personasAsignadas;
        }
      }
      
      return null;
    },
    observacionesCompletas() {
      if (!this.albaran) return 'Sin observaciones';
      
      const observaciones = this.albaran.observaciones || this.albaran.attributes?.observaciones || '';
      const creadoPor = this.getCreadoPor;
      const preasignadoPor = this.getPreasignadoPor;
      
      let resultado = '';
      
      // Añadir información de creado por
      if (creadoPor) {
        resultado += `Creado por: ${creadoPor}\n`;
      }
      
      // Añadir información de preasignado a
      if (preasignadoPor) {
        resultado += `Preasignado a: ${preasignadoPor}\n`;
      }
      
      // Añadir las observaciones originales (sin la parte de preasignación si ya la extrajimos)
      if (observaciones) {
        let observacionesLimpias = observaciones;
        
        // Remover la parte de preasignación si ya la extrajimos
        observacionesLimpias = observacionesLimpias.replace(/Preasignado a .+?(?:\s*-|$)/i, '').trim();
        observacionesLimpias = observacionesLimpias.replace(/Preasignado automáticamente/i, '').trim();
        
        // Si hay más contenido después de limpiar, añadirlo
        if (observacionesLimpias && observacionesLimpias.length > 0) {
          // Si empieza con " - ", quitarlo
          if (observacionesLimpias.startsWith(' - ')) {
            observacionesLimpias = observacionesLimpias.substring(3);
          }
          
          if (resultado) {
            resultado += '\n' + observacionesLimpias;
          } else {
            resultado = observacionesLimpias;
          }
        }
      }
      
      return resultado || 'Sin observaciones';
    },
    getTareaKeikakuDataFlattened() {
      // Aplanar la estructura de tarea_keikaku_data (Properties con Value/Text)
      const tareaData = this.getAlbaranAttributes.tarea_keikaku_data;
      if (!tareaData) return {};
      
      const flattened = {};
      
      // Si tiene Properties (estructura de Keikaku API)
      if (tareaData.Properties) {
        Object.keys(tareaData.Properties).forEach(key => {
          const prop = tareaData.Properties[key];
          if (prop && typeof prop === 'object') {
            // Si tiene Value, usar Value; si tiene Text, usar Text; si tiene ambos, mostrar ambos
            if (prop.Value !== undefined) {
              flattened[key] = prop;
            } else if (prop.Text !== undefined || prop.TextVa !== undefined) {
              flattened[key] = prop;
            } else {
              flattened[key] = prop;
            }
          } else {
            flattened[key] = prop;
          }
        });
      } else {
        // Si no tiene Properties, usar directamente los datos
        Object.keys(tareaData).forEach(key => {
          flattened[key] = tareaData[key];
        });
      }
      
      return flattened;
    },
    getIdTareaFromData() {
      // Verificar tarea_keikaku_id en ambas estructuras posibles
      const tareaKeikakuId = this.albaran?.tarea_keikaku_id || 
                             this.albaran?.attributes?.tarea_keikaku_id;
      if (tareaKeikakuId) {
        return tareaKeikakuId;
      }
      
      // Si no hay tarea_keikaku_id, intentar obtener de tarea_keikaku_data
      const tareaData = this.albaran?.tarea_keikaku_data || 
                       this.albaran?.attributes?.tarea_keikaku_data;
      if (!tareaData) return null;
      
      // Intentar obtener desde Properties.IdTarea.Value
      if (tareaData.Properties?.IdTarea?.Value) {
        return tareaData.Properties.IdTarea.Value;
      }
      
      // Intentar obtener desde IdTarea.Value directamente
      if (tareaData.IdTarea?.Value) {
        return tareaData.IdTarea.Value;
      }
      
      return null;
    },
    getIdParteFromData() {
      // Obtener IdParte de parte_trabajo
      const parteData = this.getAlbaranAttributes.parte_trabajo;
      if (!parteData) return null;
      
      return parteData.IdParte || null;
    },
    getProyectoFromData() {
      // Obtener Proyecto de parte_trabajo
      const parteData = this.getAlbaranAttributes.parte_trabajo;
      if (!parteData) return null;
      
      // Intentar obtener IdProyecto o IdProyecto_flxtext
      return parteData.IdProyecto_flxtext || parteData.IdProyecto || null;
    },
    canEdit() {
      return true;
    },
    // Función para verificar el rol del usuario actual (solo jefe de taller puede poner "Material Abandonado")
    currentUserRole() {
      const user = this.$store.getters['users/user'];
      if (user && user.role && user.role.id === 3) return 'jefe_taller';
      return null;
    },
    getMaterialesTallerCount(row) {
      const materialesTaller = row.materiales_taller;
      return materialesTaller && Array.isArray(materialesTaller) ? materialesTaller.length : 0;
    },
    getMaterialesTallerTooltip(row) {
      const materialesTaller = row.materiales_taller;
      if (!materialesTaller || !Array.isArray(materialesTaller) || materialesTaller.length === 0) {
        return "No hay materiales del taller asignados";
      }
      
      const materialesList = materialesTaller.map(m => 
        `${m.idArticulo_flxtext} (${m.cantidad} ${m.unidad || 'un'})`
      ).join('\n');
      
      return `${materialesTaller.length} materiales del taller:\n${materialesList}`;
    },
    isConsumibleValido() {
      return this.nuevoConsumible.descripcion.trim() && 
             this.nuevoConsumible.cantidad && 
             this.nuevoConsumible.cantidad > 0;
    },
    consumiblesParaIncidencia() {
      const materiales = this.getMateriales || [];
      return materiales.filter(row => {
        if (row.activo) return false;
        if (row.es_saca || row.tipo_material === 'saca') return false;
        return true;
      });
    },
    isMaterialValido() {
      // Para consumibles, son obligatorios: idArticulo, descripcion, cantidad
      return this.nuevoMaterial.idArticulo && this.nuevoMaterial.idArticulo.trim() &&
             this.nuevoMaterial.descripcion && this.nuevoMaterial.descripcion.trim() &&
             this.nuevoMaterial.cantidad && this.nuevoMaterial.cantidad > 0;
    },
    isMaterialValidoERP() {
      // Solo permitir añadir si hay un consumible del ERP seleccionado y tiene cantidad
      return this.selectedConsumibleERP && 
             this.nuevoMaterial.cantidad && 
             this.nuevoMaterial.cantidad > 0;
    },
    isValidEdit() {
      if (!this.editMaterial) return false;
      
      // Para todos los materiales validamos que haya un estado seleccionado
      return !!this.editMaterial.estado;
    },
    isValidAlbaranEdit() {
      return this.editAlbaran.titulo?.trim() &&
             this.editAlbaran.estado &&
             this.editAlbaran.fecha_entrega;
    },
    isIncidenciaConsumibleFormValida() {
      const f = this.incidenciaConsumibleForm;
      return f.albaran_activo_material != null &&
             f.cantidad_afectada != null && f.cantidad_afectada >= 1 &&
             f.motivo_consumible;
    },
    cantidadMaxIncidenciaConsumible() {
      const id = this.incidenciaConsumibleForm.albaran_activo_material;
      if (!id) return null;
      const m = (this.getMateriales || []).find(x => x.id === id);
      if (!m) return null;
      return m.cantidad != null ? Number(m.cantidad) : (this.getMaterialCantidad(m) || 999);
    },
    puedeCerrarAlbaran() {
      // Solo mostrar el botón si el albarán no está cerrado
      if (!this.albaran || this.albaran.estado === 'cerrado' || this.albaran.estado === 'cerrado_sin_tarea') {
        return false;
      }

      const materiales = this.getMateriales || [];

      // Sin materiales: se puede cerrar
      if (materiales.length === 0) {
        return true;
      }

      // Con materiales: se puede cerrar si todos están en estado final (incluye incidencia)
      // - devuelto, consumido, no_devuelto, incidencia
      const estadosFinales = ['devuelto', 'consumido', 'no_devuelto', 'incidencia'];
      const todosEnEstadoFinal = materiales.every(material => {
        const estado = material.estado || material.attributes?.estado;
        return estadosFinales.includes(estado);
      });

      return todosEnEstadoFinal;
    },
    // Albarán cerrado y con al menos un material en incidencia (indicador en la vista)
    cerradoConIncidencias() {
      const estado = this.albaran?.estado || this.albaran?.attributes?.estado;
      if (estado !== 'cerrado' && estado !== 'cerrado_sin_tarea') {
        return false;
      }
      const materiales = this.getMateriales || [];
      return materiales.some(m => {
        const e = m.estado || m.attributes?.estado;
        return e === 'incidencia';
      });
    },
    puedeEliminarAlbaran() {
      if (!this.albaran) {
        return false;
      }
      
      // El botón aparece solo si se cumplen TODAS estas condiciones:
      // 1. No debe tener materiales asignados
      // 2. No debe estar asignado a ningún usuario
      // 3. Debe estar en estado "pendiente_asignar"
      
      const materiales = this.getMateriales || [];
      if (materiales && materiales.length > 0) {
        return false;
      }
      
      const tieneUsuario = this.albaran.users_permissions_user || this.albaran.attributes?.users_permissions_user;
      if (tieneUsuario) {
        return false;
      }
      
      const estado = this.albaran.estado || this.albaran.attributes?.estado;
      if (estado !== 'pendiente_asignar') {
        return false;
      }
      
      return true;
    }
  },
  methods: {
    isAlbaranBloqueadoParaEdicion() {
      const estadosBloqueados = ["cerrado", "cerrado_sin_tarea"];
      const estadoBackendRaw = this.albaran?.estado || this.albaran?.attributes?.estado || "";
      const estadoVisualRaw = this.getEstadoAlbaran?.() || "";
      const estadoBackend = String(estadoBackendRaw).trim().toLowerCase();
      const estadoVisual = String(estadoVisualRaw).trim().toLowerCase();

      return estadosBloqueados.includes(estadoBackend) || estadosBloqueados.includes(estadoVisual);
    },
    assertAlbaranEditable() {
      if (!this.isAlbaranBloqueadoParaEdicion()) return true;
      this.$notify({
        type: "warning",
        message: `Este albarán está en estado "${this.formatEstado(this.getEstadoAlbaran())}" y no permite modificaciones.`
      });
      return false;
    },
    prepararAudioAsignacion() {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (AudioCtx && !this._audioAsignacion) {
        this._audioAsignacion = new AudioCtx();
      }
      if (this._audioAsignacion && this._audioAsignacion.state === "suspended") {
        this._audioAsignacion.resume();
      }
    },
    avisarAsignacionFallida() {
      this.reproducirTonoNoAsignado();
    },
    reproducirTonoNoAsignado() {
      this.prepararAudioAsignacion();
      const ctx = this._audioAsignacion;
      if (!ctx) return;
      const start = ctx.currentTime + 0.05;
      [523, 349, 523, 349, 523, 349].forEach((freq, index) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        const t0 = start + index * 0.22;
        osc.type = "square";
        osc.frequency.setValueAtTime(freq, t0);
        gain.gain.setValueAtTime(0.0001, t0);
        gain.gain.exponentialRampToValueAtTime(0.22, t0 + 0.015);
        gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.14);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(t0);
        osc.stop(t0 + 0.16);
      });
    },
    // Extraer identificador de una URL (último segmento después del último /)
    extractIdentifierFromUrl(input) {
      if (!input) return input;
      
      // Si es una URL de misepis.com/code/, extraer el código después de code/
      if (input.includes('misepis.com/code/')) {
        const codeMatch = input.split('code/').pop().split('/')[0];
        if (codeMatch) {
          console.log(`URL misepis.com detectada: ${input} -> Código extraído: ${codeMatch}`);
          return codeMatch;
        }
      }
      
      // Si es una URL de ssea.es/, extraer el código después de ssea.es/
      if (input.includes('ssea.es/')) {
        const codeMatch = input.split('ssea.es/').pop().split('/')[0];
        if (codeMatch) {
          console.log(`URL ssea.es detectada: ${input} -> Código extraído: ${codeMatch}`);
          return codeMatch;
        }
      }
      
      // Verificar si es una URL (contiene http, https, o múltiples /)
      if (input.includes('http') || input.split('/').length > 2) {
        // Extraer el último segmento después del último /
        const segments = input.split('/');
        const lastSegment = segments[segments.length - 1];
        
        // Si el último segmento está vacío, tomar el penúltimo
        const identifier = lastSegment || segments[segments.length - 2];
        
        console.log(`URL detectada: ${input} -> Identificador extraído: ${identifier}`);
        return identifier;
      }
      
      return input;
    },

    openImportMaterialesModal() {
      this.showImportMaterialesModal = true;
    },

    openTransferMaterialesModal() {
      this.showTransferMaterialesModal = true;
    },

    openIncidenciaConsumibleModal() {
      this.incidenciaConsumibleForm = {
        albaran_activo_material: null,
        cantidad_afectada: 1,
        motivo_consumible: 'perdida',
        descripcion: ''
      };
      this.showIncidenciaConsumibleModal = true;
      this.loadIncidenciasAlbaran();
    },
    cerrarIncidenciaConsumibleModal() {
      this.showIncidenciaConsumibleModal = false;
    },
    async loadIncidenciasAlbaran() {
      const albaranId = this.albaran?.id ?? this.albaran?.attributes?.id;
      if (!albaranId) return;
      try {
        const res = await axios.get(
          `${this.apiUrl}/incidencia-albarans?filters[albaran_activo][id][$eq]=${albaranId}&filters[albaran_activo_material][id][$notNull]=true&populate[albaran_activo_material]=*&populate[estado_incidencia]=*`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
        this.incidenciasAlbaran = (res.data?.data || [])
          .map(x => x.attributes ? { id: x.id, ...x.attributes } : x)
          .filter(ia => this.getIncidenciaMaterialId(ia) != null);
      } catch (e) {
        console.error('Error cargando incidencias albarán:', e);
        this.incidenciasAlbaran = [];
      }
    },
    getMotivoConsumibleLabel(motivo) {
      const map = { perdida: 'Pérdida', rotura: 'Rotura', no_retorna_taller: 'No retorna a taller', otro: 'Otro' };
      return map[motivo] || motivo;
    },
    getEstadoIncidenciaAlbaranNombre(ia) {
      const nom = ia.estado_incidencia?.nombre ?? ia.estado_incidencia?.data?.attributes?.nombre;
      return nom || 'Abierta';
    },
    estadoIncidenciaAlbaranBadge(ia) {
      const nom = (this.getEstadoIncidenciaAlbaranNombre(ia) || '').toLowerCase();
      if (nom.includes('resuelta') || nom.includes('cerrada')) return 'badge-success';
      return 'badge-warning';
    },
    getMaterialNombreIncidenciaAlbaran(ia) {
      const relation = ia?.albaran_activo_material;
      const m = relation?.data ?? relation;
      if (m && typeof m === 'object') {
        const att = m.attributes || m;
        return att.descripcion || att.idArticulo_flxtext || att.nombre || att.idArticulo || 'Consumible';
      }

      // Fallback: cuando Strapi devuelve solo el ID de la relación
      const materialId = this.getIncidenciaMaterialId(ia);
      if (materialId != null) {
        const material = this.getMateriales.find(x => String(x.id) === String(materialId));
        if (material) {
          return material.descripcion || material.idArticulo_flxtext || material.idArticulo || 'Consumible';
        }
      }

      return 'Consumible';
    },
    async crearIncidenciaConsumible() {
      if (!this.isIncidenciaConsumibleFormValida) return;
      const albaranId = this.albaran?.id ?? this.albaran?.attributes?.id;
      if (!albaranId) return;
      this.guardandoIncidenciaConsumible = true;
      try {
        const payload = {
          data: {
            albaran_activo: albaranId,
            albaran_activo_material: this.incidenciaConsumibleForm.albaran_activo_material,
            cantidad_afectada: this.incidenciaConsumibleForm.cantidad_afectada,
            motivo_consumible: this.incidenciaConsumibleForm.motivo_consumible,
            descripcion: this.incidenciaConsumibleForm.descripcion || undefined,
            fecha: new Date().toISOString(),
            usuario: 333
          }
        };
        await axios.post(`${this.apiUrl}/incidencia-albarans`, payload, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (this.$message && typeof this.$message.success === 'function') {
          this.$message.success('Incidencia sobre consumible registrada. El material queda en estado Incidencia.');
        }
        this.incidenciaConsumibleForm.albaran_activo_material = null;
        this.incidenciaConsumibleForm.cantidad_afectada = 1;
        this.incidenciaConsumibleForm.descripcion = '';
        this.showIncidenciaConsumibleModal = false;
        await this.loadIncidenciasAlbaran();
        await new Promise((r) => setTimeout(r, 500));
        await this.loadAlbaran();
        await this.actualizarEstadoAlbaran();
        this.$forceUpdate();
        if (this.$notify && typeof this.$notify === 'function') {
          this.$notify({ type: 'success', message: 'Datos actualizados' });
        }
      } catch (err) {
        let msg = 'Error al registrar la incidencia';
        try {
          const d = err && err.response && err.response.data;
          if (d != null) {
            if (typeof d === 'string') msg = d;
            else if (typeof d === 'object') {
              const e = d.error;
              if (e != null && typeof e === 'object' && typeof e.message === 'string') msg = e.message;
              else if (typeof d.message === 'string') msg = d.message;
            }
          }
          if (msg === 'Error al registrar la incidencia' && err && typeof err.message === 'string') msg = err.message;
        } catch (_) {}
        if (this.$message && this.$message.error) {
          this.$message.error(msg);
        } else {
          console.error('crearIncidenciaConsumible error:', msg, err);
        }
      } finally {
        this.guardandoIncidenciaConsumible = false;
      }
    },

    async initTransferMateriales() {
      this.sourceAlbaranIdTransfer = null;
      this.transferCandidates = [];
      await this.fetchTransferCandidates();
    },

    async fetchTransferCandidates() {
      if (!this.puedeMostrarTransferenciaMateriales) return;
      const fechaEntrega = this.fechaEntregaYMD;
      const albaranId = this.albaran?.id || this.albaran?.attributes?.id;
      if (!fechaEntrega || !albaranId) return;
      this.loadingTransferCandidates = true;
      try {
        const response = await axios.get(`${this.apiUrl}/albaran-activo-list`, {
          params: {
            fechaEntrega,
            excludeId: albaranId,
            sortOrder: "desc",
          },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        this.transferCandidates = response.data?.albaranes || [];
      } catch (error) {
        console.error("Error cargando albaranes para transferencia:", error);
        this.transferCandidates = [];
        this.$notify({
          type: "danger",
          message: "Error al cargar albaranes del día: " + (error.response?.data?.error?.message || error.message),
        });
      } finally {
        this.loadingTransferCandidates = false;
      }
    },

    async transferirMaterialesDesdeOtroAlbaran() {
      const albaranDestinoId = this.albaran?.id || this.albaran?.attributes?.id;
      if (!albaranDestinoId || !this.sourceAlbaranIdTransfer) return;
      this.transferiendoMateriales = true;
      try {
        const response = await axios.post(
          `${this.apiUrl}/albaran-transfer-materials`,
          {
            albaranOrigenId: this.sourceAlbaranIdTransfer,
            albaranDestinoId,
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        const data = response.data;
        await this.loadAlbaran();
        this.showTransferMaterialesModal = false;
        this.$notify({
          type: "success",
          message: data?.message || `Se han transferido ${data?.moved ?? 0} material(es) a este albarán.`,
        });
      } catch (error) {
        const msg = error.response?.data?.error?.message || error.response?.data?.message || error.message;
        this.$notify({
          type: "danger",
          message: "Error al transferir materiales: " + msg,
        });
      } finally {
        this.transferiendoMateriales = false;
      }
    },

    async initImportMateriales() {
      // Reset estado del modal cada vez que se abre
      this.sourceAlbaranId = null;
      this.importCandidates = [];
      await this.fetchImportCandidates();
    },

    formatAlbaranCandidate(a) {
      const numero = a?.numero || a?.attributes?.numero || `#${a?.id}`;
      const titulo = a?.titulo || a?.attributes?.titulo;
      const cliente = a?.cliente || a?.attributes?.cliente;

      // Formato "normal": NUMERO - TITULO
      // Fallback: NUMERO - CLIENTE
      const second = (titulo || cliente || "").trim();
      return second ? `${numero} - ${second}` : `${numero}`;
    },

    async fetchImportCandidates() {
      if (!this.puedeMostrarImportacionMateriales) return;

      const tareaId =
        this.albaran?.tarea_keikaku_id || this.albaran?.attributes?.tarea_keikaku_id;
      const fechaEntrega = this.fechaEntregaYMD;
      const albaranId = this.albaran?.id || this.albaran?.attributes?.id;

      if (!tareaId || !fechaEntrega || !albaranId) return;

      this.loadingImportCandidates = true;
      try {
        const response = await axios.get(`${this.apiUrl}/albaran-activo-list`, {
          params: {
            fechaEntrega,
            tareaKeikakuId: tareaId,
            excludeId: albaranId,
            sortOrder: "desc",
          },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });

        this.importCandidates = response.data?.albaranes || [];
      } catch (error) {
        console.error("Error cargando albaranes candidatos para importación:", error);
        this.importCandidates = [];
        this.$notify({
          type: "danger",
          message:
            "Error al cargar albaranes candidatos: " +
            (error.response?.data?.error?.message || error.message),
        });
      } finally {
        this.loadingImportCandidates = false;
      }
    },

    getCantidadConsumibleEnAlbaran(idArticulo) {
      if (!idArticulo) return 0;
      const idLower = String(idArticulo).toLowerCase();
      return (this.getMateriales || [])
        .filter(
          (m) =>
            !m.activo &&
            !(m.es_saca || m.tipo_material === "saca") &&
            m.idArticulo &&
            String(m.idArticulo).toLowerCase() === idLower
        )
        .reduce((sum, m) => sum + (parseInt(m.cantidad) || 0), 0);
    },

    async importarMaterialesDesdeOtroAlbaran() {
      if (!this.albaran?.id || !this.sourceAlbaranId) return;

      this.importandoMateriales = true;
      try {
        const detalleRes = await axios.get(
          `${this.apiUrl}/albaran-activo-detalle/${this.sourceAlbaranId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        const albaranOrigen = detalleRes.data;
        const materialesOrigen =
          albaranOrigen?.albaran_activo_materials || albaranOrigen?.materiales || [];

        const tareaOrigen =
          albaranOrigen?.tarea_keikaku_id || albaranOrigen?.attributes?.tarea_keikaku_id;
        const fechaOrigen = albaranOrigen?.fecha_entrega || albaranOrigen?.attributes?.fecha_entrega;

        // Guard-rails: validar que coincide tarea + fecha (día)
        const tareaActual = this.albaran?.tarea_keikaku_id || this.albaran?.attributes?.tarea_keikaku_id;
        const fechaActual = this.getAlbaranAttributes.fecha_entrega;
        const fechaOrigenYMD = fechaOrigen ? moment(fechaOrigen).format("YYYY-MM-DD") : null;
        const fechaActualYMD = fechaActual ? moment(fechaActual).format("YYYY-MM-DD") : null;

        if (!tareaOrigen || String(tareaOrigen) !== String(tareaActual) || fechaOrigenYMD !== fechaActualYMD) {
          this.$notify({
            type: "warning",
            message:
              "El albarán origen no coincide en tarea Keikaku y/o fecha de entrega con el albarán actual.",
          });
          return;
        }

        const currentActivoIds = new Set(
          (this.getMateriales || [])
            .filter((m) => m.activo && m.activo.id)
            .map((m) => m.activo.id)
        );

        // Agrupar consumibles del origen por idArticulo para importar solo diferencias
        const consumiblesOrigen = new Map(); // idArticulo -> { cantidad, descripcion, material_keikaku_id }

        let activosImportados = 0;
        let activosOmitidos = 0;
        let consumiblesImportados = 0;
        let consumiblesOmitidos = 0;
        let sacasOmitidas = 0;
        let errores = 0;

        for (const mat of materialesOrigen) {
          // Omitir sacas (no hay endpoint de importación de saca aquí)
          if (mat?.es_saca || mat?.tipo_material === "saca") {
            sacasOmitidas++;
            continue;
          }

          // Activos
          if (mat?.activo) {
            const activoId = mat.activo.id;
            const qr = mat.activo.numero_serie_qr;

            if (!qr) {
              activosOmitidos++;
              continue;
            }
            if (activoId && currentActivoIds.has(activoId)) {
              activosOmitidos++;
              continue;
            }

            const payload = {
              tipo_material: "activo",
              qr,
            };

            // Copiar metadatos opcionales si existen
            if (mat.idArticulo) payload.idArticulo = mat.idArticulo;
            if (mat.descripcion) payload.descripcion = mat.descripcion;
            if (mat.cantidad !== undefined && mat.cantidad !== null) payload.cantidad = mat.cantidad;
            if (mat.material_keikaku_id) payload.material_keikaku_id = mat.material_keikaku_id;

            try {
              await axios.post(`${this.apiUrl}/albaran-add/${this.albaran.id}`, payload, {
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
              });
              activosImportados++;
            } catch (e) {
              // Si ya existe por QR/activo, tratar como omitido
              activosOmitidos++;
              console.error("Error importando activo desde otro albarán:", payload, e);
            }
            continue;
          }

          // Consumibles (sin activo)
          if (mat?.idArticulo) {
            const key = String(mat.idArticulo);
            const prev = consumiblesOrigen.get(key) || {
              cantidad: 0,
              descripcion: mat.descripcion,
              material_keikaku_id: mat.material_keikaku_id,
            };
            prev.cantidad += parseInt(mat.cantidad) || 0;
            if (!prev.descripcion && mat.descripcion) prev.descripcion = mat.descripcion;
            if (!prev.material_keikaku_id && mat.material_keikaku_id)
              prev.material_keikaku_id = mat.material_keikaku_id;
            consumiblesOrigen.set(key, prev);
          }
        }

        // Importar consumibles por diferencia
        for (const [idArticulo, info] of consumiblesOrigen.entries()) {
          const origenQty = info.cantidad || 0;
          if (origenQty <= 0) {
            consumiblesOmitidos++;
            continue;
          }
          const currentQty = this.getCantidadConsumibleEnAlbaran(idArticulo);
          const missing = origenQty - currentQty;
          if (missing <= 0) {
            consumiblesOmitidos++;
            continue;
          }

          const payload = {
            tipo_material: "consumible",
            idArticulo,
            descripcion: info.descripcion || idArticulo,
            cantidad: missing,
          };
          if (info.material_keikaku_id) payload.material_keikaku_id = info.material_keikaku_id;

          try {
            await axios.post(`${this.apiUrl}/albaran-add/${this.albaran.id}`, payload, {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            });
            consumiblesImportados++;
          } catch (e) {
            errores++;
            console.error("Error importando consumible desde otro albarán:", payload, e);
          }
        }

        await this.loadAlbaran();

        this.$notify({
          type: errores > 0 ? "warning" : "success",
          message:
            `Importación desde albarán completada. ` +
            `Activos importados: ${activosImportados}, omitidos: ${activosOmitidos}. ` +
            `Consumibles importados: ${consumiblesImportados}, omitidos: ${consumiblesOmitidos}. ` +
            (sacasOmitidas ? `Sacas omitidas: ${sacasOmitidas}. ` : "") +
            `Errores: ${errores}.`,
        });
      } finally {
        this.importandoMateriales = false;
      }
    },
    
    async loadAlbaran() {
      try {
        this.loading = true;
        const id = this.$route.params.id;
        
        // Limpiar cache de incidencias al cargar un nuevo albarán
        this.incidenciasCache = {};
        // Llamada directa a la API con populate=*
        const response = await axios.get(`${this.apiUrl}/albaran-activo-detalle/${id}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        console.log('Respuesta directa axios:', response.data);
        this.albaran = response.data;
        
        // Log del estado inicial del backend
        const estadoInicialBackend = this.albaran.estado || this.albaran.attributes?.estado;
        console.log('🔍 [LOAD] Estado inicial del backend:', estadoInicialBackend);
        console.log('🔍 [LOAD] Materiales en albarán:', (this.albaran.albaran_activo_materials || []).length);
        
        // Cargar materiales marcados manualmente desde materiales_taller
        this.cargarMaterialesMarcadosManual();
        
        // Actualizar el estado del albarán basándose en los materiales actuales
        // Esto corrige discrepancias entre el estado del backend y el estado real de los materiales
        await this.$nextTick();
        const estadoAntesActualizar = this.getEstadoAlbaran();
        console.log('🔍 [LOAD] Estado antes de actualizar:', estadoAntesActualizar);
        await this.actualizarEstadoAlbaran();
        const estadoDespuesActualizar = this.getEstadoAlbaran();
        console.log('🔍 [LOAD] Estado después de actualizar:', estadoDespuesActualizar);
        
        // Cargar TODOS los materiales pendientes de aceptación sin límite de pageSize
        // Esto asegura que el modal de revisión muestre todos los materiales
        try {
          const materialesResponse = await axios.get(
            `${this.apiUrl}/albaran-activo-materials?filters[albaran_activo][id][$eq]=${id}&filters[estado][$eq]=pendiente_aceptar_devolucion&pagination[pageSize]=10000&populate=*`,
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              }
            }
          );
          
          // Si hay materiales pendientes que no están en la respuesta principal, agregarlos
          const materialesPendientes = materialesResponse.data.data || [];
          if (materialesPendientes.length > 0) {
            // Combinar con los materiales existentes, evitando duplicados
            const materialesExistentesIds = new Set(
              (this.albaran.albaran_activo_materials || []).map(m => m.id)
            );
            
            materialesPendientes.forEach(material => {
              if (!materialesExistentesIds.has(material.id)) {
                if (!this.albaran.albaran_activo_materials) {
                  this.albaran.albaran_activo_materials = [];
                }
                this.albaran.albaran_activo_materials.push(material);
              }
            });
          }
        } catch (error) {
          console.error('Error cargando materiales pendientes:', error);
          // No es crítico, continuar con los materiales que ya tenemos
        }
        
        // Cargar incidencias para todos los activos del albarán en una sola llamada optimizada
        if (this.albaran.albaran_activo_materials) {
          // Obtener todos los IDs únicos de activos
          const activosIds = new Set();
          for (const material of this.albaran.albaran_activo_materials) {
            if (material.activo?.id) {
              activosIds.add(material.activo.id);
            }
          }
          
          if (activosIds.size > 0) {
            try {
              // Hacer una sola llamada para obtener todas las incidencias de todos los activos
              const activosIdsArray = Array.from(activosIds);
              console.log(`🔍 Cargando incidencias para ${activosIdsArray.length} activos en una sola llamada:`, activosIdsArray);
              
              // Construir el filtro $in para obtener solo las incidencias ACTIVAS (no resueltas) de los activos del albarán
              // Sintaxis correcta para Strapi v4: filters[activo][id][$in][0]=id1&filters[activo][id][$in][1]=id2...
              // Además, filtramos por estado_incidencia != "Resuelta" para optimizar la carga
              const filtersActivos = activosIdsArray.map((id, index) => `filters[activo][id][$in][${index}]=${id}`).join('&');
              // Filtrar solo incidencias no resueltas (estado != 2 que es "Resuelta")
              const filtersEstado = 'filters[estado_incidencia][id][$ne]=2';
              const filters = `${filtersActivos}&${filtersEstado}`;
              const response = await axios.get(`${this.apiUrl}/incidencias?${filters}&populate=*`, {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
              });
              
              const todasLasIncidencias = response.data.data || [];
              console.log(`✅ Se obtuvieron ${todasLasIncidencias.length} incidencias en total para ${activosIdsArray.length} activos`);
              
              // Agrupar incidencias por activo
              const incidenciasPorActivo = {};
              todasLasIncidencias.forEach(incidencia => {
                const activoId = incidencia.activo?.id || 
                                 incidencia.attributes?.activo?.data?.id ||
                                 incidencia.activo?.data?.id;
                if (activoId) {
                  if (!incidenciasPorActivo[activoId]) {
                    incidenciasPorActivo[activoId] = [];
                  }
                  incidenciasPorActivo[activoId].push(incidencia);
                }
              });
              
              // Guardar en cache y asignar a los materiales
              for (const material of this.albaran.albaran_activo_materials) {
                if (material.activo?.id) {
                  const activoId = material.activo.id;
                  const incidencias = incidenciasPorActivo[activoId] || [];
                  
                  // Guardar en cache
                  this.$set(this.incidenciasCache, activoId, incidencias);
                  
                  // Asignar a este material
                this.$set(material, 'incidencias', incidencias);
                
                if (incidencias.length > 0) {
                    console.log(`📦 Activo ID: ${activoId} (Nombre: ${material.activo?.nombre}): ${incidencias.length} incidencias asignadas`);
                  }
                } else {
                  // Si no tiene activo, asignar array vacío
                  this.$set(material, 'incidencias', []);
                }
                }
              } catch (error) {
              console.error('Error cargando incidencias para activos del albarán:', error);
              // Asignar array vacío a todos los materiales en caso de error
              for (const material of this.albaran.albaran_activo_materials) {
                this.$set(material, 'incidencias', []);
              }
            }
          } else {
            // Si no hay activos, asignar array vacío a todos los materiales
            for (const material of this.albaran.albaran_activo_materials) {
              this.$set(material, 'incidencias', []);
            }
          }
        }
        
        // Verificar y actualizar el estado del albarán basándose en los estados de los materiales
        // Solo actualizar si el estado actual no es 'cerrado' o 'cerrado_sin_tarea' (estados finales)
        const estadoActual = this.albaran.estado || this.albaran.attributes?.estado;
        if (estadoActual !== 'cerrado' && estadoActual !== 'cerrado_sin_tarea') {
          await this.actualizarEstadoAlbaran();
        }

        // Cargar incidencias de consumibles del albarán para mostrarlas en la vista
        await this.loadIncidenciasAlbaran();
     
      } catch (error) {
        console.error("Error loading albaran:", error);
        this.$notify({
          type: "danger",
          message: `Error al cargar el albarán: ${error.message}`
        });
      } finally {
        this.loading = false;
      }
    },

    async refrescarAlbaran() {
      try {
        await this.loadAlbaran();
        await this.actualizarEstadoAlbaran();
        this.$notify({
          type: 'success',
          message: 'Materiales actualizados correctamente'
        });
      } catch (error) {
        console.error('Error al refrescar el albarán:', error);
        this.$notify({
          type: 'danger',
          message: 'Error al actualizar los materiales'
        });
      }
    },

    formatDate(date) {
      return date ? moment(date).format("DD/MM/YYYY") : "-";
    },
    formatDateForInput(date) {
      if (!date) return '';
      return moment(date).format('YYYY-MM-DD');
    },
    formatEstado(estado) {
      return getEstadoTextoUtil(estado);
    },

    getEstadoBadgeClass(estado) {
      return getEstadoBadgeClassUtil(estado);
    },

    getEstadoColor(estado) {
      // Usar los colores centralizados de estadoColors.js
      const color = getEstadoColorUtil(estado);
      // Si el color es transparente, usar el color transparente original
      if (color === 'transparent') {
        return 'rgba(255, 255, 255, 0.3)';
      }
      return color;
    },

    getEstadoColorMaterial(estado) {
      // Usar los colores centralizados de estadoColors.js para materiales
      const color = getEstadoColorUtil(estado, 'material');
      return color || '#6c757d';
    },

    async getIncidencesForActivo(activoId) {
      // Verificar si ya tenemos las incidencias en cache
      if (this.incidenciasCache[activoId]) {
        console.log(`✅ Usando cache de incidencias para activo ${activoId}`);
        return this.incidenciasCache[activoId];
      }
      
      try {
        console.log(`🔍 Cargando incidencias desde API para activo ${activoId}`);
        const response = await axios.get(`${this.apiUrl}/incidencias?filters[activo]=${activoId}&populate=*`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        const incidencias = response.data.data || [];
        
        // Guardar en cache
        this.$set(this.incidenciasCache, activoId, incidencias);
        
        console.log(`🔍 API INCIDENCIAS para activo ${activoId}:`, {
          total: incidencias.length,
          desdeCache: false,
          incidencias: incidencias.map(inc => ({
            id: inc.id || inc.data?.id,
            estado: inc.attributes?.estado_incidencia?.data?.attributes?.nombre ||
                   inc.estado_incidencia?.data?.attributes?.nombre ||
                   inc.estado_incidencia?.nombre ||
                   inc.attributes?.estado_incidencia?.nombre || 'sin estado',
            estructura: {
              tieneAttributes: !!inc.attributes,
              tieneEstadoIncidencia: !!(inc.attributes?.estado_incidencia || inc.estado_incidencia),
              estructuraCompleta: inc.attributes?.estado_incidencia?.data?.attributes ? 'Strapi v4 completa' :
                                 inc.estado_incidencia?.data?.attributes ? 'Strapi v4 parcial' :
                                 inc.estado_incidencia?.nombre ? 'Formato directo' : 'Estructura desconocida'
            }
          }))
        });
        return incidencias;
      } catch (error) {
        console.error(`❌ Error obteniendo incidencias para activo ${activoId}:`, error);
        // Guardar array vacío en cache para evitar reintentos innecesarios
        this.$set(this.incidenciasCache, activoId, []);
        return [];
      }
    },
    goBack() {
      this.$router.push({ name: "List Albaran" });
    },
    
    puedeEliminarAlbaranValidacion() {
      // Esta función contiene la lógica de validación real
      // Se usa en eliminarAlbaran() para verificar si realmente se puede eliminar
      // Independientemente del tipo de albarán (manual, clonado o normal)
      if (!this.albaran) {
        return { puede: false, motivo: "No hay datos del albarán" };
      }
      
      const materiales = this.getMateriales || [];
      const tieneUsuario = this.albaran.users_permissions_user || this.albaran.attributes?.users_permissions_user;
      const estado = this.albaran.estado || this.albaran.attributes?.estado;
      
      // Verificar condiciones: independientemente del tipo de albarán
      if (materiales && materiales.length > 0) {
        return { puede: false, motivo: "No se puede eliminar un albarán que tiene materiales asignados" };
      }
      
      if (tieneUsuario) {
        return { puede: false, motivo: "No se puede eliminar un albarán que está asignado a un usuario" };
      }
      
      if (estado !== 'pendiente_asignar') {
        return { puede: false, motivo: `No se puede eliminar un albarán en estado "${this.formatEstado(estado)}". Solo se pueden eliminar albaranes en estado "Pendiente asignar" sin materiales asignados y sin usuario asignado.` };
      }
      
      return { puede: true };
    },
    
    async eliminarAlbaran() {
      if (!this.albaran) return;
      
      // Verificar si se puede eliminar usando la validación completa
      const validacion = this.puedeEliminarAlbaranValidacion();
      if (!validacion.puede) {
        this.$notify({
          type: "warning",
          message: validacion.motivo || "No se puede eliminar este albarán clonado."
        });
        return;
      }

      try {
        const tituloConfirmacion = "¿Estás seguro de eliminar este albarán?";
        const textoConfirmacion = "Esta acción eliminará el albarán. Esta acción no se puede deshacer.";
        
        const result = await swal.fire({
          title: tituloConfirmacion,
          text: textoConfirmacion,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Sí, eliminar",
          cancelButtonText: "Cancelar",
          confirmButtonClass: "btn btn-danger",
          cancelButtonClass: "btn btn-secondary",
        });

        if (result.value) {
        this.loading = true;

          await axios.delete(
            `${this.apiUrl}/albaran-activos/${this.albaran.id}`,
            {
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              }
            }
          );
          
          const numero = this.albaran.numero || this.albaran.attributes?.numero;
          const esManual = numero && typeof numero === 'string' && numero.startsWith('ST-');
          const mensaje = esManual 
            ? "Albarán manual eliminado correctamente"
            : "Albarán clonado eliminado correctamente";
          
          this.$notify({
            type: "success",
            message: mensaje
          });
          
          // Redirigir al listado
          this.$router.push({ name: "List Albaran" });
        }
      } catch (error) {
        console.error("Error eliminando albarán:", error);
        this.$notify({
          type: "danger",
          message: `Error al eliminar el albarán: ${error.response?.data?.error?.message || error.message || "Error desconocido"}`
        });
      } finally {
        this.loading = false;
      }
    },
    
    async eliminarAlbaranDesdeModal() {
      // Cerrar el modal primero
      this.cerrarModalEditAlbaran();
      
      // Llamar a la función de eliminación
      await this.eliminarAlbaran();
    },
    
    async actualizarMaterialesDesdeKeikaku() {
      if (!this.assertAlbaranEditable()) return;
      if (!this.albaran || !this.albaran.tarea_keikaku_id) {
        this.$notify({
          type: "warning",
          message: "Este albarán no tiene una tarea de Keikaku asociada"
        });
        return;
      }

      try {
        this.actualizandoMateriales = true;
        const tareaKeikakuId = this.albaran.tarea_keikaku_id;

        console.log('🔄 [ACTUALIZAR] Iniciando actualización de materiales desde Keikaku para tarea:', tareaKeikakuId);

        // Crear instancia de axios para Keikaku
        const axiosInstance = axios.create({
          headers: {
            'Accept': undefined,
            'Content-Type': undefined
          }
        });

        // Obtener materiales de Keikaku para esta tarea específica
        const materialQueryParams = new URLSearchParams({
          page: '0',
          pageSize: '100',
          orderBy: 'IdTarea desc',
          access_token: KEIKAKU_ACCESS_TOKEN
        }).toString();

        const materialConfig = {
          method: 'get',
          maxBodyLength: Infinity,
          url: `https://aplicaciones.salvadorvertical.com/Keikaku/webapi/list/Tarea_Material?${materialQueryParams}`,
          headers: {}
        };

        console.log('📊 [ACTUALIZAR] Consultando materiales en Keikaku...');
        const materialResponse = await axiosInstance.request(materialConfig);

        // Filtrar materiales solo de esta tarea
        const materialesKeikaku = materialResponse.data.filter(item => 
          String(item.IdTarea) === String(tareaKeikakuId)
        );

        console.log('📊 [ACTUALIZAR] Materiales encontrados en Keikaku:', {
          tareaId: tareaKeikakuId,
          cantidad: materialesKeikaku.length,
          materiales: materialesKeikaku.map(m => m.IdArticulo_flxtext)
        });

        if (materialesKeikaku.length === 0) {
          this.$notify({
            type: "warning",
            message: "No se encontraron materiales en Keikaku para esta tarea"
          });
          return;
        }

        // Obtener materiales actuales del albarán
        const materialesActuales = this.getMaterialesTaller || [];
        
        // Normalizar IDs para comparación (convertir a string y manejar null/undefined)
        const normalizarId = (id) => {
          if (id === null || id === undefined) return null;
          return String(id).trim();
        };
        
        const idsMaterialesActuales = materialesActuales
          .map(m => normalizarId(m.idArticulo_flxtext))
          .filter(id => id !== null);
        const idsMaterialesKeikaku = materialesKeikaku
          .map(m => normalizarId(m.IdArticulo_flxtext))
          .filter(id => id !== null);

        console.log('📦 [ACTUALIZAR] Materiales actuales en albarán:', {
          cantidad: materialesActuales.length,
          ids: idsMaterialesActuales,
          materiales: materialesActuales.map(m => ({
            idArticulo: m.idArticulo,
            idArticulo_flxtext: m.idArticulo_flxtext,
            descripcion: m.descripcion
          }))
        });

        // Identificar materiales nuevos (están en Keikaku pero no en el albarán)
        const materialesNuevos = materialesKeikaku.filter(material => {
          const idKeikaku = normalizarId(material.IdArticulo_flxtext);
          return idKeikaku && !idsMaterialesActuales.includes(idKeikaku);
        });

        // Identificar materiales a eliminar (están en el albarán pero no en Keikaku)
        const materialesAEliminar = materialesActuales.filter(material => {
          const idActual = normalizarId(material.idArticulo_flxtext);
          return idActual && !idsMaterialesKeikaku.includes(idActual);
        });

        console.log('📦 [ACTUALIZAR] Análisis de cambios:', {
          nuevos: materialesNuevos.length,
          aEliminar: materialesAEliminar.length,
          materialesNuevos: materialesNuevos.map(m => m.IdArticulo_flxtext),
          materialesAEliminar: materialesAEliminar.map(m => m.idArticulo_flxtext)
        });

        if (materialesNuevos.length === 0 && materialesAEliminar.length === 0) {
          this.$notify({
            type: "info",
            message: "Los materiales ya están actualizados. No hay cambios."
          });
          return;
        }

        // Construir la lista final de materiales
        // Primero, mantener los que siguen en Keikaku (actualizando sus datos si es necesario)
        const materialesFinales = materialesActuales
          .filter(material => {
            const idActual = normalizarId(material.idArticulo_flxtext);
            return idActual && idsMaterialesKeikaku.includes(idActual);
          })
          .map(material => {
            // Buscar el material correspondiente en Keikaku para actualizar datos si es necesario
            const materialKeikaku = materialesKeikaku.find(m => 
              normalizarId(m.IdArticulo_flxtext) === normalizarId(material.idArticulo_flxtext)
            );
            
            if (materialKeikaku) {
              // Actualizar con datos de Keikaku pero mantener campos adicionales del albarán
              return {
                ...material,
                idArticulo: materialKeikaku.IdArticulo || material.idArticulo,
                idArticulo_flxtext: materialKeikaku.IdArticulo_flxtext || material.idArticulo_flxtext,
                cantidad: materialKeikaku.Cantidad || material.cantidad || 1,
                descripcion: materialKeikaku.Descripcion || material.descripcion || '',
                unidad: materialKeikaku.Unidad || material.unidad || ''
              };
            }
            return material;
          });

        // Luego, añadir los nuevos
        materialesNuevos.forEach(material => {
          materialesFinales.push({
            idArticulo: material.IdArticulo,
            idArticulo_flxtext: material.IdArticulo_flxtext,
            cantidad: material.Cantidad || 1,
            descripcion: material.Descripcion || '',
            unidad: material.Unidad || '',
            marcado_manual: false // Los materiales de Keikaku no están marcados manualmente por defecto
          });
        });

        console.log('✅ [ACTUALIZAR] Actualizando albarán con materiales:', {
          albaranId: this.albaran.id,
          materialesFinales: materialesFinales.length,
          añadidos: materialesNuevos.length,
          eliminados: materialesAEliminar.length
        });

        // Actualizar el albarán
        await axios.put(
          `${this.apiUrl}/albaran-activos/${this.albaran.id}`,
          {
          data: {
              materiales_taller: materialesFinales
            }
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );

        // Recargar el albarán para ver los cambios
        await this.loadAlbaran();

        // Mostrar notificación de éxito
        let mensaje = 'Materiales actualizados correctamente.';
        if (materialesNuevos.length > 0) {
          mensaje += ` ${materialesNuevos.length} material(es) añadido(s).`;
        }
        if (materialesAEliminar.length > 0) {
          mensaje += ` ${materialesAEliminar.length} material(es) eliminado(s).`;
        }

        this.$notify({
          type: "success",
          message: mensaje
        });

      } catch (error) {
        console.error('❌ [ACTUALIZAR] Error actualizando materiales:', error);
        this.$notify({
          type: "danger",
          message: `Error al actualizar materiales desde Keikaku: ${error.response?.data?.error?.message || error.message || "Error desconocido"}`
        });
      } finally {
        this.actualizandoMateriales = false;
      }
    },

    async actualizarParteTrabajo() {
      if (!this.albaran || !this.albaran.tarea_keikaku_id) {
        this.$notify({
          type: "warning",
          message: "Este albarán no tiene una tarea de Keikaku asociada"
        });
        return;
      }

      try {
        this.actualizandoParte = true;

        const tareaKeikakuId = this.albaran.tarea_keikaku_id || this.albaran.attributes?.tarea_keikaku_id;

        console.log('🔄 [ACTUALIZAR PARTE] Paso 1: Actualizando tarea_keikaku_data desde Keikaku...');

        // Crear instancia de axios para Keikaku
        const axiosInstance = axios.create({
          headers: {
            'Accept': undefined,
            'Content-Type': undefined
          }
        });

        // 1. Primero actualizar tarea_keikaku_data desde Keikaku para obtener el IdParte actualizado
        const tareaDetalleConfig = {
          method: 'get',
          maxBodyLength: Infinity,
          url: `${KEIKAKU_API_BASE_URL}/object/Planificador_Equipo_Movil/${tareaKeikakuId}?access_token=${KEIKAKU_ACCESS_TOKEN}`,
          headers: {}
        };

        console.log('🔍 [ACTUALIZAR PARTE] Obteniendo detalles actualizados de la tarea desde Keikaku...');
        const tareaDetalleResponse = await axiosInstance.request(tareaDetalleConfig);
        const tareaDataActualizada = tareaDetalleResponse.data.Properties || tareaDetalleResponse.data;

        console.log('✅ [ACTUALIZAR PARTE] Datos actualizados de la tarea obtenidos:', {
          IdTarea: tareaDataActualizada.IdTarea?.Value,
          Titulo: tareaDataActualizada.Titulo?.Value,
          IdParte: tareaDataActualizada.IdParte?.Value,
          tieneIdParte: !!tareaDataActualizada.IdParte?.Value
        });

        // 2. Actualizar tarea_keikaku_data en el albarán
        const tareaKeikakuDataCompleto = tareaDetalleResponse.data;
        
        await axios.put(
          `${this.apiUrl}/albaran-activos/${this.albaran.id}`,
          {
            data: {
              tarea_keikaku_data: tareaKeikakuDataCompleto
            }
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );

        console.log('✅ [ACTUALIZAR PARTE] tarea_keikaku_data actualizado en el albarán');

        // 3. Obtener IdParte desde los datos actualizados
        const idParte = tareaDataActualizada.IdParte?.Value;

        if (!idParte) {
          this.$notify({
            type: "warning",
            message: "La tarea de Keikaku no tiene un IdParte asociado aún. El parte puede no haberse creado todavía."
          });
          // Recargar el albarán para reflejar el tarea_keikaku_data actualizado
          await this.loadAlbaran();
          return;
        }

        console.log('🔄 [ACTUALIZAR PARTE] Paso 2: Obteniendo parte de trabajo para IdParte:', idParte);

        // 4. Hacer la llamada a la API de SAT para obtener el parte
        const parteQueryParams = new URLSearchParams({
          filter: `Partes.IdParte=${idParte}`,
          page: '0',
          pageSize: '5000',
          orderBy: 'IdParte desc',
          access_token: SAT_ACCESS_TOKEN
        }).toString();

        const parteConfig = {
          method: 'get',
          maxBodyLength: Infinity,
          url: `https://aplicaciones.salvadorvertical.com/SAT/webapi/list/sat_Parte?${parteQueryParams}`,
          headers: {}
        };

        const parteResponse = await axiosInstance.request(parteConfig);

        if (!parteResponse.data || parteResponse.data.length === 0) {
          this.$notify({
            type: "warning",
            message: "No se encontró parte de trabajo para este IdParte"
          });
          return;
        }

        const parteTrabajo = parteResponse.data[0]; // Tomar el primer resultado

        console.log('✅ [ACTUALIZAR PARTE] Parte de trabajo obtenida:', {
          IdParte: parteTrabajo.IdParte,
          NumParte: parteTrabajo.NumParte,
          Descrip: parteTrabajo.Descrip,
          p_TareaFacturacion: parteTrabajo.p_TareaFacturacion
        });

        // Verificar si es facturable (p_TareaFacturacion = 4 o 5)
        const esFacturable = parteTrabajo.p_TareaFacturacion === 4 || parteTrabajo.p_TareaFacturacion === 5;
        
        if (esFacturable) {
          console.log('💰 [ACTUALIZAR PARTE] Parte de trabajo es facturable (p_TareaFacturacion:', parteTrabajo.p_TareaFacturacion, ')');
        }

        // 5. Actualizar el albarán con la parte de trabajo y es_facturable
        await axios.put(
          `${this.apiUrl}/albaran-activos/${this.albaran.id}`,
          {
            data: {
              parte_trabajo: parteTrabajo,
              es_facturable: esFacturable
            }
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );

        // 6. Recargar el albarán para reflejar los cambios
        await this.loadAlbaran();

        this.$notify({
          type: "success",
          message: "Parte de trabajo actualizada correctamente"
        });

      } catch (error) {
        console.error('❌ [ACTUALIZAR PARTE] Error actualizando parte de trabajo:', error);
        this.$notify({
          type: "danger",
          message: `Error al actualizar parte de trabajo: ${error.response?.data?.error?.message || error.message || "Error desconocido"}`
        });
      } finally {
        this.actualizandoParte = false;
      }
    },
    
    openCloneModal() {
      // Establecer fecha por defecto (hoy)
      const hoy = new Date();
      const fechaHoy = hoy.toISOString().split('T')[0];
      this.fechaEntregaClon = fechaHoy;
      this.showCloneModal = true;
    },

    cerrarModalClone() {
      this.showCloneModal = false;
      this.fechaEntregaClon = '';
    },

    async confirmarClonarAlbaran() {
      if (!this.albaran) {
        this.$notify({
          type: "warning",
          message: "No hay datos del albarán para clonar"
        });
        return;
      }

      // Validar fecha
      if (!this.fechaEntregaClon) {
        this.$notify({
          type: "warning",
          message: "Por favor, selecciona una fecha de entrega"
        });
        return;
      }

      try {
        this.loading = true;

        // Obtener todos los materiales del albarán
        const materiales = this.getMateriales || [];
        
        // Filtrar solo los materiales que NO están devueltos
        // Solo se clonan los materiales que aún no han sido devueltos
        const materialesNoDevueltos = materiales.filter(material => {
          const estado = material.estado || material.attributes?.estado;
          return estado !== 'devuelto';
        });
        
        // Extraer los IDs de los materiales a clonar
        const materialesIds = materialesNoDevueltos.map(material => {
          return material.id || material.attributes?.id;
        }).filter(id => id); // Filtrar IDs nulos o undefined

        console.log('📋 [CLON] Materiales a clonar:', {
          totalMateriales: materiales.length,
          materialesNoDevueltos: materialesNoDevueltos.length,
          materialesIds: materialesIds,
          fechaEntrega: this.fechaEntregaClon
        });

        // Preparar el payload para el endpoint de clonado
        const payload = {
          materialesIds: materialesIds,
          fecha_entrega: this.fechaEntregaClon
        };

        console.log('🔄 [CLON] Clonando albarán con payload:', payload);

        // Cerrar el modal antes de clonar
        this.cerrarModalClone();

        // Llamar al endpoint específico de clonado del backend
        const response = await axios.post(
          `${this.apiUrl}/albaran-clone/${this.albaran.id}`,
          payload,
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );

        console.log('✅ [CLON] Respuesta del clonado:', response.data);

        // Obtener el número del albarán clonado de la respuesta
        const albaranClonado = response.data.data || response.data;
        const numero = albaranClonado.numero || albaranClonado.attributes?.numero || 'N/A';

        this.$notify({
          type: "success",
          message: `Albarán ${numero} clonado correctamente con ${materialesIds.length} material(es)`
        });

        // Redirigir al detalle del albarán clonado
        const albaranClonadoId = albaranClonado.id || albaranClonado.attributes?.id || response.data.id;
        
        // Asociar automáticamente la tarea de Keikaku y el parte de trabajo
        try {
          await this.asociarTareaYParteAlClon(albaranClonadoId);
        } catch (error) {
          console.error('⚠️ [CLON] Error asociando tarea y parte al clon:', error);
          // No bloquear la redirección si falla la asociación
        }
        
        this.$router.push({
          name: "View Albaran",
          params: { id: albaranClonadoId }
        });

      } catch (error) {
        console.error("❌ [CLON] Error clonando albarán:", error);
        this.$notify({
          type: "danger",
          message: `Error al clonar el albarán: ${error.response?.data?.error?.message || error.message || "Error desconocido"}`
        });
      } finally {
        this.loading = false;
      }
    },
    
    async revertirClon() {
      if (!this.albaran) {
        this.$notify({
          type: "warning",
          message: "No hay datos del albarán para revertir"
        });
        return;
      }

      const esClon = this.getAlbaranAttributes.es_clon;
      if (!esClon) {
        this.$notify({
          type: "warning",
          message: "Este albarán no es un clon, no se puede revertir"
        });
        return;
      }

      // Confirmar la acción
      this.showConfirmDialog({
        title: "Revertir Clon de Albarán",
        message: `¿Está seguro de revertir este clon? Todos los materiales serán devueltos al albarán original y el estado del albarán original cambiará a "Asignado". El clon será eliminado.`,
        buttonText: "Revertir Clon",
        buttonType: "warning",
        action: async () => {
          try {
            this.loading = true;

            const albaranId = this.albaran.id || this.albaran.attributes?.id;
            
            console.log('🔄 [REVERTIR] Revertiendo clon de albarán:', albaranId);

            // Llamar al endpoint de reversión
            const response = await axios.post(
              `${this.apiUrl}/albaran-clone/${albaranId}/revert`,
              {},
              {
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
              }
            );

            console.log('✅ [REVERTIR] Respuesta de la reversión:', response.data);

            const resultado = response.data.data || response.data;
            // El ID del albarán original está en albaran_original_id del clon, o en resultado.albaranOriginal.id
            const albaranOriginalId = resultado.albaranOriginal?.id || 
                                      this.getAlbaranAttributes.albaran_original_id ||
                                      resultado.albaranOriginal?.attributes?.id;

            this.$notify({
              type: "success",
              message: resultado.message || `Clon revertido correctamente. ${resultado.materialesRevertidos || 0} material(es) devuelto(s) al albarán original.`
            });

            // Redirigir al albarán original
            if (albaranOriginalId) {
              this.$router.push({
                name: "View Albaran",
                params: { id: albaranOriginalId }
              });
            } else {
              // Si no hay ID del original, volver a la lista
              this.$router.push({ name: "List Albaran" });
            }

          } catch (error) {
            console.error("❌ [REVERTIR] Error revirtiendo clon:", error);
            this.$notify({
              type: "danger",
              message: `Error al revertir el clon: ${error.response?.data?.error?.message || error.message || "Error desconocido"}`
            });
          } finally {
            this.loading = false;
          }
        }
      });
    },
    
    async asociarTareaYParteAlClon(albaranClonadoId) {
      try {
        console.log('🔄 [CLON] ============================================');
        console.log('🔄 [CLON] Iniciando asociación de tarea y parte para albarán clonado');
        console.log('🔄 [CLON] Albarán clonado ID:', albaranClonadoId);
        console.log('🔄 [CLON] Albarán original:', {
          id: this.albaran?.id || this.albaran?.attributes?.id,
          numero: this.albaran?.numero || this.albaran?.attributes?.numero,
          tarea_keikaku_id: this.albaran?.tarea_keikaku_id || this.albaran?.attributes?.tarea_keikaku_id
        });
        
        // 1. Obtener el albarán clonado para tener su fecha_entrega
        console.log('📥 [CLON] Paso 1: Obteniendo albarán clonado...');
        const albaranClonadoResponse = await axios.get(
          `${this.apiUrl}/albaran-activos/${albaranClonadoId}?populate=*`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        console.log('📥 [CLON] Respuesta completa del albarán clonado:', albaranClonadoResponse.data);
        
        const albaranClonado = albaranClonadoResponse.data.data || albaranClonadoResponse.data;
        const fechaEntregaClon = albaranClonado.attributes?.fecha_entrega || albaranClonado.fecha_entrega;
        
        console.log('📥 [CLON] Albarán clonado parseado:', {
          id: albaranClonado.id || albaranClonado.attributes?.id,
          numero: albaranClonado.numero || albaranClonado.attributes?.numero,
          fecha_entrega: fechaEntregaClon,
          tieneAttributes: !!albaranClonado.attributes
        });
        
        if (!fechaEntregaClon) {
          console.error('❌ [CLON] El albarán clonado no tiene fecha_entrega, no se puede asociar tarea');
          console.log('❌ [CLON] Estructura completa del albarán clonado:', JSON.stringify(albaranClonado, null, 2));
          return;
        }
        
        // 2. Obtener IdProyecto del albarán original
        console.log('📥 [CLON] Paso 2: Obteniendo IdProyecto del albarán original...');
        console.log('📥 [CLON] Estructura del albarán original:', {
          tieneAlbaran: !!this.albaran,
          tieneAttributes: !!this.albaran?.attributes,
          tarea_keikaku_data: this.albaran?.tarea_keikaku_data ? 'existe' : 'no existe',
          tarea_keikaku_data_attributes: this.albaran?.attributes?.tarea_keikaku_data ? 'existe' : 'no existe'
        });
        
        const tareaKeikakuData = this.albaran.tarea_keikaku_data || this.albaran.attributes?.tarea_keikaku_data;
        
        if (!tareaKeikakuData) {
          console.error('❌ [CLON] El albarán original no tiene tarea_keikaku_data, no se puede obtener IdProyecto');
          console.log('❌ [CLON] Albarán original completo:', JSON.stringify(this.albaran, null, 2));
          return;
        }
        
        console.log('📥 [CLON] tarea_keikaku_data encontrado:', {
          tieneProperties: !!tareaKeikakuData.Properties,
          estructura: Object.keys(tareaKeikakuData),
          Properties_keys: tareaKeikakuData.Properties ? Object.keys(tareaKeikakuData.Properties) : 'no Properties'
        });
        
        // La estructura puede ser tareaKeikakuData.Properties.IdProyecto?.Value o directamente tareaKeikakuData.IdProyecto?.Value
        const idProyecto = tareaKeikakuData.Properties?.IdProyecto?.Value || 
                           tareaKeikakuData.IdProyecto?.Value ||
                           tareaKeikakuData.IdProyecto;
        
        console.log('📥 [CLON] Intentos de obtener IdProyecto:', {
          'Properties.IdProyecto.Value': tareaKeikakuData.Properties?.IdProyecto?.Value,
          'IdProyecto.Value': tareaKeikakuData.IdProyecto?.Value,
          'IdProyecto': tareaKeikakuData.IdProyecto,
          'idProyecto_final': idProyecto
        });
        
        if (!idProyecto) {
          console.error('❌ [CLON] No se encontró IdProyecto en tarea_keikaku_data');
          console.log('❌ [CLON] tarea_keikaku_data completo:', JSON.stringify(tareaKeikakuData, null, 2));
          return;
        }
        
        console.log('✅ [CLON] IdProyecto obtenido:', idProyecto);
        console.log('📊 [CLON] Datos para buscar tarea:', {
          IdProyecto: idProyecto,
          fechaEntregaClon: fechaEntregaClon,
          fechaEntregaClon_tipo: typeof fechaEntregaClon
        });
        
        // 3. Buscar la tarea en Keikaku usando IdProyecto y fecha_inicio
        console.log('📥 [CLON] Paso 3: Buscando tarea en Keikaku...');
        const axiosInstance = axios.create({
          headers: {
            'Accept': undefined,
            'Content-Type': undefined
          }
        });
        
        // Formatear la fecha para el filtro (formato YYYY-MM-DD)
        const fechaInicioFormato = new Date(fechaEntregaClon).toISOString().split('T')[0];
        console.log('📅 [CLON] Fechas formateadas:', {
          fechaEntregaClon_original: fechaEntregaClon,
          fechaInicioFormato: fechaInicioFormato,
          fecha_parseada: new Date(fechaEntregaClon)
        });
        
        // Buscar tareas con IdProyecto
        const keikakuQueryParams = new URLSearchParams({
          filter: `FLX_Pers_Tareas.IdProyecto='${idProyecto}'`,
          page: '0',
          pageSize: '500',
          orderBy: 'IdTarea desc',
          access_token: KEIKAKU_ACCESS_TOKEN
        }).toString();
        
        const keikakuConfig = {
          method: 'get',
          maxBodyLength: Infinity,
          url: `${KEIKAKU_API_BASE_URL}/list/Planificador_Equipo_Movil?${keikakuQueryParams}`,
          headers: {}
        };
        
        console.log('🔍 [CLON] URL de búsqueda en Keikaku:', keikakuConfig.url);
        console.log('🔍 [CLON] Query params:', keikakuQueryParams);
        console.log('🔍 [CLON] KEIKAKU_API_BASE_URL:', KEIKAKU_API_BASE_URL);
        console.log('🔍 [CLON] KEIKAKU_ACCESS_TOKEN existe:', !!KEIKAKU_ACCESS_TOKEN);
        
        const keikakuResponse = await axiosInstance.request(keikakuConfig);
        
        console.log('📥 [CLON] Respuesta de Keikaku:', {
          tieneData: !!keikakuResponse.data,
          esArray: Array.isArray(keikakuResponse.data),
          longitud: keikakuResponse.data?.length || 0,
          primerosElementos: keikakuResponse.data?.slice(0, 2) || 'no data'
        });
        
        // La respuesta de Keikaku es un array directo
        const tareasKeikaku = Array.isArray(keikakuResponse.data) ? keikakuResponse.data : [];
        
        if (tareasKeikaku.length === 0) {
          console.error('❌ [CLON] No se encontraron tareas para IdProyecto:', idProyecto);
          console.log('❌ [CLON] Respuesta completa de Keikaku:', JSON.stringify(keikakuResponse.data, null, 2));
          return;
        }
        
        console.log('✅ [CLON] Tareas encontradas:', tareasKeikaku.length);
        console.log('📋 [CLON] Primeras 3 tareas:', tareasKeikaku.slice(0, 3).map(t => ({
          IdTarea: t.IdTarea,
          FechaInicio: t.FechaInicio,
          Titulo: t.Titulo,
          IdParte: t.IdParte
        })));
        
        // Filtrar por fecha_inicio (debe ser el mismo día que fecha_entrega del clon)
        console.log('🔍 [CLON] Filtrando tareas por fecha_inicio...');
        console.log('🔍 [CLON] Fecha objetivo (fecha_entrega del clon):', fechaEntregaClon);
        console.log('🔍 [CLON] Fecha objetivo formateada:', fechaInicioFormato);
        
        const tareasFiltradas = tareasKeikaku.filter(tarea => {
          const fechaInicioTarea = tarea.FechaInicio;
          if (!fechaInicioTarea) {
            console.log('⚠️ [CLON] Tarea sin FechaInicio:', tarea.IdTarea);
            return false;
          }
          
          // La fecha viene en formato ISO con timezone: "2025-11-28T01:00:00+01:00"
          // Necesitamos extraer solo la parte de la fecha (YYYY-MM-DD)
          // Usar moment o Date para parsear correctamente
          const fechaTarea = new Date(fechaInicioTarea);
          const fechaTareaFormato = fechaTarea.toISOString().split('T')[0];
          
          // También comparar la fecha original del albarán clonado
          const fechaClon = new Date(fechaEntregaClon);
          const fechaClonFormato = fechaClon.toISOString().split('T')[0];
          
          const coincide = fechaTareaFormato === fechaClonFormato;
          
          if (coincide) {
            console.log('✅ [CLON] Tarea coincide con fecha:', {
              IdTarea: tarea.IdTarea,
              FechaInicio_original: fechaInicioTarea,
              fechaTareaFormato: fechaTareaFormato,
              fechaClonFormato: fechaClonFormato,
              coincide: coincide
            });
          }
          
          return coincide;
        });
        
        console.log('📊 [CLON] Resultado del filtrado:', {
          totalTareas: tareasKeikaku.length,
          tareasFiltradas: tareasFiltradas.length,
          tareasFiltradas_ids: tareasFiltradas.map(t => t.IdTarea)
        });
        
        if (tareasFiltradas.length === 0) {
          console.error('❌ [CLON] No se encontró tarea con fecha_inicio igual a fecha_entrega del clon');
          console.log('❌ [CLON] Fechas de todas las tareas:', tareasKeikaku.map(t => {
            const fechaTarea = t.FechaInicio ? new Date(t.FechaInicio).toISOString().split('T')[0] : 'sin fecha';
            return {
              IdTarea: t.IdTarea,
              FechaInicio: t.FechaInicio,
              FechaInicio_formateada: fechaTarea,
              coincide: fechaTarea === fechaInicioFormato
            };
          }));
          return;
        }
        
        // Tomar la primera tarea encontrada (o la más reciente si hay varias)
        const tareaEncontrada = tareasFiltradas[0];
        const idTarea = tareaEncontrada.IdTarea;
        
        console.log('✅ [CLON] Tarea encontrada:', {
          IdTarea: idTarea,
          FechaInicio: tareaEncontrada.FechaInicio,
          Titulo: tareaEncontrada.Titulo,
          IdParte: tareaEncontrada.IdParte,
          tareaCompleta: tareaEncontrada
        });
        
        // 4. Obtener los detalles completos de la tarea
        console.log('📥 [CLON] Paso 4: Obteniendo detalles completos de la tarea...');
        const tareaDetalleConfig = {
          method: 'get',
          maxBodyLength: Infinity,
          url: `${KEIKAKU_API_BASE_URL}/object/Planificador_Equipo_Movil/${idTarea}?access_token=${KEIKAKU_ACCESS_TOKEN}`,
          headers: {}
        };
        
        console.log('🔍 [CLON] URL para obtener detalles:', tareaDetalleConfig.url);
        const tareaDetalleResponse = await axiosInstance.request(tareaDetalleConfig);
        
        console.log('📥 [CLON] Respuesta de detalles de tarea:', {
          tieneData: !!tareaDetalleResponse.data,
          tieneProperties: !!tareaDetalleResponse.data?.Properties,
          estructura: Object.keys(tareaDetalleResponse.data || {})
        });
        
        const tareaData = tareaDetalleResponse.data.Properties;
        
        console.log('📊 [CLON] Datos completos de la tarea obtenidos:', {
          IdTarea: tareaData.IdTarea?.Value,
          Titulo: tareaData.Titulo?.Value,
          FechaInicio: tareaData.FechaInicio?.Value,
          IdParte: tareaData.IdParte?.Value,
          Properties_keys: Object.keys(tareaData || {})
        });
        
        // 5. Obtener el parte de trabajo si existe IdParte
        console.log('📥 [CLON] Paso 5: Obteniendo parte de trabajo...');
        let parteTrabajo = null;
        let esFacturable = false;
        // El IdParte puede venir de la tarea encontrada directamente o de los detalles
        const idParte = tareaEncontrada.IdParte || tareaData.IdParte?.Value;
        
        console.log('📥 [CLON] IdParte de la tarea:', idParte);
        
        if (idParte) {
          const parteQueryParams = new URLSearchParams({
            filter: `Partes.IdParte=${idParte}`,
            page: '0',
            pageSize: '5000',
            orderBy: 'IdParte desc',
            access_token: SAT_ACCESS_TOKEN
          }).toString();
          
          const parteConfig = {
            method: 'get',
            maxBodyLength: Infinity,
            url: `${SAT_API_BASE_URL}/list/sat_Parte?${parteQueryParams}`,
            headers: {}
          };
          
          console.log('🔍 [CLON] URL para obtener parte:', parteConfig.url);
          console.log('🔍 [CLON] SAT_API_BASE_URL:', SAT_API_BASE_URL);
          console.log('🔍 [CLON] SAT_ACCESS_TOKEN existe:', !!SAT_ACCESS_TOKEN);
          
          const parteResponse = await axiosInstance.request(parteConfig);
          
          console.log('📥 [CLON] Respuesta de SAT:', {
            tieneData: !!parteResponse.data,
            esArray: Array.isArray(parteResponse.data),
            longitud: parteResponse.data?.length || 0
          });
          
          if (parteResponse.data && parteResponse.data.length > 0) {
            parteTrabajo = parteResponse.data[0];
            esFacturable = parteTrabajo.p_TareaFacturacion === 4 || parteTrabajo.p_TareaFacturacion === 5;
            console.log('✅ [CLON] Parte de trabajo obtenida:', {
              IdParte: parteTrabajo.IdParte,
              p_TareaFacturacion: parteTrabajo.p_TareaFacturacion,
              esFacturable: esFacturable
            });
          } else {
            console.log('⚠️ [CLON] No se encontró parte de trabajo para IdParte:', idParte);
          }
        } else {
          console.log('⚠️ [CLON] La tarea no tiene IdParte asociado');
        }
        
        // 6. Actualizar el albarán clonado con tarea_keikaku_id, tarea_keikaku_data y parte_trabajo
        console.log('📥 [CLON] Paso 6: Actualizando albarán clonado...');
        const updatePayload = {
          data: {
            tarea_keikaku_id: idTarea,
            tarea_keikaku_data: tareaData
          }
        };
        
        if (parteTrabajo) {
          updatePayload.data.parte_trabajo = parteTrabajo;
          updatePayload.data.es_facturable = esFacturable;
        }
        
        console.log('📤 [CLON] Payload de actualización:', {
          tarea_keikaku_id: updatePayload.data.tarea_keikaku_id,
          tiene_tarea_keikaku_data: !!updatePayload.data.tarea_keikaku_data,
          tiene_parte_trabajo: !!updatePayload.data.parte_trabajo,
          es_facturable: updatePayload.data.es_facturable,
          payload_completo: updatePayload
        });
        
        console.log('🔍 [CLON] URL de actualización:', `${this.apiUrl}/albaran-activos/${albaranClonadoId}`);
        
        const updateResponse = await axios.put(
          `${this.apiUrl}/albaran-activos/${albaranClonadoId}`,
          updatePayload,
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        console.log('📥 [CLON] Respuesta de actualización:', {
          status: updateResponse.status,
          tieneData: !!updateResponse.data,
          data: updateResponse.data
        });
        
        console.log('✅ [CLON] Albarán clonado actualizado con tarea y parte:', {
          tarea_keikaku_id: idTarea,
          tiene_parte_trabajo: !!parteTrabajo,
          es_facturable: esFacturable
        });
        
        console.log('🔄 [CLON] ============================================');
        
        this.$notify({
          type: "success",
          message: `Tarea de Keikaku y parte de trabajo asociados automáticamente al albarán clonado`
        });
        
      } catch (error) {
        console.error('❌ [CLON] ============================================');
        console.error('❌ [CLON] Error asociando tarea y parte:', error);
        console.error('❌ [CLON] Error message:', error.message);
        console.error('❌ [CLON] Error response:', error.response?.data);
        console.error('❌ [CLON] Error stack:', error.stack);
        console.error('❌ [CLON] ============================================');
        // No mostrar error al usuario, solo log
      }
    },

    async asociarTareaYParteAlClonDesdeOriginal(albaranClonId, albaranOriginalId) {
      try {
        console.log('🔄 [ENLAZAR] ============================================');
        console.log('🔄 [ENLAZAR] Iniciando asociación automática de tarea y parte');
        console.log('🔄 [ENLAZAR] Albarán clon ID:', albaranClonId);
        console.log('🔄 [ENLAZAR] Albarán original ID:', albaranOriginalId);
        console.log('🔄 [ENLAZAR] Tipos de datos:', {
          albaranClonId_tipo: typeof albaranClonId,
          albaranOriginalId_tipo: typeof albaranOriginalId,
          albaranClonId_valor: albaranClonId,
          albaranOriginalId_valor: albaranOriginalId
        });
        
        // 1. Obtener el albarán clon para tener su fecha_entrega
        console.log('📥 [ENLAZAR] Paso 1: Obteniendo albarán clon...');
        const albaranClonResponse = await axios.get(
          `${this.apiUrl}/albaran-activos/${albaranClonId}?populate=*`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        const albaranClon = albaranClonResponse.data.data || albaranClonResponse.data;
        const fechaEntregaClon = albaranClon.attributes?.fecha_entrega || albaranClon.fecha_entrega;
        
        console.log('📥 [ENLAZAR] Albarán clon parseado:', {
          id: albaranClon.id || albaranClon.attributes?.id,
          numero: albaranClon.numero || albaranClon.attributes?.numero,
          fecha_entrega: fechaEntregaClon
        });
        
        if (!fechaEntregaClon) {
          throw new Error('El albarán clon no tiene fecha_entrega, no se puede asociar tarea');
        }
        
        // 2. Obtener el albarán original para obtener IdProyecto
        console.log('📥 [ENLAZAR] Paso 2: Obteniendo albarán original...');
        const albaranOriginalResponse = await axios.get(
          `${this.apiUrl}/albaran-activos/${albaranOriginalId}?populate=*`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        const albaranOriginal = albaranOriginalResponse.data.data || albaranOriginalResponse.data;
        const tareaKeikakuData = albaranOriginal.attributes?.tarea_keikaku_data || albaranOriginal.tarea_keikaku_data;
        const parteTrabajoOriginal = albaranOriginal.attributes?.parte_trabajo || albaranOriginal.parte_trabajo;
        
        console.log('📥 [ENLAZAR] Albarán original parseado:', {
          id: albaranOriginal.id || albaranOriginal.attributes?.id,
          numero: albaranOriginal.numero || albaranOriginal.attributes?.numero,
          tiene_tarea_keikaku_data: !!tareaKeikakuData,
          tiene_parte_trabajo: !!parteTrabajoOriginal
        });
        
        if (!tareaKeikakuData) {
          throw new Error('El albarán original no tiene tarea_keikaku_data, no se puede obtener IdProyecto');
        }
        
        // Obtener IdProyecto
        const idProyecto = tareaKeikakuData.Properties?.IdProyecto?.Value || 
                           tareaKeikakuData.IdProyecto?.Value ||
                           tareaKeikakuData.IdProyecto;
        
        if (!idProyecto) {
          throw new Error('No se encontró IdProyecto en tarea_keikaku_data del albarán original');
        }
        
        // Intentar obtener el nombre del proyecto
        // Primero desde parte_trabajo (más confiable)
        let nombreProyecto = null;
        if (parteTrabajoOriginal) {
          nombreProyecto = parteTrabajoOriginal.IdProyecto_flxtext || parteTrabajoOriginal.Proyecto || null;
        }
        
        // Si no está en parte_trabajo, intentar desde tarea_keikaku_data
        if (!nombreProyecto && tareaKeikakuData) {
          nombreProyecto = tareaKeikakuData.Properties?.IdProyecto_flxtext?.Value ||
                          tareaKeikakuData.IdProyecto_flxtext?.Value ||
                          tareaKeikakuData.IdProyecto_flxtext ||
                          tareaKeikakuData.Properties?.Proyecto?.Value ||
                          tareaKeikakuData.Proyecto?.Value ||
                          tareaKeikakuData.Proyecto ||
                          null;
        }
        
        console.log('✅ [ENLAZAR] IdProyecto obtenido:', idProyecto);
        console.log('✅ [ENLAZAR] Nombre del proyecto:', nombreProyecto || 'No disponible');
        
        // 3. Buscar la tarea en Keikaku usando IdProyecto y fecha_inicio
        console.log('📥 [ENLAZAR] Paso 3: Buscando tarea en Keikaku...');
        const axiosInstance = axios.create({
          headers: {
            'Accept': undefined,
            'Content-Type': undefined
          }
        });
        
        const fechaInicioFormato = new Date(fechaEntregaClon).toISOString().split('T')[0];
        
        const keikakuQueryParams = new URLSearchParams({
          filter: `FLX_Pers_Tareas.IdProyecto='${idProyecto}'`,
          page: '0',
          pageSize: '500',
          orderBy: 'IdTarea desc',
          access_token: KEIKAKU_ACCESS_TOKEN
        }).toString();
        
        const keikakuConfig = {
          method: 'get',
          maxBodyLength: Infinity,
          url: `${KEIKAKU_API_BASE_URL}/list/Planificador_Equipo_Movil?${keikakuQueryParams}`,
          headers: {}
        };
        
        const keikakuResponse = await axiosInstance.request(keikakuConfig);
        const tareasKeikaku = Array.isArray(keikakuResponse.data) ? keikakuResponse.data : [];
        
        if (tareasKeikaku.length === 0) {
          const error = new Error(`NO_TAREAS_PROYECTO`);
          error.idProyecto = idProyecto;
          error.nombreProyecto = nombreProyecto;
          error.fechaEntrega = fechaInicioFormato;
          throw error;
        }
        
        // Filtrar por fecha_inicio
        const tareasFiltradas = tareasKeikaku.filter(tarea => {
          const fechaInicioTarea = tarea.FechaInicio;
          if (!fechaInicioTarea) return false;
          
          const fechaTarea = new Date(fechaInicioTarea);
          const fechaTareaFormato = fechaTarea.toISOString().split('T')[0];
          const fechaClon = new Date(fechaEntregaClon);
          const fechaClonFormato = fechaClon.toISOString().split('T')[0];
          
          return fechaTareaFormato === fechaClonFormato;
        });
        
        if (tareasFiltradas.length === 0) {
          const error = new Error(`NO_TAREA_FECHA`);
          error.idProyecto = idProyecto;
          error.nombreProyecto = nombreProyecto;
          error.fechaEntrega = fechaInicioFormato;
          error.tareasEncontradas = tareasKeikaku.length;
          throw error;
        }
        
        // Tomar la primera tarea encontrada
        const tareaEncontrada = tareasFiltradas[0];
        const idTarea = tareaEncontrada.IdTarea;
        
        console.log('✅ [ENLAZAR] Tarea encontrada:', {
          IdTarea: idTarea,
          FechaInicio: tareaEncontrada.FechaInicio,
          Titulo: tareaEncontrada.Titulo
        });
        
        // 4. Obtener los detalles completos de la tarea
        console.log('📥 [ENLAZAR] Paso 4: Obteniendo detalles completos de la tarea...');
        const tareaDetalleConfig = {
          method: 'get',
          maxBodyLength: Infinity,
          url: `${KEIKAKU_API_BASE_URL}/object/Planificador_Equipo_Movil/${idTarea}?access_token=${KEIKAKU_ACCESS_TOKEN}`,
          headers: {}
        };
        
        const tareaDetalleResponse = await axiosInstance.request(tareaDetalleConfig);
        const tareaData = tareaDetalleResponse.data.Properties;
        
        // 5. Obtener el parte de trabajo si existe IdParte
        console.log('📥 [ENLAZAR] Paso 5: Obteniendo parte de trabajo...');
        let parteTrabajo = null;
        let esFacturable = false;
        const idParte = tareaEncontrada.IdParte || tareaData.IdParte?.Value;
        
        if (idParte) {
          const parteQueryParams = new URLSearchParams({
            filter: `Partes.IdParte=${idParte}`,
            page: '0',
            pageSize: '5000',
            orderBy: 'IdParte desc',
            access_token: SAT_ACCESS_TOKEN
          }).toString();
          
          const parteConfig = {
            method: 'get',
            maxBodyLength: Infinity,
            url: `${SAT_API_BASE_URL}/list/sat_Parte?${parteQueryParams}`,
            headers: {}
          };
          
          const parteResponse = await axiosInstance.request(parteConfig);
          
          if (parteResponse.data && parteResponse.data.length > 0) {
            parteTrabajo = parteResponse.data[0];
            esFacturable = parteTrabajo.p_TareaFacturacion === 4 || parteTrabajo.p_TareaFacturacion === 5;
            console.log('✅ [ENLAZAR] Parte de trabajo obtenida:', {
              IdParte: parteTrabajo.IdParte,
              esFacturable: esFacturable
            });
          }
        }
        
        // 6. Actualizar el albarán clon con tarea_keikaku_id, tarea_keikaku_data y parte_trabajo
        console.log('📥 [ENLAZAR] Paso 6: Actualizando albarán clon...');
        const updatePayload = {
          data: {
            tarea_keikaku_id: idTarea,
            tarea_keikaku_data: tareaData
          }
        };
        
        if (parteTrabajo) {
          updatePayload.data.parte_trabajo = parteTrabajo;
          updatePayload.data.es_facturable = esFacturable;
        }
        
        await axios.put(
          `${this.apiUrl}/albaran-activos/${albaranClonId}`,
          updatePayload,
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        console.log('✅ [ENLAZAR] Albarán clon actualizado con tarea y parte');
        console.log('🔄 [ENLAZAR] ============================================');
        
      } catch (error) {
        console.error('❌ [ENLAZAR] ============================================');
        console.error('❌ [ENLAZAR] Error asociando tarea y parte:', error);
        console.error('❌ [ENLAZAR] Error message:', error.message);
        console.error('❌ [ENLAZAR] Error response:', error.response?.data);
        console.error('❌ [ENLAZAR] ============================================');
        throw error; // Re-lanzar el error para que se maneje en enlazarTareaKeikaku
      }
    },

    async generateNextAlbaranNumber() {
      try {
        // Obtener el último albarán para determinar el siguiente número
        const response = await axios.get(
          `${this.apiUrl}/albaran-activos?sort=numero:desc&pagination[pageSize]=1`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );

        let nextNumber = 1;
        
        if (response.data.data && response.data.data.length > 0) {
          const lastAlbaran = response.data.data[0];
          const lastNumber = lastAlbaran.attributes.numero;
          
          // Extraer el número del formato ST-XXXX
          const match = lastNumber.match(/ST-(\d+)/);
          if (match) {
            nextNumber = parseInt(match[1]) + 1;
          }
        }

        // Formatear como ST-XXXX (4 dígitos con ceros a la izquierda)
        return `ST-${nextNumber.toString().padStart(4, '0')}`;
      } catch (error) {
        console.error("Error generando número de albarán:", error);
        // Fallback: usar timestamp si hay error
        return `ST-${moment().format('YYMMDDHHmmss')}`;
      }
    },
    openEditAlbaranModal() {
      // Inicializar el formulario con los datos actuales
      this.editAlbaran = {
        titulo: this.albaran.titulo || '',
        estado: this.albaran.estado || '',
        fecha_entrega: this.formatDateForInput(this.albaran.fecha_entrega) || '',
        fecha_devolucion: this.formatDateForInput(this.albaran.fecha_devolucion) || '',
        descripcion: this.albaran.descripcion || '',
        observaciones: this.albaran.observaciones || ''
      };
      this.showEditAlbaranModal = true;
    },
    async handleQRScan() {
      if (!this.qrInput.trim()) return;
      if (!this.assertAlbaranEditable()) {
        this.avisarAsignacionFallida();
        return;
      }
      let asignado = false;
      try {
        this.error = "";

        // Determinar si es una URL o un código QR directo
        let qrCode = this.qrInput.trim();
        
        // Si es una URL, extraer el código
        if (qrCode.startsWith('http') && qrCode.includes('misepis.com/code/')) {
          console.log('Procesando URL misepis.com:', qrCode);
          qrCode = qrCode.split('code/').pop().split('/')[0];
          console.log('Código extraído de URL:', qrCode);
          // Actualizar el input con solo el código QR extraído
          this.qrInput = qrCode;
        } else if (qrCode.includes('ssea.es/')) {
          console.log('Procesando URL ssea.es:', qrCode);
          qrCode = qrCode.split('ssea.es/').pop().split('/')[0];
          console.log('Código extraído de URL:', qrCode);
          // Actualizar el input con solo el código QR extraído
          this.qrInput = qrCode;
        } else if ((qrCode.startsWith('http://') || qrCode.startsWith('https://')) && qrCode.split('/').length > 3) {
          // Manejar otras URLs (con http/https pero que no sean misepis.com/code/)
          const lastSegment = qrCode.split('/').pop();
          if (lastSegment) {
            qrCode = lastSegment;
            this.qrInput = qrCode;
          }
        } else {
          // Código QR directo - usar tal cual sin modificar
          console.log('Usando código QR directo:', qrCode);
        }

        // Primero, verificar si es un activo ya asignado
        const materialYaAsignado = this.getMateriales.find(material => 
          material.activo && 
          (material.activo.numero_serie_qr === qrCode || 
           material.activo.numero_serie === qrCode)
        );

        if (materialYaAsignado) {
          // Si el material está en estado "pendiente_aceptar_devolucion", actualizarlo automáticamente a "devuelto"
          if (materialYaAsignado.estado === 'pendiente_aceptar_devolucion') {
            try {
              await axios.put(
                `${this.apiUrl}/albaran-activo-materials/${materialYaAsignado.id}`,
                {
                  data: {
                    estado: "devuelto",
                    responsable_actual: "jefe_taller",
                    fecha_accion: new Date().toISOString(),
                    usuario_accion: localStorage.getItem('userId')
                  }
                },
                {
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                  }
                }
              );

              await this.loadAlbaran();
              this.qrInput = "";
              asignado = true;
              this.$notify({
                type: "success",
                message: "Material actualizado automáticamente a estado 'devuelto'"
              });
              return;
            } catch (error) {
              console.error("Error al actualizar el estado del material:", error);
              this.error = "Error al actualizar el estado del material";
              this.$notify({
                type: "danger",
                message: "Error al actualizar el estado del material: " + (error.message || "Error desconocido")
              });
              this.qrInput = "";
              return;
            }
          } else {
            // Si no está en pendiente_aceptar_devolucion, mostrar el error habitual
            this.error = "Este activo ya está asignado al albarán";
            this.$notify({
              type: "warning",
              message: "Este activo ya está asignado al albarán"
            });
            this.qrInput = "";
            return;
          }
        }

        // Verificar si es una Saca antes de intentar añadir como activo
        // Buscar activos por QR/ubicación para detectar Sacas
        let activosDetectados = [];
        try {
          const params = new URLSearchParams();
          params.append('search', qrCode);
          params.append('idEmpresa', localStorage.getItem("plantaSelected") || '172');
          params.append('sortOrder', 'asc');
          params.append('includeRetired', 'false');
          
          const activosResponse = await axios.get(
            `${this.apiUrl}/activos-list?${params.toString()}`,
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              }
            }
          );

          // Si se encontraron activos, verificar si forman una Saca
          if (activosResponse.data && activosResponse.data.activos && activosResponse.data.activos.length > 0) {
            const activos = activosResponse.data.activos;
            activosDetectados = activos;
            const esSaca = this.detectarSaca(activos, qrCode);
            
            if (esSaca) {
              // Es una Saca, asignarla directamente
              console.log('✅ [SACA] Saca detectada en handleQRScan, asignando...');
              const sacaAsignada = await this.asignarSacaAlAlbaran(qrCode, false);
              this.qrInput = "";
              if (sacaAsignada) {
                asignado = true;
                this.$notify({
                  type: "success",
                  message: "Saca asignada correctamente al albarán"
                });
                await this.loadAlbaran();
              }
              return;
            }
          }
        } catch (sacaError) {
          // Si hay error al buscar activos para detectar Saca, continuar con el flujo normal
          console.log('No se pudo verificar si es Saca, continuando con flujo normal:', sacaError);
        }

        // Intentar añadir como activo primero
        try {
          const activosExactos = activosDetectados.filter(activo => {
            const qrActivo = (activo.numero_serie_qr || "").trim().toLowerCase();
            return qrActivo === qrCode.trim().toLowerCase();
          });
          if (activosExactos.length === 1) {
            const advertencias = this.getAdvertenciasAsignacionActivo(activosExactos[0]);
            if (advertencias.length > 0) {
              this.reproducirTonoNoAsignado();
              const result = await swal.fire({
                title: "Advertencia de asignación",
                html: `
                  <div style="text-align:left;">
                    <p>Este activo presenta incidencias y no debería asignarse en condiciones normales:</p>
                    <ul style="margin:0; padding-left: 20px;">
                      ${advertencias.map(msg => `<li>${msg}</li>`).join("")}
                    </ul>
                    <p style="margin-top:10px;"><strong>Si continúas, quedará bajo tu responsabilidad.</strong></p>
                  </div>
                `,
                icon: "warning",
                target: document.body,
                customClass: {
                  container: "swal2-container-albaran",
                },
                showCancelButton: true,
                confirmButtonText: "Asignar igualmente",
                cancelButtonText: "Cancelar",
                confirmButtonClass: "btn btn-warning",
                cancelButtonClass: "btn btn-secondary",
              });

              if (!result.isConfirmed) {
                this.qrInput = "";
                return;
              }
            }
          }

          const response = await axios.post(
            `${this.apiUrl}/albaran-add/${this.albaran.id}`,
            { 
              qr: qrCode,
              tipo_material: 'activo'
            },
            {
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              }
            }
          );

          // Si llegamos aquí, el activo se añadió correctamente
          this.qrInput = "";
          asignado = true;
          await this.loadAlbaran();
          this.$notify({
            type: "success",
            message: "El equipo se ha añadido correctamente"
          });

        } catch (activoError) {
          console.log('No se encontró como activo, buscando en consumibles del ERP...');
          
          // Si falla como activo, buscar en consumibles del ERP
          try {
            // Cargar consumibles si no están cargados
            if (this.consumiblesERP.length === 0) {
              this.consumiblesERP = await erpService.getConsumibles();
            }

            // Buscar el consumible por IdArticulo
            const consumibleEncontrado = this.consumiblesERP.find(consumible => 
              consumible.idArticulo && consumible.idArticulo.toLowerCase() === qrCode.toLowerCase()
            );

            if (consumibleEncontrado) {
              // Verificar si el consumible ya está asignado
              // EXCLUIR sacas: las sacas no son consumibles
              const consumibleYaAsignado = this.getMateriales.find(material => 
                !material.activo && 
                !(material.es_saca || material.tipo_material === 'saca') &&
                material.idArticulo === consumibleEncontrado.idArticulo
              );

              if (consumibleYaAsignado) {
                // Si el consumible está en estado "pendiente_aceptar_devolucion", actualizarlo automáticamente a "devuelto"
                if (consumibleYaAsignado.estado === 'pendiente_aceptar_devolucion') {
                  try {
                    await axios.put(
                      `${this.apiUrl}/albaran-activo-materials/${consumibleYaAsignado.id}`,
                      {
                        data: {
                          estado: "devuelto",
                          responsable_actual: "jefe_taller",
                          fecha_accion: new Date().toISOString(),
                          usuario_accion: localStorage.getItem('userId')
                        }
                      },
                      {
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${localStorage.getItem('token')}`
                        }
                      }
                    );

                    await this.loadAlbaran();
                    this.qrInput = "";
                    asignado = true;
                    this.$notify({
                      type: "success",
                      message: "Consumible actualizado automáticamente a estado 'devuelto'"
                    });
                    return;
                  } catch (error) {
                    console.error("Error al actualizar el estado del consumible:", error);
                    this.error = "Error al actualizar el estado del consumible";
                    this.$notify({
                      type: "danger",
                      message: "Error al actualizar el estado del consumible: " + (error.message || "Error desconocido")
                    });
                    this.qrInput = "";
                    return;
                  }
                } else {
                  // Si ya está asignado, sumar cantidad en la misma línea
                  const cantidadActual = parseInt(consumibleYaAsignado.cantidad, 10) || 0;
                  const nuevaCantidadTotal = cantidadActual + 1;
                  await axios.put(
                    `${this.apiUrl}/albaran-activo-materials/${consumibleYaAsignado.id}`,
                    {
                      data: {
                        cantidad: nuevaCantidadTotal,
                        descripcion: consumibleYaAsignado.descripcion || consumibleEncontrado.descripcion
                      }
                    },
                    {
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                      }
                    }
                  );

                  await this.loadAlbaran();
                  this.qrInput = "";
                  asignado = true;
                  this.$notify({
                    type: "success",
                    message: `Consumible actualizado: ${cantidadActual} + 1 = ${nuevaCantidadTotal}`
                  });
                  return;
                }
              }

              // Añadir el consumible con cantidad por defecto 1
              const payload = {
                tipo_material: 'consumible',
                idArticulo: consumibleEncontrado.idArticulo,
                descripcion: consumibleEncontrado.descripcion,
                cantidad: 1,
                material_keikaku_id: consumibleEncontrado.idArticulo
              };

              await axios.post(
                `${this.apiUrl}/albaran-add/${this.albaran.id}`,
                payload,
                {
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                  }
                }
              );

              // Éxito añadiendo consumible
              this.qrInput = "";
              asignado = true;
              await this.loadAlbaran();
              this.$notify({
                type: "success",
                message: `Consumible "${consumibleEncontrado.descripcion}" añadido correctamente`
              });

            } else {
              // No se encontró ni como activo ni como consumible
              this.error = "No se encontró ningún activo o consumible con ese código";
              this.$notify({
                type: "warning",
                message: this.error
              });
            }

          } catch (consumibleError) {
            console.error("Error buscando consumible:", consumibleError);
            this.error = "Error al buscar el consumible en el ERP";
            this.$notify({
              type: "danger",
              message: this.error
            });
          }
        }

      } catch (error) {
        console.error("Error general en handleQRScan:", error);
        this.error = error.response?.data?.error?.message || "Error al procesar el código";
        this.$notify({
          type: "danger",
          message: this.error
        });
      } finally {
        if (!asignado) this.avisarAsignacionFallida();
      }
    },
    getColorByYear(fechaRevision) {
      if (!fechaRevision) return "#ccc";

      const year = new Date(fechaRevision).getFullYear();
      const lastDigit = year % 10;

      const colorMapByYear = {
        1: "#F1C40F", // Amarillo
        2: "#2ECC71", // Verde
        3: "#E67E22", // Naranja
        4: "#3498DB", // Azul
        5: "#E74C3C", // Rojo
        6: "#F1C40F", // Amarillo
        7: "#2ECC71", // Verde
        8: "#E67E22", // Naranja
        9: "#3498DB", // Azul
        0: "#E74C3C", // Rojo
      };

      return colorMapByYear[lastDigit] || "#ccc";
    },
    viewTipo(row) {
      const tipoActivo = row.modelo?.division?.nombre;
      
      if (tipoActivo === "DISPOSITIVOS DE ANCLAJE") {
        this.$router.push({
          name: "View Anclaje",
          params: { id: row.id }
        });
      } else {
        this.$router.push({
          name: "View Activo",
          params: { id: row.id }
        });
      }
    },
    async desAssignOnlyOnePost(row) {
      try {
        this.loading = true;
        
        // Usar la API directa en lugar del store
        await axios.put(
          `${this.apiUrl}/activos/${row.id}`,
          {
            usuario: null
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        this.$notify({
          type: "success",
          message: "El equipo se ha devuelto correctamente"
        });
        
        await this.loadAlbaran();
      } catch (error) {
        console.error("Error al devolver el equipo:", error);
        this.$notify({
          type: "danger",
          message: "Se ha producido un error al devolver el equipo: " + (error.response?.data?.error?.message || error.message)
        });
      } finally {
        this.loading = false;
      }
    },
    async fetchEmpleados() {
      try {
        this.loadingUsers = true;
        const response = await UsersService.fetchEmpleados({
          idEmpresa: 172
        });
        console.log('Jefes de equipo response:', response);
        this.allUsers = Array.isArray(response.empleados) ? response.empleados : response.data || [];
        this.availableUsers = this.allUsers;
        console.log('Jefes de equipo disponibles:', this.availableUsers);
      } catch (error) {
        console.error("Error cargando jefes de equipo:", error);
        this.$notify({
          type: "danger",
          message: "Error al cargar la lista de jefes de equipo"
        });
      } finally {
        this.loadingUsers = false;
      }
    },
    searchUsers(query = '') {
      if (!query) {
        this.availableUsers = this.allUsers;
        return;
      }

      // Procesar el query para extraer identificador si es una URL
      const processedQuery = this.extractIdentifierFromUrl(query);
      const searchTerm = processedQuery.toLowerCase();
      
      this.availableUsers = this.allUsers.filter(user => {
        const fullName = `${user.nombre} ${user.apellidos}`.toLowerCase();
        const email = (user.email || '').toLowerCase();
        return fullName.includes(searchTerm) || email.includes(searchTerm);
      });
    },
    async assignUser() {
      if (!this.selectedUserId) return;

      try {
        this.assigningUser = true;
        
        // Usar el endpoint correcto para asignar usuario
        await axios.put(
          `${this.apiUrl}/albaran-activo-usuario/${this.albaran.id}/asignar-jefe-equipo`,
          {
            userId: this.selectedUserId
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );

        // Recargar los datos del albarán
        await this.loadAlbaran();

        this.$notify({
          type: "success",
          message: "El jefe de equipo se ha asignado correctamente"
        });

        this.showUserAssignModal = false;
        this.selectedUserId = null;

      } catch (error) {
        console.error("Error asignando usuario:", error);
        this.$notify({
          type: "danger",
          message: "Error al asignar el jefe de equipo: " + (error.response?.data?.error?.message || error.message)
        });
      } finally {
        this.assigningUser = false;
      }
    },
    openUserAssignModal() {
      console.log('Opening modal...');
      this.showUserAssignModal = true;
  },
    isConsumible(row) {
      // Verificar si es consumible por idArticulo o por descripción (idArticulo_flxtext)
      const isConsumibleById = row.idArticulo && row.idArticulo.includes('CONS');
      const isConsumibleByDesc = row.idArticulo_flxtext && row.idArticulo_flxtext.includes('CONS');
      return isConsumibleById || isConsumibleByDesc;
    },
    editCantidadConsumible(row) {
      this.selectedConsumible = row;
      this.nuevaCantidad = row.cantidad;
      this.nuevaUnidad = row.unidad || '';
      this.showEditCantidadModal = true;
    },
    async guardarCantidadConsumible() {
      if (!this.assertAlbaranEditable()) return;
      try {
        this.guardandoCantidad = true;
        const cantidadAAnadir = parseInt(this.nuevaCantidad, 10) || 0;
        if (cantidadAAnadir <= 0) {
          this.$notify({
            type: "warning",
            message: "La cantidad debe ser mayor que 0"
          });
          return;
        }

        // Si ya existe este consumible en el albarán, sumar cantidad en la misma línea
        const consumibleYaAsignado = this.getMateriales.find(material =>
          !material.activo &&
          !(material.es_saca || material.tipo_material === 'saca') &&
          material.idArticulo === this.selectedConsumible.idArticulo
        );

        if (consumibleYaAsignado) {
          const cantidadActual = parseInt(consumibleYaAsignado.cantidad, 10) || 0;
          const nuevaCantidadTotal = cantidadActual + cantidadAAnadir;

          await axios.put(
            `${this.apiUrl}/albaran-activo-materials/${consumibleYaAsignado.id}`,
            {
              data: {
                cantidad: nuevaCantidadTotal,
                descripcion: consumibleYaAsignado.descripcion || this.selectedConsumible.idArticulo_flxtext || this.selectedConsumible.descripcion
              }
            },
            {
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              }
            }
          );

          await this.loadAlbaran();
          this.$forceUpdate();
          this.$notify({
            type: "success",
            message: `Consumible actualizado: ${cantidadActual} + ${cantidadAAnadir} = ${nuevaCantidadTotal}`
          });
          this.showEditCantidadModal = false;
          return;
        }
        
        // Preparar payload para crear registro en albaran_activo_materials
        const payload = {
          tipo_material: 'consumible',
          idArticulo: this.selectedConsumible.idArticulo,
          descripcion: this.selectedConsumible.idArticulo_flxtext,
          cantidad: cantidadAAnadir,
          material_keikaku_id: this.selectedConsumible.idArticulo
        };
        
        console.log('📦 [CONSUMIBLE] Añadiendo consumible desde Keikaku:', payload);
        
        // Llamada a la API para crear registro en albaran_activo_materials
        await axios.post(
          `${this.apiUrl}/albaran-add/${this.albaran.id}`,
          payload,
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        // Recargar el albarán
        await this.loadAlbaran();
        
        // Forzar actualización de la vista
        this.$forceUpdate();
        
        this.$notify({
          type: "success",
          message: "Consumible asignado correctamente al albarán"
        });
        
        this.showEditCantidadModal = false;
        
      } catch (error) {
        console.error("Error asignando consumible:", error);
        this.$notify({
          type: "danger",
          message: `Error al asignar el consumible: ${error.response?.data?.error?.message || error.message}`
        });
      } finally {
        this.guardandoCantidad = false;
      }
    },
    asignarActivo(row) {
      this.modoAsignacion = true;
      this.selectedActivo = row;
      // Limpiar todos los campos de búsqueda
      this.qrActivo = "";
      this.activosEncontrados = null;
      this.activoSeleccionado = null;
      this.showAsignarActivoModal = true;
      
      // Hacer focus en el input de búsqueda después de que el modal se abra
      this.$nextTick(() => {
        if (this.$refs.qrActivoInput && this.$refs.qrActivoInput.$el) {
          const input = this.$refs.qrActivoInput.$el.querySelector('input');
          if (input) {
            input.focus();
          }
        }
      });
    },
    abrirModalAnadirActivo() {
      // Abrir modal de asignación de activo (no modal unificado)
      this.modoAsignacion = false; // No estamos asignando a un material específico
      this.selectedActivo = null;
      // Limpiar todos los campos de búsqueda
      this.qrActivo = "";
      this.activosEncontrados = null;
      this.activoSeleccionado = null;
      this.showAsignarActivoModal = true;
      
      // Hacer focus en el input de búsqueda después de que el modal se abra
      this.$nextTick(() => {
        if (this.$refs.qrActivoInput && this.$refs.qrActivoInput.$el) {
          const input = this.$refs.qrActivoInput.$el.querySelector('input');
          if (input) {
            input.focus();
          }
        }
      });
    },
    cerrarModalActivo() {
      this.showAsignarActivoModal = false;
      this.modoAsignacion = false;
      this.selectedActivo = null;
      this.qrActivo = "";
      this.activosEncontrados = null;
      this.activoSeleccionado = null;
    },
    async buscarActivo() {
      if (!this.qrActivo.trim()) return;
      if (!this.assertAlbaranEditable()) {
        this.avisarAsignacionFallida();
        return;
      }
      let asignado = false;
      
      try {
        this.buscandoActivo = true;
        this.activosEncontrados = null; // Limpiar resultados anteriores
        this.activoSeleccionado = null; // Limpiar selección anterior
        
        // Procesar el QR para extraer identificador si es una URL
        const searchTerm = this.extractIdentifierFromUrl(this.qrActivo.trim());
        
        // Actualizar el campo de entrada con solo el código extraído
        if (searchTerm !== this.qrActivo.trim()) {
          this.qrActivo = searchTerm;
        }
        
        // Buscar el activo por QR o número de serie usando la API directa
        const params = new URLSearchParams();
        params.append('search', searchTerm);
        params.append('idEmpresa', localStorage.getItem("plantaSelected") || '172');
        params.append('sortOrder', 'asc');
        params.append('includeRetired', 'false');
        
        const response = await axios.get(
          `${this.apiUrl}/activos-list?${params.toString()}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        console.log('Respuesta de búsqueda de activo:', response);
        console.log('response.data:', response.data);
        console.log('response.data.activos:', response.data?.activos);
        console.log('response.data.activos.length:', response.data?.activos?.length);
        
        // Verificar si la respuesta tiene la estructura esperada
        if (response.data && response.data.activos && response.data.activos.length > 0) {
          let activos = response.data.activos;
          
          // Verificar si los activos encontrados forman una Saca
          const esSaca = this.detectarSaca(activos, searchTerm);
          
          if (esSaca) {
            // Es una Saca, asignarla directamente usando el endpoint específico
            console.log('✅ [SACA] Saca detectada, asignando...');
            
            // Asignar automáticamente la Saca (sin cerrar el modal para modo lectura continua)
            asignado = !!(await this.asignarSacaAlAlbaran(searchTerm, false));
            
            // Limpiar campos para siguiente búsqueda
            this.qrActivo = "";
            this.activosEncontrados = null;
            this.activoSeleccionado = null;
            
            // Mantener focus en el input para siguiente búsqueda
            this.$nextTick(() => {
              if (this.$refs.qrActivoInput && this.$refs.qrActivoInput.$el) {
                const input = this.$refs.qrActivoInput.$el.querySelector('input');
                if (input) {
                  input.focus();
                }
              }
            });
            
            // Si estamos en modo asignación, verificar si se completaron todas las unidades requeridas
            if (this.modoAsignacion && this.selectedActivo) {
              const activosAsignados = this.getActivosAsignadosCount(this.selectedActivo);
              const activosRequeridos = this.getMaterialesRequeridos(this.selectedActivo);
              
              if (activosAsignados >= activosRequeridos) {
                // Se completaron todas las unidades, cerrar el modal
                this.$notify({
                  type: "success",
                  message: `Se han asignado todos los activos requeridos (${activosAsignados}/${activosRequeridos})`
                });
                this.cerrarModalActivo();
                return;
              }
            }
            return;
          }
          
          // Si no es una Saca, comportamiento normal
          this.activosEncontrados = activos;
          
          // Si solo hay un resultado, seleccionarlo automáticamente
          if (activos.length === 1) {
            this.activoSeleccionado = response.data.activos[0];
            this.$notify({
              type: "info",
              message: "Activo encontrado. Revisa y confirma la asignación."
            });
            
            // Asignar automáticamente el activo (sin cerrar el modal para modo lectura continua)
            asignado = !!(await this.asignarActivoAlAlbaran(false));
            
            // Limpiar campos para siguiente búsqueda
            this.qrActivo = "";
            this.activosEncontrados = null;
            this.activoSeleccionado = null;
            
            // Mantener focus en el input para siguiente búsqueda
            this.$nextTick(() => {
              if (this.$refs.qrActivoInput && this.$refs.qrActivoInput.$el) {
                const input = this.$refs.qrActivoInput.$el.querySelector('input');
                if (input) {
                  input.focus();
                }
              }
            });
            
            // Si estamos en modo asignación, verificar si se completaron todas las unidades requeridas
            if (this.modoAsignacion && this.selectedActivo) {
              const activosAsignados = this.getActivosAsignadosCount(this.selectedActivo);
              const activosRequeridos = this.getMaterialesRequeridos(this.selectedActivo);
              
              if (activosAsignados >= activosRequeridos) {
                // Se completaron todas las unidades, cerrar el modal
                this.$notify({
                  type: "success",
                  message: `Se han asignado todos los activos requeridos (${activosAsignados}/${activosRequeridos})`
                });
                this.cerrarModalActivo();
                return;
              }
            }
          } else {
            // Si hay múltiples resultados, buscar el que tiene el QR exacto
            const activosConQrExacto = activos.filter(activo => {
              const qrActivo = activo.numero_serie_qr || '';
              // Comparar el QR exacto (case-insensitive y sin espacios)
              return qrActivo.trim().toLowerCase() === searchTerm.trim().toLowerCase();
            });
            
            // Si solo uno tiene el QR exacto, seleccionarlo automáticamente
            if (activosConQrExacto.length === 1) {
              this.activoSeleccionado = activosConQrExacto[0];
              console.log('✅ [QR EXACTO] Se encontró un activo con QR exacto, asignando automáticamente:', activosConQrExacto[0].numero_serie_qr);
              
            this.$notify({
              type: "info",
                message: "Activo con QR exacto encontrado. Revisa y confirma la asignación."
              });
              
              // Asignar automáticamente el activo (sin cerrar el modal para modo lectura continua)
              asignado = !!(await this.asignarActivoAlAlbaran(false));
              
              // Limpiar campos para siguiente búsqueda
              this.qrActivo = "";
              this.activosEncontrados = null;
              this.activoSeleccionado = null;
              
              // Mantener focus en el input para siguiente búsqueda
              this.$nextTick(() => {
                if (this.$refs.qrActivoInput && this.$refs.qrActivoInput.$el) {
                  const input = this.$refs.qrActivoInput.$el.querySelector('input');
                  if (input) {
                    input.focus();
                  }
                }
              });
              
              // Si estamos en modo asignación, verificar si se completaron todas las unidades requeridas
              if (this.modoAsignacion && this.selectedActivo) {
                const activosAsignados = this.getActivosAsignadosCount(this.selectedActivo);
                const activosRequeridos = this.getMaterialesRequeridos(this.selectedActivo);
                
                if (activosAsignados >= activosRequeridos) {
                  // Se completaron todas las unidades, cerrar el modal
                  this.$notify({
                    type: "success",
                    message: `Se han asignado todos los activos requeridos (${activosAsignados}/${activosRequeridos})`
                  });
                  this.cerrarModalActivo();
                  return;
                }
              }
            } else {
              // Si hay múltiples con QR exacto o ninguno, mostrar la lista para selección manual
              this.$notify({
                type: "info",
                message: `Se encontraron ${activos.length} activos. ${activosConQrExacto.length === 0 ? 'Ninguno tiene el QR exacto.' : 'Múltiples tienen el QR exacto.'} Selecciona uno de la lista.`
              });
            }
          }
        } else {
          this.activosEncontrados = null;
          this.$notify({
            type: "warning",
            message: "No se encontró ningún activo con ese código en Taller Verticales"
          });
        }
      } catch (error) {
        console.error("Error buscando activo:", error);
        this.activosEncontrados = null;
        this.$notify({
          type: "danger",
          message: "Error al buscar el activo: " + (error.response?.data?.error?.message || error.message)
        });
      } finally {
        this.buscandoActivo = false;
        if (!asignado) this.avisarAsignacionFallida();
      }
    },
    seleccionarActivo(activo) {
      this.activoSeleccionado = activo;
    },
    /**
     * Detecta si los activos encontrados forman una saca
     * Condiciones:
     * - Todos los activos pertenecen a la planta id: 172 (Taller Verticales Salvador Sea)
     * - Todos tienen la misma ubicación
     * - La ubicación contiene la palabra "Saca"
     * - El QR buscado coincide con la ubicación
     */
    detectarSaca(activos, qrBuscado) {
      if (!activos || activos.length < 2) {
        return false;
      }
      
      // Verificar que todos pertenecen a la planta id: 172
      const plantaId = '172';
      const todosMismaPlanta = activos.every(activo => {
        const activoPlantaId = activo.empresa?.id || 
                               activo.empresa?.data?.id || 
                               activo.idEmpresa ||
                               activo.empresa;
        return String(activoPlantaId) === plantaId;
      });
      
      if (!todosMismaPlanta) {
        console.log('❌ [SACA] No todos los activos pertenecen a la planta 172');
        return false;
      }
      
      // Verificar que todos tienen la misma ubicación
      const primeraUbicacion = activos[0]?.ubicacion;
      if (!primeraUbicacion) {
        console.log('❌ [SACA] El primer activo no tiene ubicación');
        return false;
      }
      
      const todosMismaUbicacion = activos.every(activo => {
        return activo.ubicacion === primeraUbicacion;
      });
      
      if (!todosMismaUbicacion) {
        console.log('❌ [SACA] No todos los activos tienen la misma ubicación');
        return false;
      }
      
      // Verificar que la ubicación contiene la palabra "Saca" (case insensitive)
      const ubicacionLower = primeraUbicacion.toLowerCase();
      if (!ubicacionLower.includes('saca')) {
        console.log('❌ [SACA] La ubicación no contiene la palabra "Saca"');
        return false;
      }
      
      // Verificar que el QR buscado coincide con la ubicación (o está incluido en ella)
      const qrBuscadoLower = qrBuscado.toLowerCase();
      const ubicacionCoincide = ubicacionLower.includes(qrBuscadoLower) || 
                                qrBuscadoLower.includes(ubicacionLower) ||
                                primeraUbicacion === qrBuscado;
      
      if (!ubicacionCoincide) {
        console.log('❌ [SACA] El QR buscado no coincide con la ubicación');
        return false;
      }
      
      console.log('✅ [SACA] Saca detectada:', {
        cantidadActivos: activos.length,
        ubicacion: primeraUbicacion,
        qrBuscado: qrBuscado
      });
      
      return true;
    },
    /**
     * Asigna una Saca al albarán usando el endpoint específico
     */
    async asignarSacaAlAlbaran(qr, cerrarModal = true) {
      try {
        this.asignandoActivo = true;
        
        // Preparar payload
        let payload = { 
          qr: qr
        };
        
        if (this.modoAsignacion && this.selectedActivo) {
          payload.material_keikaku_id = this.selectedActivo.idArticulo;
          console.log('🔗 [SACA] Asignando Saca al material:', this.selectedActivo.idArticulo);
        }
        
        // Llamada al endpoint específico para Sacas
        const response = await axios.post(
          `${this.apiUrl}/albaran-add-saca/${this.albaran.id}`,
          payload,
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        console.log('✅ [SACA] Respuesta del backend:', response.data);
        
        // Recargar el albarán
        await this.loadAlbaran();
        
        // Mostrar notificación de éxito
        const sacaInfo = response.data?.meta?.saca_info;
        if (sacaInfo) {
          this.$notify({
            type: "success",
            message: `Saca asignada correctamente: ${sacaInfo.ubicacion} (${sacaInfo.cantidad_activos} activos)`
          });
        } else {
          this.$notify({
            type: "success",
            message: "Saca asignada correctamente al albarán"
          });
        }
        
        // Solo cerrar el modal si se indica explícitamente
        if (cerrarModal) {
          this.showAsignarActivoModal = false;
          this.activoSeleccionado = null;
          this.qrActivo = "";
        }
        return true;
      } catch (error) {
        console.error("Error asignando Saca:", error);
        
        // Manejar diferentes tipos de errores
        if (error.response?.status === 409) {
          this.$notify({
            type: "warning",
            message: "Esta Saca ya está asignada al albarán"
          });
        } else if (error.response?.status === 400) {
          const errorMessage = error.response?.data?.error?.message || 
                              error.response?.data?.message || 
                              "El QR no corresponde a una Saca válida";
          this.$notify({
            type: "warning",
            message: errorMessage
          });
        } else {
          this.$notify({
            type: "danger",
            message: "Error al asignar la Saca: " + (error.response?.data?.error?.message || error.message)
          });
        }
        return false;
      } finally {
        this.asignandoActivo = false;
      }
    },
    async asignarActivoAlAlbaran(cerrarModal = true) {
      if (!this.assertAlbaranEditable()) return false;
      if (!this.activoSeleccionado) return false;
      
      try {
        // Verificar si el activo ya está asignado
        const activoYaAsignado = this.getMateriales.some(material => 
          material.activo && 
          (material.activo.numero_serie_qr === this.activoSeleccionado.numero_serie_qr || 
           material.activo.numero_serie === this.activoSeleccionado.numero_serie)
        );

        if (activoYaAsignado) {
          this.$notify({
            type: "warning",
            message: "Este activo ya está asignado al albarán"
          });
          return false;
        }

        const advertencias = this.getAdvertenciasAsignacionActivo(this.activoSeleccionado);
        if (advertencias.length > 0) {
          this.reproducirTonoNoAsignado();
          const result = await swal.fire({
            title: "Advertencia de asignación",
            html: `
              <div style="text-align:left;">
                <p>Este activo presenta incidencias y no debería asignarse en condiciones normales:</p>
                <ul style="margin:0; padding-left: 20px;">
                  ${advertencias.map(msg => `<li>${msg}</li>`).join("")}
                </ul>
                <p style="margin-top:10px;"><strong>Si continúas, quedará bajo tu responsabilidad.</strong></p>
              </div>
            `,
            icon: "warning",
            target: document.body,
            customClass: {
              container: "swal2-container-albaran",
            },
            showCancelButton: true,
            confirmButtonText: "Asignar igualmente",
            cancelButtonText: "Cancelar",
            confirmButtonClass: "btn btn-warning",
            cancelButtonClass: "btn btn-secondary",
          });

          if (!result.isConfirmed) {
            return false;
          }
        }

        this.asignandoActivo = true;

        // Si estamos en modo asignación, guardar el material_keikaku_id
        let payload = { 
          qr: this.activoSeleccionado.numero_serie_qr,
          tipo_material: 'activo' // ← Añadido tipo_material obligatorio
        };
        
        if (this.modoAsignacion && this.selectedActivo) {
          // Añadir el material_keikaku_id del material seleccionado
          payload.material_keikaku_id = this.selectedActivo.idArticulo;
          console.log('🔗 [ASIGNACIÓN] Asignando activo al material:', this.selectedActivo.idArticulo);
        }

        // Llamada a la API para añadir el activo al albarán usando el endpoint correcto
        await axios.post(
          `${this.apiUrl}/albaran-add/${this.albaran.id}`,
          payload,
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        // Recargar el albarán
        await this.loadAlbaran();
        this.$notify({
          type: "success",
          message: "Activo asignado correctamente al albarán"
        });
        
        // Solo cerrar el modal si se indica explícitamente
        if (cerrarModal) {
        this.showAsignarActivoModal = false;
        this.activoSeleccionado = null;
        this.qrActivo = "";
        }
        return true;
      } catch (error) {
        console.error("Error asignando activo:", error);
        
        // Verificar si el error es por falta de QR
        if (error.response?.data?.error?.message?.includes('QR') || 
            error.response?.data?.message?.includes('QR') ||
            !this.activoSeleccionado.numero_serie_qr) {
          this.$notify({
            type: "warning",
            message: "El activo no se puede asignar al albarán porque no tiene QR"
          });
        } else {
        this.$notify({
          type: "danger",
            message: error.response?.data?.error?.message || "Error al asignar el activo al albarán"
        });
        }
        return false;
      } finally {
        this.asignandoActivo = false;
      }
    },
    getAdvertenciasAsignacionActivo(activo) {
      const advertencias = [];
      const ultimaRevision = this.getUltimaRevisionActivo(activo);
      const estadoRevision = ultimaRevision?.estado;

      if (estadoRevision === "no_apto") {
        advertencias.push("Estado de revisión: NO APTO");
      }

      if (activo?.fin_vida_util_alcanzada === true) {
        advertencias.push("Fin de vida útil alcanzada");
      }

      return advertencias;
    },
    getUltimaRevisionActivo(activo) {
      const revisiones = Array.isArray(activo?.revisiones) ? activo.revisiones : [];
      if (!revisiones.length) return null;

      const getRevisionTs = (rev) => {
        const raw =
          rev?.fecha_revision ||
          rev?.fecha ||
          rev?.updatedAt ||
          rev?.createdAt ||
          rev?.attributes?.fecha_revision ||
          rev?.attributes?.fecha ||
          rev?.attributes?.updatedAt ||
          rev?.attributes?.createdAt;
        const ts = raw ? new Date(raw).getTime() : Number.NaN;
        return Number.isNaN(ts) ? 0 : ts;
      };

      return revisiones.reduce((latest, current) => {
        if (!latest) return current;
        return getRevisionTs(current) > getRevisionTs(latest) ? current : latest;
      }, null);
    },
    getMaterialDescripcionIncidenciaAlbaran(ia) {
      const relation = ia?.albaran_activo_material;
      const m = relation?.data ?? relation;
      if (m && typeof m === 'object') {
        const attrs = m.attributes || m;
        const desc = attrs.descripcion || attrs.idArticulo_flxtext || attrs.idArticulo;
        if (desc) return desc;
      }

      const materialId = this.getIncidenciaMaterialId(ia);
      if (materialId != null) {
        const material = this.getMateriales.find(x => String(x.id) === String(materialId));
        if (material) {
          return material.descripcion || material.idArticulo_flxtext || material.idArticulo || '—';
        }
      }

      return "—";
    },
    getIncidenciaMaterialId(ia) {
      const rel = ia?.albaran_activo_material;
      if (rel == null) return null;
      if (typeof rel === 'number' || typeof rel === 'string') return rel;
      if (rel?.id != null) return rel.id;
      if (rel?.data?.id != null) return rel.data.id;
      return null;
    },
    extraerMotivoRechazo(observaciones) {
      if (!observaciones) return '';
      
      // Buscar el patrón "MATERIAL RECHAZADO - Motivo: ..." o "DEVOLUCIÓN RECHAZADA - Motivo: ..."
      const match = observaciones.match(/(?:MATERIAL RECHAZADO|DEVOLUCIÓN RECHAZADA)\s*-\s*Motivo:\s*(.+?)(?:\n|$)/i);
      if (match && match[1]) {
        return match[1].trim();
      }
      
      // Si no encuentra el patrón, devolver las primeras 100 caracteres
      return observaciones.length > 100 ? observaciones.substring(0, 100) + '...' : observaciones;
    },
    
    formatEstadoMaterial(estado) {
      // Los materiales usan 'entregado' cuando están aceptados por el jefe de equipo
      // Normalizar: "asignado" se muestra como "entregado" (unificación de estados)
      if (estado === 'asignado') {
        estado = 'entregado';
      }
      return getEstadoTextoUtil(estado);
    },
    
    // Función helper para obtener el estado de visualización (consistente con kanban)
    getEstadoVisualizacion(material) {
      const estado = material.estado || material.attributes?.estado;
      // Normalizar: "asignado" se muestra como "entregado" (unificación de estados)
      if (estado === 'asignado') {
        return 'entregado';
      }
      return estado;
    },
    formatResponsable(responsable) {
      const responsables = {
        jefe_taller: "Jefe Taller",
        jefe_equipo: "Jefe Equipo",
        empleado: "Empleado"
      };
      return responsables[responsable] || responsable || '-';
    },
    getActivosAsignadosCount(row) {
      // Para activos, verificar si hay algún activo o Saca asignado que corresponda a este material específico
      const materiales = this.getMateriales;
      
      // Filtrar activos y Sacas asignados
      const activosAsignados = materiales.filter(m => m.activo);
      const sacasAsignadas = materiales.filter(m => m.es_saca || m.tipo_material === 'saca');
      
      // Para materiales que no son consumibles, verificar si hay activos o Sacas asignados
      // que correspondan a este material específico
      if (!this.isConsumible(row)) {
        let count = 0;
        
        // Contar activos asignados para este material
        const activosParaEsteMaterial = activosAsignados.filter(material => {
          // Solo contar si el activo tiene material_keikaku_id y coincide con este material
          if (material.material_keikaku_id) {
            return material.material_keikaku_id === row.idArticulo;
          }
          return false;
        });
        count += activosParaEsteMaterial.length;
        
        // Contar Sacas asignadas para este material
        const sacasParaEsteMaterial = sacasAsignadas.filter(material => {
          // Solo contar si la Saca tiene material_keikaku_id y coincide con este material
          if (material.material_keikaku_id) {
            return material.material_keikaku_id === row.idArticulo;
          }
          return false;
        });
        count += sacasParaEsteMaterial.length;
        
        // Contar asignaciones manuales: primero verificar en materiales_taller
        const materialTaller = this.getMaterialesTaller.find(m => m.idArticulo === row.idArticulo);
        if (materialTaller && materialTaller.marcado_manual === true) {
          // Si está marcado manualmente, contar todas las cantidades requeridas
          const cantidadRequerida = parseInt(row.cantidad) || 1;
          count += cantidadRequerida;
        } else {
          // Si no está marcado en materiales_taller, verificar en materiales asignados (compatibilidad)
          const asignacionesManuales = materiales.filter(material => {
            return material.asignacion_manual === true && 
                   material.material_keikaku_id === row.idArticulo &&
                   !material.activo && 
                   !(material.es_saca || material.tipo_material === 'saca');
          });
          count += asignacionesManuales.length;
        }
        
        return count;
      }
      
      return 0;
    },
    getConsumibleAsignadoCantidad(row) {
      // Solo para consumibles, verificar si hay consumibles asignados para este material específico
      if (!this.isConsumible(row)) return 0;
      
      const materiales = this.getMateriales;
      let total = 0;
      
      // Buscar consumibles asignados que correspondan a este material específico
      // EXCLUIR sacas: las sacas no son consumibles
      materiales.forEach(m => {
        const esSaca = m.es_saca || m.tipo_material === 'saca';
        if (!m.activo && !esSaca) {
          let coincide = false;
          
          // 1. Verificar por material_keikaku_id (método más confiable)
          if (m.material_keikaku_id && row.idArticulo && m.material_keikaku_id === row.idArticulo) {
            coincide = true;
          }
          // 2. Verificar por idArticulo directo
          else if (m.idArticulo && row.idArticulo && m.idArticulo === row.idArticulo) {
            coincide = true;
          }
          // 3. Verificar por descripción/idArticulo_flxtext
          else if (m.descripcion && row.idArticulo_flxtext && m.descripcion === row.idArticulo_flxtext) {
            coincide = true;
          }
          // 4. Fallback: verificar por observaciones (para compatibilidad con datos antiguos)
          else if (m.observaciones && row.idArticulo_flxtext && m.observaciones.includes(row.idArticulo_flxtext)) {
            coincide = true;
          }
          
          if (coincide) {
            // Usar cantidad directa si está disponible
            if (m.cantidad) {
              total += parseFloat(m.cantidad);
            } else if (m.observaciones) {
              // Extraer cantidad del texto de observaciones
          const match = m.observaciones.match(/Cantidad: (\d+(?:[.,]\d+)?)/);
          if (match) {
            total += parseFloat(match[1].replace(',', '.'));
              }
            }
          }
        }
      });
      
      // También contar asignaciones manuales para consumibles: primero verificar en materiales_taller
      const materialTaller = this.getMaterialesTaller.find(m => m.idArticulo === row.idArticulo);
      if (materialTaller && materialTaller.marcado_manual === true) {
        // Si está marcado manualmente en materiales_taller, añadir la cantidad requerida
        const cantidadRequerida = parseInt(row.cantidad) || 1;
        total += cantidadRequerida;
      } else {
        // Si no está marcado en materiales_taller, verificar en materiales asignados (compatibilidad)
        const asignacionesManuales = materiales.filter(material => {
          return material.asignacion_manual === true && 
                 material.material_keikaku_id === row.idArticulo &&
                 !material.activo && 
                 !(material.es_saca || material.tipo_material === 'saca');
        });
        
        asignacionesManuales.forEach(m => {
          if (m.cantidad) {
            total += parseFloat(m.cantidad);
          } else {
            total += 1; // Si no hay cantidad, contar como 1
          }
        });
      }
      
      return total;
    },
    cargarMaterialesMarcadosManual() {
      // Cargar materiales marcados manualmente desde materiales_taller
      this.materialesMarcadosManual = [];
      const materialesTaller = this.getMaterialesTaller || [];
      
      materialesTaller.forEach(material => {
        // Si el material tiene marcado_manual === true, añadirlo al array
        if (material.marcado_manual === true) {
          this.materialesMarcadosManual.push(material.idArticulo);
        }
      });
    },
    estaMarcadoManualmente(row) {
      // Primero verificar en materiales_taller si está marcado manualmente
      const materialTaller = this.getMaterialesTaller.find(m => m.idArticulo === row.idArticulo);
      if (materialTaller && materialTaller.marcado_manual === true) {
        return true;
      }
      
      // Si no está en materiales_taller o marcado_manual es false/no existe,
      // verificar en materiales asignados como hasta ahora
      const materiales = this.getMateriales;
      return materiales.some(material => {
        return material.asignacion_manual === true && 
               material.material_keikaku_id === row.idArticulo &&
               !material.activo && 
               !(material.es_saca || material.tipo_material === 'saca');
      });
    },
    async marcarMaterialManual(row) {
      try {
        const materialId = row.idArticulo;
        const materialTaller = this.getMaterialesTaller.find(m => m.idArticulo === materialId);
        const estaMarcado = materialTaller && materialTaller.marcado_manual === true;
        
        // Actualizar materiales_taller con el nuevo estado
        const materialesTallerActualizados = this.getMaterialesTaller.map(material => {
          if (material.idArticulo === materialId) {
            return {
              ...material,
              marcado_manual: !estaMarcado
            };
          }
          return material;
        });
        
        // Actualizar el albarán con los materiales_taller modificados
        await axios.put(
          `${this.apiUrl}/albaran-activos/${this.albaran.id}`,
          {
            data: {
              materiales_taller: materialesTallerActualizados
            }
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        // Actualizar el estado local
        if (estaMarcado) {
          const index = this.materialesMarcadosManual.indexOf(materialId);
          if (index > -1) {
            this.materialesMarcadosManual.splice(index, 1);
          }
        } else {
          if (!this.materialesMarcadosManual.includes(materialId)) {
            this.materialesMarcadosManual.push(materialId);
          }
        }
        
        // Recargar el albarán para sincronizar
        await this.loadAlbaran();
        this.$forceUpdate();
        
      } catch (error) {
        console.error("Error marcando/desmarcando material:", error);
        this.$notify({
          type: "danger",
          message: `Error: ${error.response?.data?.error?.message || error.message || "Error desconocido"}`
        });
      }
    },
    
    getMaterialesRequeridos(row) {
      // Obtener la cantidad requerida del material de Keikaku
      if (this.isConsumible(row)) {
        // Para consumibles, usar la cantidad del material del taller
        const materialTaller = this.albaran?.materiales_taller?.find(m => m.idArticulo === row.idArticulo);
        if (materialTaller && materialTaller.cantidad) {
          return parseInt(materialTaller.cantidad);
        }
        return 1; // Valor por defecto
      } else {
        // Para activos, usar la cantidad del material del taller
        const materialTaller = this.albaran?.materiales_taller?.find(m => m.idArticulo === row.idArticulo);
        if (materialTaller && materialTaller.cantidad) {
          return parseInt(materialTaller.cantidad);
        }
        return 1; // Valor por defecto
      }
    },
    
    getProgresoBadgeClass(row) {
      const asignados = this.getActivosAsignadosCount(row);
      const requeridos = this.getMaterialesRequeridos(row);
      
      if (asignados >= requeridos) {
        return 'badge badge-secondary text-white'; // Gris: completo
      } else if (asignados > 0) {
        return 'badge badge-warning text-white'; // Amarillo: parcial
      } else {
        return 'badge badge-danger text-white'; // Rojo: nada
      }
    },
    
    getProgresoIconClass(row) {
      const asignados = this.getActivosAsignadosCount(row);
      const requeridos = this.getMaterialesRequeridos(row);
      
      if (asignados >= requeridos) {
        return 'fas fa-check-circle'; // Gris oscuro: completo (se aplica estilo inline)
      } else if (asignados > 0) {
        return 'fas fa-exclamation-circle text-warning'; // Amarillo: parcial
      } else {
        return 'fas fa-times-circle text-danger'; // Rojo: nada
      }
    },
    
    getMaterialCantidad(material) {
      // Obtener la cantidad del material
      if (material.cantidad !== undefined && material.cantidad !== null) {
        return material.cantidad;
      }
      
      // Si no hay cantidad directa, intentar extraer de observaciones
      if (material.observaciones) {
        const match = material.observaciones.match(/Cantidad: (\d+(?:[.,]\d+)?)/);
        if (match) {
          return parseFloat(match[1].replace(',', '.'));
        }
      }
      
      return 1; // Valor por defecto
    },
    
    getMaterialUnidad(material) {
      if (!material) return null;
      
      // Manejar tanto formato directo como Strapi v4 (con attributes)
      const materialData = material.attributes || material;
      
      // Buscar unidad en diferentes lugares posibles
      if (materialData.unidad) {
        return materialData.unidad;
      }
      
      // Si no hay unidad, retornar null
      return null;
    },
    async desasignarEmpleado() {
      if (!this.albaran) return;
      try {
        this.assigningUser = true;
        
        // Usar el endpoint correcto para desasignar usuario
        await axios.put(
          `${this.apiUrl}/albaran-activo-usuario/${this.albaran.id}/desasignar-jefe-equipo`,
          {
            userId: null
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        await this.loadAlbaran();
        this.$notify({
          type: "success",
          message: "Jefe de equipo desasignado correctamente"
        });
      } catch (error) {
        console.error("Error desasignando empleado:", error);
        this.$notify({
          type: "danger",
          message: "Error al desasignar el jefe de equipo: " + (error.response?.data?.error?.message || error.message)
        });
      } finally {
        this.assigningUser = false;
      }
    },
    canDesasignar(material) {
      // El jefe de taller puede eliminar materiales en cualquier estado
      // EXCEPTO si el activo tiene incidencias activas (no resueltas)
      
      // Verificar si es una saca (se trata como activo)
      const esSaca = material.es_saca || material.tipo_material === 'saca';
      
      // Solo verificar incidencias si es un activo o saca (no consumible)
      if (!material.activo && !esSaca) {
        // Para consumibles, permitir siempre
        return true;
      }
      
      // Para sacas, permitir siempre (no tienen incidencias)
      if (esSaca) {
        return true;
      }
      
      // Verificar si el activo tiene incidencias activas
      if (material.incidencias && Array.isArray(material.incidencias) && material.incidencias.length > 0) {
        // Verificar si hay alguna incidencia no resuelta
        const tieneIncidenciaActiva = material.incidencias.some(inc => {
          // Manejar diferentes formatos de datos (Strapi v4 con attributes o formato deserializado)
          const estadoNombre = inc.attributes?.estado_incidencia?.data?.attributes?.nombre || 
                              inc.estado_incidencia?.nombre ||
                              inc.estado_incidencia?.data?.attributes?.nombre;
          
          // Si el estado no es "Resuelta", la incidencia está activa
          return estadoNombre && estadoNombre !== 'Resuelta';
        });
        
        // Si hay incidencias activas, no permitir desasignar
        if (tieneIncidenciaActiva) {
          return false;
        }
      }
      
      // Si no hay incidencias activas, permitir desasignar
      return true;
    },
    
    // Verificar si un material tiene incidencia activa
    hasIncidenciaActiva(material) {
      if (!material.activo || !material.incidencias || !Array.isArray(material.incidencias) || material.incidencias.length === 0) {
        return false;
      }
      
      return material.incidencias.some(inc => {
        const estadoNombre = inc.attributes?.estado_incidencia?.data?.attributes?.nombre || 
                            inc.estado_incidencia?.nombre ||
                            inc.estado_incidencia?.data?.attributes?.nombre;
        return estadoNombre && estadoNombre !== 'Resuelta';
      });
    },
    
    // Abrir modal para resolver incidencia
    abrirModalResolverIncidencia(material) {
      if (!material.activo || !material.incidencias || material.incidencias.length === 0) {
        this.$notify({
          type: "warning",
          message: "Este material no tiene incidencias activas."
        });
        return;
      }
      
      // Obtener la primera incidencia activa
      const incidenciaActiva = material.incidencias.find(inc => {
        const estadoNombre = inc.attributes?.estado_incidencia?.data?.attributes?.nombre || 
                            inc.estado_incidencia?.nombre ||
                            inc.estado_incidencia?.data?.attributes?.nombre;
        return estadoNombre && estadoNombre !== 'Resuelta';
      });
      
      if (!incidenciaActiva) {
        this.$notify({
          type: "warning",
          message: "No se encontró una incidencia activa para resolver."
        });
        return;
      }
      
      this.incidenciaSeleccionada = {
        ...material,
        incidencia: incidenciaActiva
      };
      this.comentarioResolucion = "";
      this.showResolverIncidenciaModal = true;
    },
    
    // Cerrar modal de resolución de incidencia
    cerrarModalResolverIncidencia() {
      this.showResolverIncidenciaModal = false;
      this.incidenciaSeleccionada = null;
      this.comentarioResolucion = "";
    },
    
    // Resolver incidencia desde el listado
    async resolverIncidenciaDesdeListado() {
      if (!this.incidenciaSeleccionada || !this.incidenciaSeleccionada.incidencia) {
        this.$notify({
          type: "warning",
          message: "No hay incidencia seleccionada."
        });
        return;
      }
      
      if (!this.comentarioResolucion || !this.comentarioResolucion.trim()) {
        this.$notify({
          type: "warning",
          message: "Debes agregar un comentario de resolución."
        });
        return;
      }
      
      try {
        this.resolviendoIncidencia = true;
        
        const incidencia = this.incidenciaSeleccionada.incidencia;
        const incidenciaId = incidencia.id || 
                            incidencia.data?.id ||
                            incidencia.attributes?.id;
        
        // Preparar los datos de la incidencia en el formato correcto para Strapi
        const incidenciaUpdate = {
          id: incidenciaId,
          resolucion: this.comentarioResolucion.trim(),
          estado_incidencia: {
            id: 2 // ID del estado "Resuelta"
          },
          fecha_resolucion: new Date().toISOString()
        };
        
        // Mantener los documentos relacionados si existen
        if (incidencia.incidence_documents && incidencia.incidence_documents.data) {
          incidenciaUpdate.incidence_documents = incidencia.incidence_documents.data.map(doc => doc.id);
        } else if (incidencia.attributes?.incidence_documents?.data) {
          incidenciaUpdate.incidence_documents = incidencia.attributes.incidence_documents.data.map(doc => doc.id);
        }
        
        // Actualizar la incidencia
        await this.$store.dispatch("incidencias/update", incidenciaUpdate);
        
        // Cerrar el modal
        this.cerrarModalResolverIncidencia();
        
        this.$notify({
          type: "success",
          message: "Incidencia resuelta correctamente."
        });
        
        // Recargar el albarán para actualizar las incidencias
        await this.loadAlbaran();
        
      } catch (error) {
        console.error('Error al resolver la incidencia:', error);
        this.$notify({
          type: "danger",
          message: `Error al resolver la incidencia: ${error.response?.data?.error?.message || error.message || 'Error desconocido'}`
        });
      } finally {
        this.resolviendoIncidencia = false;
      }
    },

    showConfirmDialog(options) {
      this.confirmTitle = options.title;
      this.confirmMessage = options.message;
      this.confirmButtonText = options.buttonText || 'Confirmar';
      this.confirmButtonType = options.buttonType || 'primary';
      this.confirmAction = options.action;
      this.showConfirmModal = true;
    },
    cancelConfirmAction() {
      this.showConfirmModal = false;
      this.confirmAction = null;
    },
    executeConfirmAction() {
      if (this.confirmAction) {
        this.confirmAction();
      }
      this.showConfirmModal = false;
      this.confirmAction = null;
    },
    // Métodos para modal de añadir consumibles
    abrirModalAnadirConsumible() {
      // Redirigir al modal unificado
      this.abrirModalAddMaterial();
    },
    
    cerrarModalAddConsumible() {
      this.showAddConsumibleModal = false;
      this.nuevoConsumible = {
        descripcion: '',
        cantidad: ''
      };
    },
    
    async guardarNuevoConsumible() {
      if (!this.nuevoConsumible.descripcion.trim() || !this.nuevoConsumible.cantidad) {
        this.$notify({
          type: "warning",
          message: "Por favor, completa todos los campos"
        });
        return;
      }
      
      try {
        this.guardandoConsumible = true;
        
        // Preparar payload para crear registro en albaran_activo_materials
        const payload = {
          tipo_material: 'consumible',
          idArticulo: this.nuevoConsumible.descripcion.trim(),
          descripcion: this.nuevoConsumible.descripcion.trim(),
          cantidad: parseInt(this.nuevoConsumible.cantidad)
        };
        
        console.log('📦 [CONSUMIBLE] Añadiendo consumible manual:', payload);
        
        // Llamada a la API para crear registro en albaran_activo_materials
        await axios.post(
          `${this.apiUrl}/albaran-add/${this.albaran.id}`,
          payload,
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        // Recargar el albarán
        await this.loadAlbaran();
        
        // Forzar actualización de la vista
        this.$forceUpdate();
        
        this.$notify({
          type: "success",
          message: "Consumible añadido correctamente al albarán"
        });
        
        this.cerrarModalAddConsumible();
      } catch (error) {
        console.error("Error añadiendo consumible:", error);
        this.$notify({
          type: "danger",
          message: `Error al añadir el consumible: ${error.response?.data?.error?.message || error.message}`
        });
      } finally {
        this.guardandoConsumible = false;
      }
    },
    
    // Métodos para modal de edición de materiales
    editarMaterial(material) {
      this.materialSeleccionado = material;
      // Manejar tanto formato directo como Strapi v4 (con attributes)
      const materialData = material.attributes || material;
      let estado = materialData.estado || '';
      // Normalizar: "asignado" se convierte a "entregado" (unificación de estados)
      if (estado === 'asignado') {
        estado = 'entregado';
      }
      this.editMaterial = {
        estado: estado,
        observaciones: materialData.observaciones || '',
        responsable_actual: materialData.responsable_actual || '',
        cantidad: materialData.cantidad || null
      };
      this.showEditMaterialModal = true;
    },
    
    async guardarEdicionMaterial() {
      if (!this.assertAlbaranEditable()) return;
      if (!this.materialSeleccionado) return;
      
      try {
        // Preparar solo los campos válidos para el endpoint
        const dataPayload = {};
        
        // Solo incluir campos que existen en el objeto editMaterial
        if (this.editMaterial.estado) {
          dataPayload.estado = this.editMaterial.estado;
        }
        if (this.editMaterial.observaciones) {
          dataPayload.observaciones = this.editMaterial.observaciones;
        }
        if (this.editMaterial.responsable_actual) {
          dataPayload.responsable_actual = this.editMaterial.responsable_actual;
        }
        
        // Incluir cantidad si es un consumible y el estado es pendiente_aceptar o rechazado
        const materialData = this.materialSeleccionado.attributes || this.materialSeleccionado;
        const esConsumible = !materialData.activo && 
                             !materialData.es_saca && 
                             materialData.tipo_material !== 'saca';
        const estadoPermiteEditarCantidad = this.editMaterial.estado === 'pendiente_aceptar' || 
                                            this.editMaterial.estado === 'rechazado';
        if (esConsumible && estadoPermiteEditarCantidad && this.editMaterial.cantidad !== null && this.editMaterial.cantidad !== undefined) {
          dataPayload.cantidad = parseInt(this.editMaterial.cantidad);
        }
        
        // Añadir fecha_accion si se está cambiando el estado
        if (this.editMaterial.estado) {
          dataPayload.fecha_accion = new Date().toISOString();
        }
        
        // Envolver en el objeto data como requiere el endpoint
        const payload = {
          data: dataPayload
        };
        
        console.log('Enviando payload:', payload);
        console.log('Material seleccionado:', this.materialSeleccionado);
        console.log('URL:', `${this.apiUrl}/albaran-activo-materials/${this.materialSeleccionado.id}`);
        
        // Usar directamente la API en lugar del store
        await axios.put(
          `${this.apiUrl}/albaran-activo-materials/${this.materialSeleccionado.id}`,
          payload,
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        await this.loadAlbaran();
        
        // Actualizar el estado del albarán basándose en los estados de los materiales
        await this.actualizarEstadoAlbaran();
        
        this.$notify({
          type: "success",
          message: "Material actualizado correctamente"
        });
        
        this.showEditMaterialModal = false;
        this.materialSeleccionado = null;
      } catch (error) {
        console.error("Error actualizando material:", error);
        this.$notify({
          type: "danger",
          message: "Error al actualizar el material: " + (error.response?.data?.error?.message || error.message)
        });
      }
    },
    
    cerrarModalEditMaterial() {
      this.showEditMaterialModal = false;
      this.materialSeleccionado = null;
      this.editMaterial = {
        estado: '',
        observaciones: '',
        responsable_actual: '',
        cantidad: null
      };
    },
    
    cerrarModalEditAlbaran() {
      this.showEditAlbaranModal = false;
      this.editAlbaran = {
        titulo: '',
        estado: '',
        fecha_entrega: '',
        fecha_devolucion: '',
        descripcion: '',
        observaciones: ''
      };
    },

    // Métodos para revisión masiva
    async aceptarDevolucionesMasivas(materiales) {
      try {
        this.loading = true;
        
        // Procesar todos los materiales directamente sin preguntar cantidad
        const promises = materiales.map(async (material) => {
          // Para consumibles, usar la cantidad completa que dice el jefe de equipo
          if (!material.activo && !material.es_saca && material.tipo_material !== 'saca') {
            // Es un consumible - devolver la cantidad completa
            const cantidad = material.cantidad || this.getMaterialCantidad(material) || 0;
            
            await axios.put(
              `${this.apiUrl}/albaran-activo-materials/${material.id}`,
              {
                data: {
                  estado: "devuelto",
                  cantidad: parseFloat(cantidad),
                  responsable_actual: "jefe_taller",
                  fecha_accion: new Date().toISOString(),
                  usuario_accion: localStorage.getItem('userId')
                }
              },
              {
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
              }
            );
          } else {
            // Es un activo o saca - procesar directamente
            await axios.put(
              `${this.apiUrl}/albaran-activo-materials/${material.id}`,
              {
                data: {
                  estado: "devuelto",
                  responsable_actual: "jefe_taller",
                  fecha_accion: new Date().toISOString(),
                  usuario_accion: localStorage.getItem('userId')
                }
              },
              {
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
              }
            );
          }
        });
        
        await Promise.all(promises);
        
        // Recargar el albarán para actualizar la vista
        await this.loadAlbaran();
        
        this.$notify({
          type: "success",
          message: `${materiales.length} materiales aceptados correctamente`
        });
      } catch (error) {
        console.error("Error aceptando devoluciones masivas:", error);
        this.$notify({
          type: "danger",
          message: "Error al aceptar las devoluciones: " + (error.response?.data?.error?.message || error.message)
        });
      } finally {
        this.loading = false;
      }
    },

    async aceptarDevolucionConsumible({ material, cantidad }) {
      try {
        // Actualizar la cantidad del material antes de aceptar
        const materialActualizado = {
          ...material,
          cantidad: cantidad
        };
        
        await this.aceptarDevolucion(materialActualizado);
        
        const unidad = (material && material.unidad) ? material.unidad : 'un';
        this.$notify({
          type: "success",
          message: `Consumible aceptado: ${cantidad} ${unidad}`
        });
      } catch (error) {
        console.error("Error aceptando devolución de consumible:", error);
        this.$notify({
          type: "danger",
          message: "Error al aceptar la devolución: " + (error.response?.data?.error?.message || error.message)
        });
      }
    },
    

    
    async desasignarActivo(material) {
      try {
        if (!this.canDesasignar(material)) {
          // Verificar si la razón es por incidencias activas
          if (material.activo && material.incidencias && Array.isArray(material.incidencias) && material.incidencias.length > 0) {
            const tieneIncidenciaActiva = material.incidencias.some(inc => {
              const estadoNombre = inc.attributes?.estado_incidencia?.data?.attributes?.nombre || 
                                  inc.estado_incidencia?.nombre ||
                                  inc.estado_incidencia?.data?.attributes?.nombre;
              return estadoNombre && estadoNombre !== 'Resuelta';
            });
            
            if (tieneIncidenciaActiva) {
              this.$notify({
                type: "warning",
                message: "No se puede desasignar un activo que tiene incidencias activas. Por favor, resuelva las incidencias primero."
              });
              return;
            }
          }
          
          this.$notify({
            type: "warning",
            message: "No se puede desasignar un activo cuando está en estado '" + this.formatEstadoMaterial(this.getEstadoVisualizacion(material)) + "'"
          });
          return;
        }

        this.showConfirmDialog({
          title: 'Desasignar Activo',
          message: '¿Está seguro de que desea desasignar este activo del albarán?',
          buttonText: 'Desasignar',
          buttonType: 'danger',
          action: async () => {
            this.loading = true;
            try {
              await axios.delete(
                `${this.apiUrl}/albaran-activo-materials/${material.id}`,
                {
                  headers: {
                    'accept': 'application/json'
                  }
                }
              );

              await this.loadAlbaran();

              this.$notify({
                type: "success",
                message: "Activo desasignado correctamente del albarán"
              });
            } catch (error) {
              console.error("Error desasignando activo:", error);
              this.$notify({
                type: "danger",
                message: "Error al desasignar el activo del albarán: " + (error.response?.data?.error?.message || error.message)
              });
            } finally {
              this.loading = false;
            }
          }
        });
      } catch (error) {
        console.error("Error en desasignarActivo:", error);
      }
    },

    async desasignarConsumible(material) {
      try {
        if (!this.canDesasignar(material)) {
          this.$notify({
            type: "warning",
            message: "No se puede desasignar un consumible cuando está en estado '" + this.formatEstadoMaterial(this.getEstadoVisualizacion(material)) + "'"
          });
          return;
        }

        this.showConfirmDialog({
          title: 'Desasignar Consumible',
          message: '¿Está seguro de que desea desasignar este consumible del albarán?',
          buttonText: 'Desasignar',
          buttonType: 'danger',
          action: async () => {
            this.loading = true;
            try {
              // Eliminar el registro de albaran_activo_materials
              await axios.delete(
                `${this.apiUrl}/albaran-activo-materials/${material.id}`,
                  {
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                  }
                );

                await this.loadAlbaran();

                this.$notify({
                  type: "success",
                  message: "Consumible desasignado correctamente"
                });
            } catch (error) {
              console.error("Error desasignando consumible:", error);
              this.$notify({
                type: "danger",
                message: "Error al desasignar el consumible: " + (error.response?.data?.error?.message || error.message)
              });
            } finally {
              this.loading = false;
            }
          }
        });
      } catch (error) {
        console.error("Error en desasignarConsumible:", error);
      }
    },

    async aceptarDevolucion(material) {
      // Verificar si es una saca (se trata como activo, no como consumible)
      const esSaca = material.es_saca || material.tipo_material === 'saca';
      
      // Si es un consumible (no saca), abrir modal para ingresar cantidad
      if (!material.activo && !esSaca) {
        this.materialDevolucion = material;
        this.cantidadDevolucion = material.cantidad || this.getMaterialCantidad(material) || "";
        this.showCantidadDevolucionModal = true;
        return;
      }

      // Para activos y sacas, procesar directamente
      try {
        this.loading = true;

        await axios.put(
          `${this.apiUrl}/albaran-activo-materials/${material.id}`,
          {
            data: {
              estado: "devuelto",
              responsable_actual: "jefe_taller",
              fecha_accion: new Date().toISOString(),
              usuario_accion: localStorage.getItem('userId')
            }
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );

        await this.loadAlbaran();
        
        // Actualizar el estado del albarán basándose en los estados de los materiales
        await this.actualizarEstadoAlbaran();

        this.$notify({
          type: "success",
          message: "Devolución aceptada correctamente"
        });

      } catch (error) {
        console.error("Error al aceptar la devolución:", error);
        this.$notify({
          type: "danger",
          message: "Error al aceptar la devolución: " + (error.message || "Error desconocido")
        });
      } finally {
        this.loading = false;
      }
    },

    async confirmarDevolucionConCantidad() {
      if (!this.materialDevolucion || !this.cantidadDevolucion) {
        return;
      }

      try {
        this.guardandoDevolucion = true;

        const cantidadDevolucion = parseFloat(this.cantidadDevolucion);
        const cantidadOriginal = this.materialDevolucion.cantidad || this.getMaterialCantidad(this.materialDevolucion) || 0;
        const cantidadNoDevuelta = cantidadOriginal - cantidadDevolucion;

        // Si es devolución parcial (cantidadDevolucion < cantidadOriginal)
        if (cantidadDevolucion < cantidadOriginal && cantidadNoDevuelta > 0) {
          // 1. Actualizar el registro actual con la cantidad devuelta en estado devuelto
        await axios.put(
          `${this.apiUrl}/albaran-activo-materials/${this.materialDevolucion.id}`,
          {
            data: {
              estado: "devuelto",
                cantidad: cantidadDevolucion,
              responsable_actual: "jefe_taller",
              fecha_accion: new Date().toISOString(),
                usuario_accion: localStorage.getItem('userId'),
                observaciones: `Devolución parcial: ${cantidadDevolucion} de ${cantidadOriginal} unidades devueltas`
            }
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );

          // 2. Crear un nuevo registro para la parte no devuelta en estado entregado
          const materialNoDevueltoData = {
            tipo_material: "consumible",
            estado: "pendiente_aceptar",
            cantidad: cantidadNoDevuelta,
            idArticulo: this.materialDevolucion.idArticulo || this.materialDevolucion.idArticulo_flxtext || "",
            descripcion: this.materialDevolucion.descripcion || this.materialDevolucion.idArticulo_flxtext || "",
            idArticulo_flxtext: this.materialDevolucion.idArticulo_flxtext || this.materialDevolucion.descripcion || "",
            unidad: this.materialDevolucion.unidad || "",
            responsable_actual: "jefe_equipo",
            observaciones: `Parte no devuelta: ${cantidadNoDevuelta} unidades permanecen en estado entregado`
          };

          // Crear el nuevo registro
          const createResponse = await axios.post(
            `${this.apiUrl}/albaran-add/${this.albaran.id}`,
            materialNoDevueltoData,
            {
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              }
            }
          );

          if (createResponse.status === 200) {
            // Obtener el ID del material recién creado
            const albaranActualizado = createResponse.data.data || createResponse.data;
            const materiales = albaranActualizado.albaran_activo_materials || [];
            const nuevoMaterial = materiales[materiales.length - 1];
            
            if (nuevoMaterial && nuevoMaterial.id) {
              // Actualizar el nuevo registro a estado entregado
              await axios.put(
                `${this.apiUrl}/albaran-activo-materials/${nuevoMaterial.id}`,
                {
                  data: {
                    estado: "entregado",
                    responsable_actual: "jefe_equipo",
                    fecha_accion: new Date().toISOString(),
                    usuario_accion: localStorage.getItem('userId'),
                    observaciones: `Parte no devuelta: ${cantidadNoDevuelta} unidades permanecen en estado entregado`
                  }
                },
                {
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                  }
                }
              );
            }
          }

          await this.loadAlbaran();
        await this.actualizarEstadoAlbaran();
          this.cerrarModalCantidadDevolucion();

          const unidad = (this.materialDevolucion && this.materialDevolucion.unidad) ? this.materialDevolucion.unidad : 'un';
          this.$notify({
            type: "success",
            message: `Devolución parcial aceptada: ${cantidadDevolucion} ${unidad} devueltas. ${cantidadNoDevuelta} ${unidad} permanecen en estado entregado.`
          });
        } else {
          // Devolución completa: actualizar el registro existente
          await axios.put(
            `${this.apiUrl}/albaran-activo-materials/${this.materialDevolucion.id}`,
            {
              data: {
                estado: "devuelto",
                cantidad: cantidadDevolucion,
                responsable_actual: "jefe_taller",
                fecha_accion: new Date().toISOString(),
                usuario_accion: localStorage.getItem('userId')
              }
            },
            {
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              }
            }
          );

          await this.loadAlbaran();
          await this.actualizarEstadoAlbaran();
        this.cerrarModalCantidadDevolucion();

        const unidad = (this.materialDevolucion && this.materialDevolucion.unidad) ? this.materialDevolucion.unidad : 'un';
        this.$notify({
          type: "success",
            message: `Devolución aceptada: ${cantidadDevolucion} ${unidad}`
        });
        }

      } catch (error) {
        console.error("Error al aceptar la devolución:", error);
        this.$notify({
          type: "danger",
          message: "Error al aceptar la devolución: " + (error.message || "Error desconocido")
        });
      } finally {
        this.guardandoDevolucion = false;
      }
    },

    cerrarModalCantidadDevolucion() {
      this.showCantidadDevolucionModal = false;
      this.materialDevolucion = null;
      this.cantidadDevolucion = "";
    },

    async marcarComoNoDevuelto(material) {
      const esFacturable = this.getAlbaranAttributes.es_facturable;
      const mensaje = esFacturable 
        ? `¿Está seguro de marcar este consumible como NO DEVUELTO? Este albarán es FACTURABLE y este material será tenido en cuenta para la facturación.`
        : `¿Está seguro de marcar este consumible como NO DEVUELTO?`;

      this.showConfirmDialog({
        title: 'Marcar como No Devuelto',
        message: mensaje,
        buttonText: 'Confirmar',
        buttonType: 'warning',
        action: async () => {
          try {
            this.loading = true;
            await axios.put(
              `${this.apiUrl}/albaran-activo-materials/${material.id}`,
              {
                data: {
                  estado: "no_devuelto",
                  responsable_actual: "jefe_taller",
                  fecha_accion: new Date().toISOString(),
                  usuario_accion: localStorage.getItem('userId'),
                  observaciones: material.observaciones 
                    ? `${material.observaciones} - Marcado como no devuelto por jefe de taller`
                    : 'Marcado como no devuelto por jefe de taller'
                }
              },
              {
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
              }
            );

            await this.loadAlbaran();
            await this.actualizarEstadoAlbaran();
            
            const mensajeNotificacion = esFacturable
              ? 'Consumible marcado como NO DEVUELTO. Este material será tenido en cuenta para la facturación.'
              : 'Consumible marcado como NO DEVUELTO.';
            
            this.$notify({
              type: "warning",
              message: mensajeNotificacion
            });
          } catch (error) {
            console.error("Error al marcar como no devuelto:", error);
            this.$notify({
              type: "danger",
              message: "Error al marcar como no devuelto: " + (error.message || "Error desconocido")
            });
          } finally {
            this.loading = false;
          }
        }
      });
    },

    async marcarComoConsumido(material) {
      const esFacturable = this.getAlbaranAttributes.es_facturable;
      const mensaje = esFacturable 
        ? `¿Está seguro de marcar este consumible como CONSUMIDO? Este albarán es FACTURABLE y este material será tenido en cuenta para la facturación.`
        : `¿Está seguro de marcar este consumible como CONSUMIDO?`;

      this.showConfirmDialog({
        title: 'Marcar como Consumido',
        message: mensaje,
        buttonText: 'Confirmar',
        buttonType: 'info',
        action: async () => {
          try {
            this.loading = true;
            await axios.put(
              `${this.apiUrl}/albaran-activo-materials/${material.id}`,
              {
                data: {
                  estado: "consumido",
                  responsable_actual: "jefe_taller",
                  fecha_accion: new Date().toISOString(),
                  usuario_accion: localStorage.getItem('userId'),
                  observaciones: material.observaciones 
                    ? `${material.observaciones} - Marcado como consumido por jefe de taller`
                    : 'Marcado como consumido por jefe de taller'
                }
              },
              {
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
              }
            );

            await this.loadAlbaran();
            await this.actualizarEstadoAlbaran();
            
            const mensajeNotificacion = esFacturable
              ? 'Consumible marcado como CONSUMIDO. Este material será tenido en cuenta para la facturación.'
              : 'Consumible marcado como CONSUMIDO.';
            
            this.$notify({
              type: "info",
              message: mensajeNotificacion
            });
          } catch (error) {
            console.error("Error al marcar como consumido:", error);
            this.$notify({
              type: "danger",
              message: "Error al marcar como consumido: " + (error.message || "Error desconocido")
            });
          } finally {
            this.loading = false;
          }
        }
      });
    },

    async marcarComoNoDevueltoDesdeModal() {
      if (!this.materialDevolucion) return;
      
      const esFacturable = this.getAlbaranAttributes.es_facturable;
      const mensaje = esFacturable 
        ? `¿Está seguro de marcar este consumible como NO DEVUELTO? Este albarán es FACTURABLE y este material será tenido en cuenta para la facturación.`
        : `¿Está seguro de marcar este consumible como NO DEVUELTO?`;

      this.showConfirmDialog({
        title: 'Marcar como No Devuelto',
        message: mensaje,
        buttonText: 'Confirmar',
        buttonType: 'warning',
        action: async () => {
          try {
            this.guardandoDevolucion = true;
            await axios.put(
              `${this.apiUrl}/albaran-activo-materials/${this.materialDevolucion.id}`,
              {
                data: {
                  estado: "no_devuelto",
                  responsable_actual: "jefe_taller",
                  fecha_accion: new Date().toISOString(),
                  usuario_accion: localStorage.getItem('userId'),
                  observaciones: this.materialDevolucion.observaciones 
                    ? `${this.materialDevolucion.observaciones} - Marcado como no devuelto por jefe de taller`
                    : 'Marcado como no devuelto por jefe de taller'
                }
              },
              {
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
              }
            );

            await this.loadAlbaran();
            await this.actualizarEstadoAlbaran();
            this.cerrarModalCantidadDevolucion();
            
            const mensajeNotificacion = esFacturable
              ? 'Consumible marcado como NO DEVUELTO. Este material será tenido en cuenta para la facturación.'
              : 'Consumible marcado como NO DEVUELTO.';
            
            this.$notify({
              type: "warning",
              message: mensajeNotificacion
            });
          } catch (error) {
            console.error("Error al marcar como no devuelto:", error);
            this.$notify({
              type: "danger",
              message: "Error al marcar como no devuelto: " + (error.message || "Error desconocido")
            });
          } finally {
            this.guardandoDevolucion = false;
          }
        }
      });
    },

    async marcarComoConsumidoDesdeModal() {
      if (!this.materialDevolucion) return;
      
      const esFacturable = this.getAlbaranAttributes.es_facturable;
      const mensaje = esFacturable 
        ? `¿Está seguro de marcar este consumible como CONSUMIDO? Este albarán es FACTURABLE y este material será tenido en cuenta para la facturación.`
        : `¿Está seguro de marcar este consumible como CONSUMIDO?`;

      this.showConfirmDialog({
        title: 'Marcar como Consumido',
        message: mensaje,
        buttonText: 'Confirmar',
        buttonType: 'info',
        action: async () => {
          try {
            this.guardandoDevolucion = true;
            await axios.put(
              `${this.apiUrl}/albaran-activo-materials/${this.materialDevolucion.id}`,
              {
                data: {
                  estado: "consumido",
                  responsable_actual: "jefe_taller",
                  fecha_accion: new Date().toISOString(),
                  usuario_accion: localStorage.getItem('userId'),
                  observaciones: this.materialDevolucion.observaciones 
                    ? `${this.materialDevolucion.observaciones} - Marcado como consumido por jefe de taller`
                    : 'Marcado como consumido por jefe de taller'
                }
              },
              {
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
              }
            );

            await this.loadAlbaran();
            await this.actualizarEstadoAlbaran();
            this.cerrarModalCantidadDevolucion();
            
            const mensajeNotificacion = esFacturable
              ? 'Consumible marcado como CONSUMIDO. Este material será tenido en cuenta para la facturación.'
              : 'Consumible marcado como CONSUMIDO.';
            
            this.$notify({
              type: "info",
              message: mensajeNotificacion
            });
          } catch (error) {
            console.error("Error al marcar como consumido:", error);
            this.$notify({
              type: "danger",
              message: "Error al marcar como consumido: " + (error.message || "Error desconocido")
            });
          } finally {
            this.guardandoDevolucion = false;
          }
        }
      });
    },

    async rechazarDevolucion(material) {
      // Abrir modal para solicitar motivo del rechazo (reutilizar el mismo modal)
      this.materialParaRechazar = material;
      this.motivoRechazo = '';
      this.esRechazoDevolucion = true;
      this.showMotivoRechazoModal = true;
    },

    async aceptarMaterial(material) {
            this.showConfirmDialog({
        title: 'Aceptar Material',
        message: '¿Está seguro de que desea aceptar este material asignado por el jefe de taller? Al aceptarlo pasará a estado entregado.',
        buttonText: 'Aceptar',
        buttonType: 'success',
        action: async () => {
          try {
            this.loading = true;

            if (material.activo) {
              await axios.put(
                `${this.apiUrl}/albaran-activo-materials/${material.id}`,
                {
                  data: {
                    estado: "entregado",
                    responsable_actual: "jefe_equipo",
                    fecha_accion: new Date().toISOString(),
                    usuario_accion: localStorage.getItem('userId')
                  }
                },
                {
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                  }
                }
              );
            } else {
              // Para consumibles, actualizar el registro en albaran_activo_materials
              await axios.put(
                `${this.apiUrl}/albaran-activo-materials/${material.id}`,
                {
                  data: {
                    estado: "entregado",
                    responsable_actual: "jefe_equipo",
                    fecha_accion: new Date().toISOString(),
                    usuario_accion: localStorage.getItem('userId')
                  }
                },
                  {
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                  }
                );
            }

            await this.loadAlbaran();
            
            // Actualizar el estado del albarán basándose en los estados de los materiales
            await this.actualizarEstadoAlbaran();

            this.$notify({
              type: "success",
              message: "Material aceptado correctamente por el jefe de equipo"
            });

          } catch (error) {
            console.error("Error al aceptar el material:", error);
            this.$notify({
              type: "danger",
              message: "Error al aceptar el material: " + (error.message || "Error desconocido")
            });
          } finally {
            this.loading = false;
          }
        }
      });
    },

    async rechazarMaterial(material) {
      // Abrir modal para solicitar motivo del rechazo
      this.materialParaRechazar = material;
      this.motivoRechazo = '';
      this.esRechazoDevolucion = false;
      this.showMotivoRechazoModal = true;
    },
    
    cerrarModalMotivoRechazo() {
      this.showMotivoRechazoModal = false;
      this.materialParaRechazar = null;
      this.motivoRechazo = '';
      this.esRechazoDevolucion = false;
    },
    
    async confirmarRechazoMaterial() {
      if (!this.materialParaRechazar || !this.motivoRechazo || !this.motivoRechazo.trim()) {
        this.$notify({
          type: "warning",
          message: "Por favor, ingrese un motivo para el rechazo"
        });
        return;
      }

      try {
        this.rechazandoMaterial = true;
            this.loading = true;

        const material = this.materialParaRechazar;
        const motivo = this.motivoRechazo.trim();
        
        // Preparar observaciones con el motivo del rechazo
        const tipoRechazo = this.esRechazoDevolucion ? 'DEVOLUCIÓN RECHAZADA' : 'MATERIAL RECHAZADO';
        const observaciones = `${tipoRechazo} - Motivo: ${motivo}\nFecha: ${new Date().toLocaleString('es-ES')}\nUsuario: ${localStorage.getItem('userName') || 'Usuario'}`;

        // Preparar el payload base
        const payload = {
          estado: "rechazado",
          fecha_accion: new Date().toISOString(),
          usuario_accion: localStorage.getItem('userId'),
          observaciones: observaciones
        };
        
        // Solo cambiar responsable_actual si NO es un rechazo de devolución
        if (!this.esRechazoDevolucion) {
          payload.responsable_actual = "jefe_equipo";
        }

            if (material.activo) {
              await axios.put(
                `${this.apiUrl}/albaran-activo-materials/${material.id}`,
                {
              data: payload
                },
                {
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                  }
                }
              );
            } else {
              // Para consumibles, actualizar el registro en albaran_activo_materials
              await axios.put(
                `${this.apiUrl}/albaran-activo-materials/${material.id}`,
                {
              data: payload
                },
                  {
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                  }
                );
            }

            await this.loadAlbaran();
            
            // Actualizar el estado del albarán basándose en los estados de los materiales
            await this.actualizarEstadoAlbaran();

            this.$notify({
              type: "success",
          message: this.esRechazoDevolucion ? "Devolución rechazada correctamente" : "Material rechazado correctamente por el jefe de equipo"
            });

        // Cerrar el modal
        this.cerrarModalMotivoRechazo();

          } catch (error) {
            console.error("Error al rechazar el material:", error);
            this.$notify({
              type: "danger",
              message: "Error al rechazar el material: " + (error.message || "Error desconocido")
            });
          } finally {
        this.rechazandoMaterial = false;
            this.loading = false;
          }
    },



    async guardarEdicionAlbaran() {
      try {
        this.loading = true;

        // Preparar payload con el formato correcto para Strapi v4 (envolver en objeto data)
        const payload = {
          data: {
              titulo: this.editAlbaran.titulo,
              estado: this.editAlbaran.estado,
              fecha_entrega: this.editAlbaran.fecha_entrega,
            fecha_devolucion: this.editAlbaran.fecha_devolucion || null,
            descripcion: this.editAlbaran.descripcion || '',
            observaciones: this.editAlbaran.observaciones || ''
          }
        };

        await axios.put(
          `${this.apiUrl}/albaran-activos/${this.albaran.id}`,
          payload,
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );

        await this.loadAlbaran();
        
        this.$notify({
          type: "success",
          message: "Albarán actualizado correctamente"
        });

        this.cerrarModalEditAlbaran();

      } catch (error) {
        console.error("Error actualizando albarán:", error);
        this.$notify({
          type: "danger",
          message: "Error al actualizar el albarán: " + (error.message || "Error desconocido")
        });
      } finally {
        this.loading = false;
      }
    },

    // Nuevos métodos para gestión del ciclo de vida
    async openLinkKeikakuModal() {
      console.log('🔵 [MODAL] openLinkKeikakuModal llamado');
      
      // Verificar si es un clon
      const albaranOriginalId = this.albaran?.albaran_original_id || this.albaran?.attributes?.albaran_original_id;
      const albaranClonId = this.albaran?.id || this.albaran?.attributes?.id;
      
      console.log('🔵 [MODAL] Albarán actual:', {
        id: albaranClonId,
        numero: this.albaran?.numero || this.albaran?.attributes?.numero,
        tiene_albaran_original_id: !!albaranOriginalId,
        albaran_original_id: albaranOriginalId
      });
      
      // Si es un clon, verificar si el albarán original tiene tarea_keikaku_data
      if (albaranOriginalId) {
        console.log('🔵 [MODAL] Es un clon, verificando si el original tiene tarea_keikaku_data...');
        try {
          // Obtener el albarán original para verificar si tiene tarea_keikaku_data
          const albaranOriginalResponse = await axios.get(
            `${this.apiUrl}/albaran-activos/${albaranOriginalId}?populate=*`,
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              }
            }
          );
          
          const albaranOriginal = albaranOriginalResponse.data.data || albaranOriginalResponse.data;
          const tareaKeikakuData = albaranOriginal.attributes?.tarea_keikaku_data || albaranOriginal.tarea_keikaku_data;
          
          console.log('🔵 [MODAL] Albarán original:', {
            id: albaranOriginal.id || albaranOriginal.attributes?.id,
            numero: albaranOriginal.numero || albaranOriginal.attributes?.numero,
            tiene_tarea_keikaku_data: !!tareaKeikakuData
          });
          
          // Si el original tiene tarea_keikaku_data, ejecutar automáticamente
          if (tareaKeikakuData) {
            console.log('🔵 [MODAL] El original tiene tarea_keikaku_data, ejecutando enlace automático...');
            this.linkingKeikaku = true;
            
            try {
              await this.asociarTareaYParteAlClonDesdeOriginal(albaranClonId, albaranOriginalId);
              
              await this.loadAlbaran();
              
              this.$notify({
                type: "success",
                message: "Tarea enlazada con éxito"
              });
              
              console.log('🔵 [MODAL] Enlace automático completado exitosamente');
              this.linkingKeikaku = false;
              return; // No abrir el modal
            } catch (linkError) {
              console.error("❌ [MODAL] Error enlazando tarea automáticamente:", linkError);
              this.linkingKeikaku = false;
              
              // Manejar errores específicos
              let errorMessage = "Error al enlazar la tarea";
              
              if (linkError.message === 'NO_TAREAS_PROYECTO') {
                const fechaFormateada = this.formatDate(linkError.fechaEntrega);
                const proyectoDisplay = linkError.nombreProyecto || linkError.idProyecto;
                errorMessage = `No existe ninguna tarea de Keikaku para el proyecto "${proyectoDisplay}" y la fecha ${fechaFormateada}. Por favor, verifica que exista una tarea para este proyecto y día, o enlaza la tarea manualmente.`;
              } else if (linkError.message === 'NO_TAREA_FECHA') {
                const fechaFormateada = this.formatDate(linkError.fechaEntrega);
                const proyectoDisplay = linkError.nombreProyecto || linkError.idProyecto;
                errorMessage = `No existe ninguna tarea de Keikaku para el proyecto "${proyectoDisplay}" con fecha de inicio ${fechaFormateada}. Se encontraron ${linkError.tareasEncontradas} tarea(s) para el proyecto, pero ninguna coincide con la fecha de entrega del albarán (${fechaFormateada}). Por favor, verifica la fecha o enlaza la tarea manualmente.`;
              } else if (linkError.message) {
                errorMessage = linkError.message;
              } else if (linkError.response?.data?.error?.message) {
                errorMessage = linkError.response.data.error.message;
              }
              
              this.$notify({
                type: "danger",
                message: errorMessage,
                duration: 8000
              });
              
              // Abrir el modal como fallback para entrada manual
              console.log('🔵 [MODAL] Error al enlazar automáticamente, abriendo modal para entrada manual');
      this.tareaKeikakuId = '';
      this.showLinkKeikakuModal = true;
              return;
            }
          } else {
            // Si el original NO tiene tarea_keikaku_data, mostrar el modal
            console.log('🔵 [MODAL] El original NO tiene tarea_keikaku_data, abriendo modal para entrada manual');
          }
        } catch (error) {
          console.error("❌ [MODAL] Error verificando albarán original:", error);
          this.linkingKeikaku = false;
          
          // Mostrar error al usuario
          this.$notify({
            type: "danger",
            message: "Error al verificar el albarán original: " + (error.response?.data?.error?.message || error.message || "Error desconocido")
          });
          
          // Si hay error, mostrar el modal como fallback
          console.log('🔵 [MODAL] Error al verificar original, abriendo modal como fallback');
        }
      }
      
      // Abrir el modal para introducir el ID manualmente
      // Esto aplica para:
      // - Albaranes manuales (ST-XXXX) sin tarea
      // - Clones de albaranes manuales sin tarea (CL1ST-0001)
      // - Cualquier otro caso donde no se pueda enlazar automáticamente
      console.log('🔵 [MODAL] Abriendo modal para entrada manual');
      this.tareaKeikakuId = '';
      this.showLinkKeikakuModal = true;
      console.log('🔵 [MODAL] Modal abierto, showLinkKeikakuModal:', this.showLinkKeikakuModal);
    },

    cerrarModalLinkKeikaku() {
      this.showLinkKeikakuModal = false;
      this.tareaKeikakuId = '';
    },


    openTareaKeikakuModal() {
      this.showTareaKeikakuModal = true;
    },

    cerrarModalTareaKeikaku() {
      this.showTareaKeikakuModal = false;
    },

    formatTareaKeikakuKey(key) {
      // Formatear las claves para mostrarlas de forma legible
      const keyMap = {
        'IdTarea': 'ID Tarea',
        'IdProyecto': 'ID Proyecto',
        'IdEstado': 'ID Estado',
        'Titulo': 'Título',
        'Descrip': 'Descripción',
        'Observaciones': 'Observaciones',
        'IdSupervisor': 'ID Supervisor',
        'IdResponsable': 'ID Responsable',
        'IdEquipo': 'ID Equipo',
        'IdArea': 'ID Área',
        'IdDepartamento': 'ID Departamento',
        'IdComplejidad': 'ID Complejidad',
        'FechaPrevInicio': 'Fecha Prevista Inicio',
        'FechaPrevFin': 'Fecha Prevista Fin',
        'HorasPrev': 'Horas Previstas',
        'FechaInicio': 'Fecha Inicio',
        'FechaFin': 'Fecha Fin',
        'FechaCierre': 'Fecha Cierre',
        'IdPrioridad': 'ID Prioridad',
        'IdClasificacion': 'ID Clasificación',
        'IdEmpresa': 'ID Empresa',
        'IdTipoTarea': 'ID Tipo Tarea',
        'HoraInicio': 'Hora Inicio',
        'IdVehiculo': 'ID Vehículo',
        'IdLineaNegocio': 'ID Línea Negocio',
        'Avisos': 'Avisos',
        'PrecioEstimado': 'Precio Estimado',
        'TipoFacturacion': 'Tipo Facturación',
        'Importe_Facturar': 'Importe a Facturar',
        'Albaran': 'Albarán',
        'IdDocOferta': 'ID Documento Oferta',
        'TipoLinea': 'Tipo Línea',
        'IdLinea': 'ID Línea',
        'Personas_Oferta': 'Personas Oferta',
        'Jornadas_Oferta': 'Jornadas Oferta',
        'Horas_Oferta': 'Horas Oferta',
        'Precio_Oferta': 'Precio Oferta',
        'IdParte': 'ID Parte',
        'EspacioConfinado': 'Espacio Confinado',
        'IdDoc': 'ID Documento',
        'InsertUpdate': 'Insertar/Actualizar',
        'Usuario': 'Usuario',
        'FechaInsertUpdate': 'Fecha Insertar/Actualizar',
        'ETC': 'ETC',
        'Etiquetas': 'Etiquetas',
        'IdEmpleadoCreacion': 'ID Empleado Creación',
        'IdEmpleadoModificacion': 'ID Empleado Modificación',
        'FechaCreacion': 'Fecha Creación',
        'PersonasEstimadas': 'Personas Estimadas',
        'PersonasAsignadas': 'Personas Asignadas',
        'IdTipoJornada': 'ID Tipo Jornada',
        'TipoTrabajo': 'Tipo Trabajo',
        'Materiales': 'Materiales',
        'Planificador': 'Planificador',
        'IdCliente': 'ID Cliente',
        'IdPlanta': 'ID Planta',
        'IdContacto': 'ID Contacto',
        'IdEstado_flxtext': 'Estado',
        'IdResponsable_flxtext': 'Responsable',
        'IdDepartamento_flxtext': 'Departamento',
        'Value': 'Valor',
        'Text': 'Texto',
        'TextVa': 'Texto Valor'
      };
      
      return keyMap[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim();
    },

    formatTareaKeikakuValue(value) {
      // Formatear valores para mostrarlos de forma legible
      if (value === null || value === undefined) {
        return '-';
      }
      
      if (typeof value === 'boolean') {
        return value ? 'Sí' : 'No';
      }
      
      if (typeof value === 'object') {
        // Si es un objeto con Value y Text, mostrar ambos
        if (value.Value !== undefined || value.Text !== undefined || value.TextVa !== undefined) {
          const parts = [];
          if (value.Value !== undefined && value.Value !== null) {
            parts.push(`Valor: ${value.Value}`);
          }
          if (value.Text !== undefined && value.Text !== null) {
            parts.push(`Texto: ${value.Text}`);
          }
          if (value.TextVa !== undefined && value.TextVa !== null) {
            parts.push(`Texto Valor: ${value.TextVa}`);
          }
          return parts.join(' | ') || '-';
        }
        return JSON.stringify(value, null, 2);
      }
      
      // Si es una fecha, formatearla
      if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
        return this.formatDate(value);
      }
      
      return value;
    },

    openParteTrabajoModal() {
      this.showParteTrabajoModal = true;
    },

    cerrarModalParteTrabajo() {
      this.showParteTrabajoModal = false;
    },

    formatParteTrabajoKey(key) {
      // Formatear las claves para mostrarlas de forma legible
      const keyMap = {
        'IdParte': 'ID Parte',
        'NumParte': 'Número Parte',
        'IdTaller': 'ID Taller',
        'IdCliente': 'ID Cliente',
        'IdContacto': 'ID Contacto',
        'IdArticulo': 'ID Artículo',
        'Descrip': 'Descripción',
        'FechaParte': 'Fecha Parte',
        'FechaInicio': 'Fecha Inicio',
        'FechaFin': 'Fecha Fin',
        'IdEstado': 'ID Estado',
        'IdEmpleado': 'ID Empleado',
        'IdProyecto': 'ID Proyecto',
        'Observaciones': 'Observaciones',
        'IdCliente_flxtext': 'Cliente',
        'IdContacto_flxtext': 'Contacto',
        'IdArticulo_flxtext': 'Artículo',
        'IdEstado_flxtext': 'Estado',
        'IdEmpleado_flxtext': 'Empleado',
        'IdProyecto_flxtext': 'Proyecto',
        'p_TareaDescrip': 'Descripción Tarea',
        'p_TareaTitulo': 'Título Tarea',
        'p_IdPlanta': 'ID Planta',
        'p_IdPlanta_flxtext': 'Planta'
      };
      
      return keyMap[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim();
    },

    formatParteTrabajoValue(value) {
      // Formatear valores para mostrarlos de forma legible
      if (value === null || value === undefined) {
        return '-';
      }
      
      if (typeof value === 'boolean') {
        return value ? 'Sí' : 'No';
      }
      
      if (typeof value === 'object') {
        return JSON.stringify(value, null, 2);
      }
      
      // Si es una fecha, formatearla
      if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
        return this.formatDate(value);
      }
      
      return value;
    },

    async enlazarTareaKeikaku() {
      // Esta función solo se usa cuando NO es un clon (modal manual)
      // Si es un clon, la acción se ejecuta directamente en openLinkKeikakuModal
      
      if (!this.tareaKeikakuId.trim()) {
        this.$notify({
          type: "warning",
          message: "Por favor, introduce un ID de tarea válido"
        });
        return;
      }

      try {
        this.linkingKeikaku = true;

        await axios.put(
          `${this.apiUrl}/albaran-activos/${this.albaran.id}`,
          {
            // Strapi v4 requiere que los updates vayan dentro de { data: { ... } }
            data: {
              tarea_keikaku_id: this.tareaKeikakuId.trim()
            }
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );

        await this.loadAlbaran();
        
        this.$notify({
          type: "success",
          message: "Tarea enlazada con éxito"
        });

        this.cerrarModalLinkKeikaku();

      } catch (error) {
        console.error("Error enlazando tarea:", error);
        this.$notify({
          type: "danger",
          message: "Error al enlazar la tarea: " + (error.response?.data?.error?.message || "Error desconocido")
        });
      } finally {
        this.linkingKeikaku = false;
      }
    },

    async showClones() {
      try {
        this.loadingClones = true;
        this.showClonesModal = true;

        const response = await axios.get(
          `${this.apiUrl}/albaran-activos?filters[albaran_original_id][$eq]=${this.albaran.id}&populate=*`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );

        this.clones = response.data.data || [];

      } catch (error) {
        console.error("Error cargando clones:", error);
        this.$notify({
          type: "danger",
          message: "Error al cargar los clones: " + (error.response?.data?.error?.message || "Error desconocido")
        });
      } finally {
        this.loadingClones = false;
      }
    },

    cerrarModalClones() {
      this.showClonesModal = false;
      this.clones = [];
    },

    verClon(clonId) {
      this.$router.push({
        name: "View Albaran",
        params: { id: clonId }
      });
    },

    async cerrarAlbaran() {
      this.showConfirmDialog({
        title: 'Cerrar Albarán',
        message: '¿Está seguro de que desea cerrar este albarán? Se puede cerrar sin materiales o con incidencias pendientes; en ese caso quedará indicado como "Cerrado con incidencias".',
        buttonText: 'Cerrar',
        buttonType: 'success',
        action: async () => {
          try {
            this.loading = true;

            // El color se determinará automáticamente según es_facturable cuando se muestre
            // No necesitamos guardar el color, solo el estado 'cerrado'
            await axios.put(
              `${this.apiUrl}/albaran-activos/${this.albaran.id}`,
              {
                data: {
                estado: 'cerrado',
                fecha_cierre: new Date().toISOString()
                }
              },
              {
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
              }
            );

            await this.loadAlbaran();
            
            // Notificar que el albarán fue actualizado
            this.notificarActualizacionAlbaran('cerrado');
            
            this.$notify({
              type: "success",
              message: "Albarán cerrado correctamente"
            });

          } catch (error) {
            console.error("Error cerrando albarán:", error);
            this.$notify({
              type: "danger",
              message: "Error al cerrar el albarán: " + (error.response?.data?.error?.message || "Error desconocido")
            });
          } finally {
            this.loading = false;
          }
        }
      });
    },
    
    // Métodos para el modal unificado de añadir material
    abrirModalAddMaterial() {
      this.showAddMaterialModal = true;
      this.resetNuevoMaterial();
      
      // Cargar consumibles del ERP
      this.cargarConsumiblesERP();
      
      // Hacer focus en el input de búsqueda después de que el modal se abra
      this.$nextTick(() => {
        if (this.$refs.searchConsumibleInput) {
          this.$refs.searchConsumibleInput.focus();
        }
      });
    },
    
    cerrarModalAddMaterial() {
      this.showAddMaterialModal = false;
      this.resetNuevoMaterial();
    },
    
    resetNuevoMaterial() {
      this.nuevoMaterial = {
        cantidad: ''
      };
      // Limpiar también las variables del ERP
      this.searchConsumible = '';
      this.selectedConsumibleERP = null;
      this.filteredConsumibles = [];
      this.showConsumiblesList = false;
    },
    
    async guardarNuevoMaterial() {
      if (!this.assertAlbaranEditable()) return;
      if (!this.isMaterialValidoERP) {
        this.$notify({
          type: "warning",
          message: "Por favor, selecciona un consumible del ERP y especifica la cantidad"
        });
        return;
      }
      
      try {
        this.guardandoMaterial = true;
        
        // Preparar payload para consumible del ERP
        const payload = {
          tipo_material: 'consumible',
          idArticulo: this.selectedConsumibleERP.idArticulo,
          descripcion: this.selectedConsumibleERP.descripcion,
          cantidad: parseInt(this.nuevoMaterial.cantidad),
          material_keikaku_id: this.selectedConsumibleERP.idArticulo // Usar ID del ERP como keikaku_id
        };
        
        console.log('📦 [MATERIAL] Añadiendo material:', payload);
        
        // Llamada a la API para añadir el material
        await axios.post(
          `${this.apiUrl}/albaran-add/${this.albaran.id}`,
          payload,
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        // Recargar el albarán
        await this.loadAlbaran();
        
        // Forzar actualización de la vista
        this.$forceUpdate();
        
        // Log para verificar los datos que llegan del backend
        console.log('📋 [MATERIAL] Materiales después de recargar:', this.getMateriales);
        
        this.$notify({
          type: "success",
          message: "Consumible añadido correctamente al albarán"
        });
        
        this.cerrarModalAddMaterial();
        
      } catch (error) {
        console.error("Error añadiendo material:", error);
        this.$notify({
          type: "danger",
          message: `Error al añadir el material: ${error.response?.data?.error?.message || error.message}`
        });
      } finally {
        this.guardandoMaterial = false;
      }
    },

    // Métodos para el buscador del ERP
    async buscarConsumiblesERP() {
      if (!this.searchConsumible || this.searchConsumible.length < 2) {
        this.filteredConsumibles = [];
        return;
      }

      try {
        this.loadingConsumibles = true;
        
        // Si aún no hemos cargado los consumibles o necesitamos refrescar
        if (this.consumiblesERP.length === 0) {
          this.consumiblesERP = await erpService.getConsumibles();
        }

        // Filtrar localmente por el término de búsqueda (descripción e ID)
        const searchLower = this.searchConsumible.toLowerCase();
        this.filteredConsumibles = this.consumiblesERP.filter(consumible => 
          (consumible.descripcion && consumible.descripcion.toLowerCase().includes(searchLower)) ||
          (consumible.idArticulo && consumible.idArticulo.toLowerCase().includes(searchLower))
        ).slice(0, 20); // Limitar a 20 resultados para performance

      } catch (error) {
        console.error('Error buscando consumibles del ERP:', error);
        this.$notify({
          type: "danger",
          message: "Error al buscar consumibles en el ERP: " + error.message
        });
        this.filteredConsumibles = [];
      } finally {
        this.loadingConsumibles = false;
      }
    },

    seleccionarConsumibleERP(consumible) {
      this.selectedConsumibleERP = consumible;
      this.searchConsumible = consumible.descripcion;
      this.showConsumiblesList = false;
      
      // Enfocar en el campo de cantidad
      this.$nextTick(() => {
        const cantidadInput = this.$el.querySelector('input[type="number"]');
        if (cantidadInput) {
          cantidadInput.focus();
        }
      });
    },

    ocultarListaConsumibles() {
      // Usar setTimeout para permitir que el click en un elemento de la lista se procese primero
      setTimeout(() => {
        this.showConsumiblesList = false;
      }, 200);
    },

    // Método para cargar consumibles del ERP al abrir el modal
    async cargarConsumiblesERP() {
      if (this.consumiblesERP.length === 0) {
        try {
          this.loadingConsumibles = true;
          this.consumiblesERP = await erpService.getConsumibles();
        } catch (error) {
          console.error('Error cargando consumibles del ERP:', error);
          this.$notify({
            type: "warning",
            message: "No se pudieron cargar los consumibles del ERP, pero puedes añadir manualmente"
          });
        } finally {
          this.loadingConsumibles = false;
        }
      }
    },

    // Métodos para el componente Kanban
    async asignarJefeEquipoMaterial(material) {
      // Abrir el modal de asignación de usuario
      this.openUserAssignModal();
    },

    async solicitarDevolucion(material) {
      this.showConfirmDialog({
        title: 'Solicitar Devolución',
        message: '¿Está seguro de que desea solicitar la devolución de este material?',
        buttonText: 'Solicitar',
        buttonType: 'info',
        action: async () => {
          try {
            this.loading = true;
            await this.updateMaterialState(material, 'pendiente_aceptar_devolucion');
            this.$notify({
              type: "success",
              message: "Solicitud de devolución enviada correctamente"
            });
          } catch (error) {
            console.error('Error al solicitar devolución:', error);
            this.$notify({
              type: "error",
              message: "Error al solicitar la devolución del material"
            });
          } finally {
            this.loading = false;
          }
        }
      });
    },


    async changeMaterialState({ material, newEstado }) {
      try {
        this.loading = true;
        
        // Si el nuevo estado es "devuelto", usar la misma lógica que aceptarDevolucion
        // pero aceptando directamente la cantidad completa (sin preguntar)
        if (newEstado === 'devuelto') {
          // Para consumibles, aceptar directamente con la cantidad completa
          if (!material.activo && !material.es_saca && material.tipo_material !== 'saca') {
            const cantidad = material.cantidad || this.getMaterialCantidad(material) || 0;
            
            await axios.put(
              `${this.apiUrl}/albaran-activo-materials/${material.id}`,
              {
                data: {
                  estado: "devuelto",
                  cantidad: parseFloat(cantidad),
                  responsable_actual: "jefe_taller",
                  fecha_accion: new Date().toISOString(),
                  usuario_accion: localStorage.getItem('userId')
                }
              },
              {
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
              }
            );
          } else {
            // Para activos o sacas, procesar directamente
            await axios.put(
              `${this.apiUrl}/albaran-activo-materials/${material.id}`,
              {
                data: {
                  estado: "devuelto",
                  responsable_actual: "jefe_taller",
                  fecha_accion: new Date().toISOString(),
                  usuario_accion: localStorage.getItem('userId')
                }
              },
              {
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
              }
            );
          }
          
          await this.loadAlbaran();
          
          // Actualizar el estado del albarán basándose en los estados de los materiales
          await this.actualizarEstadoAlbaran();
          
          this.$notify({
            type: "success",
            message: "Devolución aceptada correctamente"
          });
        } else {
          // Para otros estados, usar el método normal
          // Normalizar: "asignado" se convierte a "entregado" (unificación de estados)
          const estadoFinal = newEstado === 'asignado' ? 'entregado' : newEstado;
          
          console.log('🔄 Cambiando estado del material:', {
            materialId: material.id,
            estadoActual: material.estado,
            nuevoEstado: estadoFinal,
            newEstadoRecibido: newEstado
          });
          
          // Actualizar el estado del material
          const payload = {
            data: {
              estado: estadoFinal,
              observaciones: material.observaciones || '',
              responsable_actual: material.responsable_actual || 'jefe_taller',
              fecha_accion: new Date().toISOString(),
              usuario_accion: localStorage.getItem('userId')
            }
          };

          console.log('📤 Payload enviado:', payload);

          const response = await axios.put(
            `${this.apiUrl}/albaran-activo-materials/${material.id}`,
            payload,
            {
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              }
            }
          );

          console.log('✅ Respuesta del servidor:', response.data);

          // Si la actualización fue exitosa, recargar el albarán
          await this.loadAlbaran();
          
          // Actualizar el estado del albarán basándose en los estados de los materiales
          await this.actualizarEstadoAlbaran();

          // Mostrar éxito después de confirmar que todo se actualizó
          this.$notify({
            type: "success",
            message: `Material movido a estado: ${getEstadoTextoUtil(estadoFinal)}`
          });
        }
      } catch (error) {
        console.error('❌ Error al cambiar estado del material:', error);
        console.error('❌ Detalles del error:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
          statusText: error.response?.statusText
        });
        
        const errorMessage = error.response?.data?.error?.message || 
                            error.response?.data?.message || 
                            error.message || 
                            "Error al cambiar el estado del material";
        
        this.$notify({
          type: "error",
          message: errorMessage,
          duration: 5000
        });
      } finally {
        this.loading = false;
      }
    },

    // Obtener el estado del albarán desde el backend (fuente de verdad)
    getEstadoAlbaran() {
      if (!this.albaran) return null;
      
      const estadoBackend = this.albaran.estado || this.albaran.attributes?.estado;
      
      // Estados manuales que NO deben sobreescribirse por recálculo en frontend
      if (estadoBackend === 'cerrado' || estadoBackend === 'cerrado_sin_tarea' || estadoBackend === 'material_abandonado') {
        return estadoBackend;
      }
      
      // Calcular el estado en tiempo real basándose en los materiales actuales
      // Esto asegura que la UI muestre el estado correcto incluso si el backend está desactualizado
      const estadoCalculado = this.calcularEstadoAlbaran();
      
      console.log('🔍 [GETESTADO] Estado Backend:', estadoBackend, '| Estado Calculado:', estadoCalculado);
      
      // Priorizar el estado calculado sobre el del backend (excepto si está cerrado)
      // Esto corrige el problema de mostrar "entregado" cuando hay materiales pendientes
      if (estadoCalculado) {
        if (estadoCalculado !== estadoBackend) {
          console.log('⚠️ [GETESTADO] Usando estado calculado:', estadoCalculado, '(Backend tenía:', estadoBackend, ')');
        }
        return estadoCalculado;
      }
      
      // Fallback al estado del backend si no se puede calcular
      return estadoBackend || null;
    },

    // Calcular el estado del albarán según las reglas de negocio
    // Se usa solo para actualizar el backend cuando hay cambios
    calcularEstadoAlbaran() {
      if (!this.albaran) return null;
      
      // Estados manuales: mantener estado (no recalcular desde materiales)
      const estadoActual = this.albaran.estado || this.albaran.attributes?.estado;
      if (estadoActual === 'cerrado' || estadoActual === 'cerrado_sin_tarea' || estadoActual === 'material_abandonado') {
        return estadoActual;
      }
      
      // Verificar si tiene jefe de equipo asignado
      const tieneJefeEquipo = this.albaran.users_permissions_user || this.albaran.attributes?.users_permissions_user;
      
      // Si no tiene jefe de equipo asignado → pendiente_asignar
      if (!tieneJefeEquipo) {
        console.log('🔍 [ESTADO] No tiene jefe de equipo asignado → pendiente_asignar');
        return 'pendiente_asignar';
      }
      
      const materiales = this.getMateriales;
      if (!materiales || materiales.length === 0) {
        // Si no hay materiales pero tiene jefe, mantener el estado actual o asignado
        return estadoActual || 'asignado';
      }

      // Obtener todos los estados de los materiales
      // IMPORTANTE: Incluir materiales sin estado como pendientes de aceptar
      const estadosMateriales = materiales.map(m => {
        // Manejar diferentes estructuras de datos
        const materialData = m.attributes || m;
        let estado = materialData.estado;
        
        // Si no tiene estado o es null/undefined, considerarlo como pendiente_aceptar
        if (!estado || estado === null || estado === undefined || estado === '') {
          console.log('🔍 [ESTADO] Material sin estado detectado:', {
            id: materialData.id,
            nombre: materialData.activo?.nombre || materialData.descripcion || materialData.idArticulo_flxtext,
            estado: estado || 'sin estado → pendiente_aceptar'
          });
          return 'pendiente_aceptar';
        }
        // Normalizar: "asignado" a nivel de material se convierte a "entregado" (unificación)
        if (estado === 'asignado') {
          estado = 'entregado';
        }
        return estado;
      });

      if (estadosMateriales.length === 0) {
        return estadoActual || 'asignado';
      }

      console.log('🔍 [ESTADO] Materiales y sus estados:', materiales.map(m => {
        const materialData = m.attributes || m;
        return {
          id: materialData.id,
          nombre: materialData.activo?.nombre || materialData.descripcion || materialData.idArticulo_flxtext,
          estado: materialData.estado || 'sin estado (pendiente_aceptar)',
          estadoRaw: materialData.estado,
          tieneAttributes: !!m.attributes
        };
      }));
      
      console.log('🔍 [ESTADO] Estados procesados:', estadosMateriales);

      // PRIORIDAD 0 (MÁS ALTA): Verificar si hay algún material con problemas (rechazado, no_devuelto, incidencia)
      const tieneProblemas = estadosMateriales.some(estado => 
        estado === 'rechazado' || estado === 'no_devuelto' || estado === 'incidencia'
      );
      if (tieneProblemas) {
        console.log('🔍 [ESTADO] Hay materiales con problemas (rechazado, no_devuelto o incidencia) → incidencia');
        return 'incidencia';
      }

      // Filtrar estados problemáticos para el resto del cálculo (excluir rechazados, no_devueltos, incidencias)
      const estadosMaterialesFiltrados = estadosMateriales.filter(estado => 
        estado !== 'rechazado' && estado !== 'no_devuelto' && estado !== 'incidencia'
      );

      if (estadosMaterialesFiltrados.length === 0) {
        return estadoActual || 'asignado';
      }

      // PRIORIDAD 1: Verificar si hay algún material en estado pendiente_aceptar_devolucion
      const tienePendienteDevolucion = estadosMaterialesFiltrados.some(estado => estado === 'pendiente_aceptar_devolucion');
      if (tienePendienteDevolucion) {
        console.log('🔍 [ESTADO] Hay materiales pendientes de aceptar devolución → pendiente_aceptar_devolucion');
        return 'pendiente_aceptar_devolucion';
      }

      // PRIORIDAD 2: Verificar si hay algún material en estado pendiente_aceptar (incluye sin estado)
      const tienePendienteAceptar = estadosMaterialesFiltrados.some(estado => estado === 'pendiente_aceptar');
      if (tienePendienteAceptar) {
        console.log('🔍 [ESTADO] Hay materiales pendientes de aceptar → asignado');
        return 'asignado';
      }

      // PRIORIDAD 3: Verificar si todos los materiales están devueltos
      const todosDevueltos = estadosMaterialesFiltrados.every(estado => estado === 'devuelto');
      if (todosDevueltos) {
        console.log('🔍 [ESTADO] Todos los materiales están devueltos → devuelto');
        return 'devuelto';
      }

      // PRIORIDAD 4: Si hay al menos un material entregado (aunque no todos) → entregado
      // Esto cubre el caso de materiales entregados + materiales devueltos
      // SOLO si NO hay ningún material pendiente de aceptar
      const tieneEntregados = estadosMaterialesFiltrados.some(estado => estado === 'entregado');
      if (tieneEntregados && !tienePendienteAceptar) {
        console.log('🔍 [ESTADO] Hay materiales entregados (aunque no todos) → entregado');
        return 'entregado';
      }

      // Por defecto, si tiene jefe asignado → asignado
      console.log('🔍 [ESTADO] Estado por defecto (tiene jefe) → asignado');
      return 'asignado';
    },

    // Actualizar el estado del albarán si es necesario
    async actualizarEstadoAlbaran() {
      if (!this.albaran) return;

      const estadoActual = this.albaran.estado || this.albaran.attributes?.estado;
      
      console.log('🔄 [ACTUALIZAR ESTADO] Estado actual del backend:', estadoActual);
      
      // No actualizar si el albarán está en estado manual (no sobrescribir)
      if (estadoActual === 'cerrado' || estadoActual === 'cerrado_sin_tarea' || estadoActual === 'material_abandonado') {
        console.log('🔄 [ACTUALIZAR ESTADO] Albarán en estado manual (cerrado/material_abandonado), no se actualiza');
        return;
      }

      const nuevoEstado = this.calcularEstadoAlbaran();
      if (!nuevoEstado) {
        console.log('🔄 [ACTUALIZAR ESTADO] No se pudo calcular el nuevo estado');
        return;
      }
      
      console.log('🔄 [ACTUALIZAR ESTADO] Nuevo estado calculado:', nuevoEstado);
      
      // SIEMPRE actualizar si hay materiales pendientes y el estado es "entregado"
      // Esto corrige el caso donde el backend tiene "entregado" pero hay materiales pendientes
      const materiales = this.getMateriales;
      const tieneMaterialesPendientes = materiales && materiales.some(m => {
        const materialData = m.attributes || m;
        const estado = materialData.estado;
        return !estado || estado === null || estado === undefined || estado === 'pendiente_aceptar';
      });
      
      if (tieneMaterialesPendientes && estadoActual === 'entregado') {
        console.log('⚠️ [ACTUALIZAR ESTADO] CORRECCIÓN: Backend tiene "entregado" pero hay materiales pendientes → actualizando a:', nuevoEstado);
        // Forzar actualización aunque el nuevo estado sea igual al actual
        // porque el actual está incorrecto
      }
      
      // Actualizar si el estado ha cambiado O si hay materiales pendientes y el estado es "entregado"
      const necesitaActualizar = nuevoEstado !== estadoActual || 
        (tieneMaterialesPendientes && estadoActual === 'entregado');
      
      if (necesitaActualizar) {
        try {
          console.log(`🔄 [ACTUALIZAR ESTADO] Actualizando estado: ${estadoActual} → ${nuevoEstado}`);
          
          await axios.put(
            `${this.apiUrl}/albaran-activos/${this.albaran.id}`,
            {
              data: {
                estado: nuevoEstado
              }
            },
            {
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              }
            }
          );
          
          // Actualizar el estado localmente para reflejar el cambio inmediatamente
          this.albaran.estado = nuevoEstado;
          if (this.albaran.attributes) {
            this.albaran.attributes.estado = nuevoEstado;
          }
          
          console.log(`✅ [ACTUALIZAR ESTADO] Estado del albarán actualizado correctamente: ${estadoActual} → ${nuevoEstado}`);
        } catch (error) {
          console.error('Error al actualizar el estado del albarán:', error);
          // No mostrar error al usuario, es una actualización automática
        }
      }
    },

    async updateMaterialState(material, newEstado) {
      if (!this.assertAlbaranEditable()) return;
      const payload = {
        data: {
        estado: newEstado,
        observaciones: material.observaciones || '',
          responsable_actual: material.responsable_actual || 'jefe_taller'
        }
      };

      try {
      const response = await axios.put(
          `${this.apiUrl}/albaran-activo-materials/${material.id}`,
          payload,
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );

        // Recargar el albarán para obtener los datos actualizados
        await this.loadAlbaran();
        
        // Actualizar el estado del albarán basándose en los estados de los materiales
        await this.actualizarEstadoAlbaran();
        
        return response;
      } catch (error) {
        console.error("Error al actualizar el estado del material:", error);
        throw new Error(error.response?.data?.error?.message || error.message || 'Error al actualizar el estado del material');
      }
    },

    // Manejar la resolución de una incidencia
    async manejarIncidenciaResuelta({ incidencia, comentario }) {
      try {
        // Si la incidencia está relacionada con un activo, necesitamos actualizar las incidencias del material
        const activoId = incidencia.attributes?.activo?.data?.id || incidencia.activo?.id;
        
        if (activoId && this.albaran?.albaran_activo_materials) {
          // Encontrar el material que tiene este activo
          const material = this.albaran.albaran_activo_materials.find(
            m => m.activo?.id === activoId
          );
          
          if (material) {
            // Recargar las incidencias del activo para obtener el estado actualizado
            try {
              const incidenciasActualizadas = await this.getIncidencesForActivo(activoId);
              // Actualizar las incidencias del material usando $set para reactividad
              this.$set(material, 'incidencias', incidenciasActualizadas);
              console.log(`✅ Incidencias actualizadas para material ${material.id}, activo ${activoId}`);
            } catch (error) {
              console.error('Error recargando incidencias específicas:', error);
            }
          }
        }
        
        // También recargar el albarán completo para asegurar que todo esté sincronizado
        await this.loadAlbaran();
        
        this.$notify({
          type: 'success',
          message: 'Incidencia resuelta y datos actualizados'
        });
      } catch (error) {
        console.error('Error al actualizar datos después de resolver incidencia:', error);
        // No mostramos error adicional ya que la incidencia ya se resolvió
      }
    }
  },
  
    async created() {
    await this.loadAlbaran();
    await this.fetchEmpleados(); // Cargamos los jefes de equipo usando el servicio
    // Log para depuración
    console.log('Albaran cargado:', this.albaran);
    console.log('Materiales asignados (getMateriales):', this.getMateriales);
    console.log('Rol del usuario: jefe_taller (solo rol con acceso web)');
    if (this.albaran?.attributes?.albaran_activo_materials) {
      console.log('albaran_activo_materials:', this.albaran.attributes.albaran_activo_materials);
    }
  }
};
</script>

<style scoped>
.activos-scroll {
  max-height: 220px;
  overflow-y: auto;
}

.input-group {
  width: 100%;
}

.input-group .form-control,
.flex-grow-1 {
  min-width: 50vw;
  flex: 1;
  height: 46px;
  font-size: 1.1rem;
  font-weight: bold;
}

.input-group-append .btn {
  height: 46px;
}

.qr-scanner-input {
  .form-control {
    border: 1px solid #000 !important;
    height: 45px;
    font-size: 1.1rem;
    font-weight: bold;
  }
}

/* Ajustar altura de las cabeceras de tabla */
.el-table th {
  padding: 8px !important;
  height: 40px !important;
  line-height: 23px !important;
}

.el-table td {
  padding: 8px !important;
  height: 40px !important;
}

/* Asegurar que la columna de ACCIONES mantenga la misma altura y alineación */
.el-table th:last-child {
  height: 40px !important;
  line-height: 23px !important;
  vertical-align: middle !important;
  padding: 8px !important;
}

/* Asegurar que todas las columnas de ACCIONES en cualquier tabla tengan alineación correcta */
.el-table th[data-column="actions"],
.el-table th:last-child {
  height: 40px !important;
  line-height: 23px !important;
  vertical-align: middle !important;
  padding: 8px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  text-align: center !important;
}

/* Asegurar que los botones de acción no afecten la altura */
.table-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.table-actions .btn {
  margin: 0 2px;
}

/* Estilos para el selector de vista */
.filter-group {
  margin-bottom: 1rem;
}

.filter-label {
  display: block;
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
}

.filter-label i {
  color: #6c757d;
}

/* CSS corporativo para botones */
.btn-corporate {
  background-color: white !important;
  border-color: #7f1058 !important;
  color: #7f1058 !important;
  border-width: 1px;
  border-style: solid;
  font-weight: 500;
  transition: all 0.2s ease;
}

.btn-corporate:hover {
  background-color: #7f1058 !important;
  border-color: #7f1058 !important;
  color: white !important;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(127, 16, 88, 0.3);
}

.btn-corporate.active {
  background-color: #7f1058 !important;
  border-color: #7f1058 !important;
  color: white !important;
  box-shadow: 0 2px 4px rgba(127, 16, 88, 0.3);
}

/* Estilos para botones de vista con altura consistente */
.filter-group .btn-group .btn {
  height: 46px !important;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 2px;
}
</style>

<style>
/* Estilos para el dropdown del select de usuarios en el modal */
/* El dropdown se renderiza fuera del componente, por lo que necesita estilos globales */
.user-select-dropdown {
  z-index: 10000 !important;
}

/* Asegurar que el dropdown del select aparezca por encima del modal */
.el-select-dropdown {
  z-index: 10000 !important;
}

/* Si el modal tiene z-index alto, asegurar que el dropdown esté por encima */
.modal.show ~ .el-select-dropdown,
.modal.show .el-select-dropdown {
  z-index: 10000 !important;
}

/* SweetAlert2 debe ir por encima del modal */
.swal2-container-albaran {
  z-index: 20000 !important;
}

/* Estilo para el botón de material marcado manualmente */
.material-marcado-manual {
  background-color: #6c757d !important;
  border-color: #6c757d !important;
  opacity: 0.8;
  position: relative;
}

.material-marcado-manual i,
.material-marcado-manual i.fa-check,
.material-marcado-manual i.fa-check-circle {
  color: #495057 !important;
}

.material-marcado-manual::after {
  content: '';
  position: absolute;
  top: 2px;
  right: 2px;
  width: 6px;
  height: 6px;
  background-color: #28a745;
  border-radius: 50%;
  border: 1px solid white;
}

/* Estilo para iconos de check en columna ASIGNADOS - gris oscuro */
.el-table .fa-check-circle[style*="color: #495057"] {
  color: #343a40 !important;
}
</style> 